const notesData = {
  General_English: {
    Antonyms: {
      title: "Antonyms",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the Topic – Antonyms. Along with this, students will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in Aptitude Tests like NIFT / NID / UCEED and all such entrances.",
      introduction: `An antonym is a word with the opposite meaning of another word. For example, hot and cold are antonyms. Antonyms are sometimes called opposites. They can be used to describe things or people: for example, "He's a very friendly person" or "That was a terrible meal."`,
      sections: [
        {
          id: "types",
          heading: "There Are Three Main Types of Antonyms",
          subsections: [
            {
              type: "card",
              color: "#FF820F",
              icon: "📊",
              title: "1. Graded (or Gradable) Antonyms",
              body: "Graded antonyms are words with opposite meanings that are often used together. For example, you can have a little bit of something or a lot. And something can be very hot, or only mildly warm.",
              examples: ["Empty / Full", "Dead / Alive", "Soft / Loud"],
            },
            {
              type: "card",
              color: "#407BFF",
              icon: "🔄",
              title: "2. Complementary Antonyms",
              body: "Complementary antonyms are words that go together and describe opposite things. For example, you need light to see, but dark to sleep. You can also be in or out of something.",
              examples: ["Inside / Outside", "Up / Down", "Over / Under"],
            },
            {
              type: "card",
              color: "#15E4B9",
              icon: "🔗",
              title: "3. Relational Antonyms",
              body: "Relational antonyms are words that describe the relationship between two things. For example, you can be the mother or father of someone. Something can also be above or below something else.",
              examples: ["Above / Below", "Front / Back", "Left / Right"],
            },
          ],
        },
        {
          id: "how-to-find",
          heading: "How To Find An Antonym Of Different Words",
          body: `When you are looking for an antonym of a word, the best way to find it is by using a thesaurus. This will give you a list of words that have opposite meanings. For example, if you are looking for an antonym of "happy," you could use the word "unhappy."`,
          tips: [
            "Look them up in a thesaurus – it provides a list of words with opposite meanings.",
            "Look them up in a dictionary for definitions and usage.",
            "Check out your local library's reference section.",
          ],
        },
      ],
      keyTakeaways: [
        "Antonyms can be used to create humour and irony in writing.",
        "The best way to find an antonym is by using a thesaurus.",
        "You can also look them up in a dictionary.",
        "Opposites can add effect and flair to your writing.",
      ],
    },
    Synonyms: {
      title: "Synonyms",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the Topic-Synonyms. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
      introduction:
        "Good synonyms are words that have the same or almost the same meaning as another term. Some synonym examples include jump, leap, bounce, and hop, which means they all have the same (or very similar) meanings. Another example can be eagerly. Its synonyms are impatiently, anxiously, willingly, keenly, gladly, readily, etc.",
      sections: [
        {
          id: "common-synonyms",
          heading: "Common Synonyms",
          icon: "📚",
          color: "#407BFF",
          table: {
            headers: ["Word", "Synonyms"],
            rows: [
              [
                "Amazing",
                "incredible, unbelievable, improbable, fabulous, wonderful, fantastic, astonishing, astounding, extraordinary.",
              ],
              [
                "Anger",
                "enrage, infuriate, arouse, nettle, exasperate, inflame, madden.",
              ],
              [
                "Angry",
                "mad, furious, enraged, excited, wrathful, indignant, exasperated, aroused, inflamed.",
              ],
              ["Answer", "reply, respond, retort, acknowledge."],
              [
                "Ask",
                "question, inquire of, seek information from, put a question to, demand, request, expect, inquire, query, interrogate, examine, quiz.",
              ],
              [
                "Awful",
                "dreadful, terrible, abominable, bad, poor, unpleasant.",
              ],
              [
                "Bad",
                "evil, immoral, wicked, corrupt, sinful, depraved, rotten, contaminated, spoiled, tainted, harmful, injurious, unfavourable, defective, inferior, imperfect, substandard, faulty, improper, inappropriate, unsuitable, disagreeable, unpleasant, cross, nasty, unfriendly, irascible, horrible, atrocious, outrageous, scandalous, infamous, wrong, noxious, sinister, putrid, snide, deplorable, dismal, gross, heinous, nefarious, base, obnoxious, detestable, despicable, contemptible, foul, rank, ghastly, execrable.",
              ],
              [
                "Beautiful",
                "pretty, lovely, handsome, attractive, gorgeous, dazzling, splendid, magnificent, comely, fair, ravishing, graceful, elegant, fine, exquisite, aesthetic, pleasing, shapely, delicate, stunning, glorious, heavenly, resplendent, radiant, glowing, blooming, sparkling.",
              ],
              [
                "Begin",
                "start, open, launch, initiate, commence, inaugurate, originate.",
              ],
              [
                "Big",
                "enormous, huge, immense, gigantic, vast, colossal, gargantuan, large, sizable, grand, great, tall, substantial, mammoth, astronomical, ample, broad, expansive, spacious, stout, tremendous, titanic, mountainous.",
              ],
              [
                "Brave",
                "courageous, fearless, dauntless, intrepid, plucky, daring, heroic, valorous, audacious, bold, gallant, valiant, doughty, mettlesome.",
              ],
              [
                "Break",
                "fracture, rupture, shatter, smash, wreck, crash, demolish, atomize.",
              ],
              [
                "Bright",
                "shining, shiny, gleaming, brilliant, sparkling, shimmering, radiant, vivid, colourful, lustrous, luminous, incandescent, intelligent, knowing, quick-witted, smart, intellectual.",
              ],
              [
                "Calm",
                "quiet, peaceful, still, tranquil, mild, serene, smooth, composed, collected, unruffled, level-headed, unexcited, detached, aloof.",
              ],
              ["Come", "approach, advance, near, arrive, reach."],
              ["Cool", "chilly, cold, frosty, wintry, icy, frigid."],
              ["Crooked", "bent, twisted, curved, hooked, zigzag."],
              [
                "Cry",
                "shout, yell, yowl, scream, roar, bellow, weep, wail, sob, bawl.",
              ],
              [
                "Cut",
                "gash, slash, prick, nick, sever, slice, carve, cleave, slit, chop, crop, lop, reduce.",
              ],
              ["Dangerous", "perilous, hazardous, risky, uncertain, unsafe."],
              [
                "Dark",
                "shadowy, unlit, murky, gloomy, dim, dusky, shaded, sunless, black, dismal, sad.",
              ],
              ["Decide", "determine, settle, choose, resolve."],
              [
                "Definite",
                "certain, sure, positive, determined, clear, distinct, obvious.",
              ],
              [
                "Delicious",
                "Savory, delectable, appetizing, luscious, scrumptious, palatable, delightful, enjoyable, toothsome, exquisite.",
              ],
              [
                "Describe",
                "portray, characterize, picture, narrate, relate, recount, represent, report, record.",
              ],
              [
                "Destroy",
                "ruin, demolish, raze, waste, kill, slay, end, extinguish.",
              ],
              [
                "Difference",
                "disagreement, inequity, contrast, dissimilarity, incompatibility.",
              ],
              [
                "Do",
                "execute, enact, carry out, finish, conclude, effect, accomplish, achieve, attain.",
              ],
              [
                "Dull",
                "boring, tiring, tiresome, uninteresting, slow, dumb, stupid, unimaginative, lifeless, dead, insensible, tedious, wearisome, listless, expressionless, plain, monotonous, humdrum, dreary.",
              ],
              [
                "Eager",
                "keen, fervent, enthusiastic, involved, interested, alive to.",
              ],
              [
                "End",
                "stop, finish, terminate, conclude, close, halt, cessation, discontinuance.",
              ],
              [
                "Enjoy",
                "appreciate, delight in, be pleased, indulge in, luxuriate in, bask in, relish, devour, Savor, like.",
              ],
              [
                "Explain",
                "elaborate, clarify, define, interpret, justify, account for.",
              ],
              [
                "Fair",
                "just, impartial, unbiased, objective, unprejudiced, honest.",
              ],
              ["Fall", "drop, descend, plunge, topple, tumble."],
              [
                "False",
                "fake, fraudulent, counterfeit, spurious, untrue, unfounded, erroneous, deceptive, groundless, fallacious.",
              ],
              [
                "Famous",
                "well-known, renowned, celebrated, famed, eminent, illustrious, distinguished, noted, notorious.",
              ],
              [
                "Fast",
                "quick, rapid, speedy, fleet, hasty, snappy, mercurial, swiftly, rapidly, quickly, snappily, speedily, lickety-split, posthaste, hastily, expeditiously, like a flash.",
              ],
              [
                "Fat",
                "stout, corpulent, fleshy, beefy, paunchy, plump, full, rotund, tubby, pudgy, chubby, chunky, burly, bulky, elephantine.",
              ],
              [
                "Fear",
                "fright, dread, terror, alarm, dismay, anxiety, scare, awe, horror, panic, apprehension.",
              ],
              [
                "Fly",
                "soar, hover, flit, wing, flee, waft, glide, coast, skim, sail, cruise.",
              ],
              [
                "Funny",
                "humorous, amusing, droll, comic, comical, laughable, silly.",
              ],
              [
                "Get",
                "acquire, obtain, secure, procure, gain, fetch, find, score, accumulate, win, earn, rep, catch, net, bag, derive, collect, gather, glean, pick up, accept, come by, regain, salvage.",
              ],
              ["Go", "recede, depart, fade, disappear, move, travel, proceed."],
              [
                "Good",
                "excellent, fine, superior, wonderful, Marvelous, qualified, suited, suitable, apt, proper, capable, generous, kindly, friendly, gracious, obliging, pleasant, agreeable, pleasurable, satisfactory, well-behaved, obedient, honourable, reliable, trustworthy, safe, favourable, profitable, advantageous, righteous, expedient, helpful, valid, genuine, ample, salubrious, estimable, beneficial, splendid, great, noble, worthy, first-rate, top-notch, grand, sterling, superb, respectable, edifying.",
              ],
              [
                "Great",
                "noteworthy, worthy, distinguished, remarkable, grand, considerable, powerful, much, mighty.",
              ],
              [
                "Gross",
                "improper, rude, coarse, indecent, crude, vulgar, outrageous, extreme, grievous, shameful, uncouth, obscene, low.",
              ],
              [
                "Happy",
                "pleased, contented, satisfied, delighted, elated, joyful, cheerful, ecstatic, jubilant, gay, tickled, gratified, glad, blissful, overjoyed.",
              ],
              [
                "Hate",
                "despise, loathe, detest, abhor, disfavour, dislike, disapprove, abominate.",
              ],
              [
                "Have",
                "hold, possess, own, contain, acquire, gain, maintain, believe, bear, beget, occupy, absorb, fill, enjoy.",
              ],
              [
                "Help",
                "aid, assist, support, encourage, back, wait on, attend, serve, relieve, succour, benefit, befriend, abet.",
              ],
              [
                "Hide",
                "conceal, cover, mask, cloak, camouflage, screen, shroud, veil.",
              ],
              [
                "Hurry",
                "rush, run, speed, race, hasten, urge, accelerate, bustle.",
              ],
              ["Hurt", "damage, harm, injure, wound, distress, afflict, pain."],
              [
                "Idea",
                "thought, concept, conception, notion, understanding, opinion, plan, view, belief.",
              ],
              [
                "Important",
                "necessary, vital, critical, indispensable, valuable, essential, significant, primary, principal, considerable, famous, distinguished, notable, well known.",
              ],
              [
                "Interesting",
                "fascinating, engaging, sharp, keen, bright, intelligent, animated, spirited, attractive, inviting, intriguing, provocative, though-provoking, challenging, inspiring, involving, moving, titillating, tantalizing, exciting, entertaining, piquant, lively, racy, spicy, engrossing, absorbing, consuming, gripping, arresting, enthralling, spellbinding, curious, captivating, enchanting, bewitching, appealing.",
              ],
              [
                "Keep",
                "hold, retain, withhold, preserve, maintain, sustain, support.",
              ],
              [
                "Kill",
                "slay, execute, assassinate, murder, destroy, cancel, abolish.",
              ],
              ["Lazy", "indolent, slothful, idle, inactive, sluggish."],
              [
                "Little",
                "tiny, small, diminutive, shrimp, runt, miniature, puny, exiguous, dinky, cramped, limited, itsy-bitsy, microscopic, slight, petite, minute.",
              ],
              [
                "Look",
                "gaze, see, glance, watch, survey, study, seek, search for, peek, peep, glimpse, stare, contemplate, examine, gape, ogle, scrutinize, inspect, leer, behold, observe, view, witness, perceive, spy, sight, discover, notice, recognize, peer, eye, gawk, peruse, explore.",
              ],
              [
                "Love",
                "like, admire, esteem, fancy, care for, cherish, adore, treasure, worship, appreciate, Savor.",
              ],
              [
                "Make",
                "create, originate, invent, beget, form, construct, design, fabricate, manufacture, produce, build, develop, do, effect, execute, compose, perform, accomplish, earn, gain, obtain, acquire, get.",
              ],
              [
                "Mark",
                "label, tag, price, ticket, impress, effect, trace, imprint, stamp, brand, sign, note, heed, notice, designate.",
              ],
              [
                "Mischievous",
                "prankish, playful, naughty, roguish, waggish, impish, sportive.",
              ],
              [
                "Move",
                "plod, go, creep, crawl, inch, poke, drag, toddle, shuffle, trot, dawdle, walk, traipse, mosey, jog, plug, trudge, slump, lumber, trail, lag, run, sprint, trip, bound, hotfoot, high-tail, streak, stride, tear, breeze, whisk, rush, dash, dart, bolt, fling, scamper, scurry, skedaddle, scoot, scuttle, scramble, race, chase, hasten, hurry, hump, gallop, lope, accelerate, stir, budge, travel, wander, roam, journey, trek, ride, spin, slip, glide, slide, slither, coast, flow, sail, saunter, hobble, amble, stagger, paddle, slouch, prance, straggle, meander, perambulate, waddle, wobble, pace, swagger, promenade, lunge.",
              ],
              [
                "Moody",
                "temperamental, changeable, short-tempered, glum, morose, sullen, mopish, irritable, testy, peevish, fretful, spiteful, sulky, touchy.",
              ],
              [
                "Neat",
                "clean, orderly, tidy, trim, dapper, natty, smart, elegant, well-organized, super, desirable, spruce, shipshape, well-kept, shapely.",
              ],
              [
                "New",
                "fresh, unique, original, unusual, novel, modern, current, recent.",
              ],
              [
                "Old",
                "feeble, frail, ancient, weak, aged, used, worn, dilapidated, ragged, faded, broken-down, former, old-fashioned, outmoded, passe, veteran, mature, venerable, primitive, traditional, archaic, conventional, customary, stale, musty, obsolete, extinct.",
              ],
              [
                "Part",
                "portion, share, piece, allotment, section, fraction, fragment.",
              ],
              [
                "Place",
                "space, area, spot, plot, region, location, situation, position, residence, dwelling, set, site, station, status, state.",
              ],
              [
                "Plan",
                "plot, scheme, design, draw, map, diagram, procedure, arrangement, intention, device, contrivance, method, way, blueprint.",
              ],
              [
                "Popular",
                "well-liked, approved, accepted, favourite, celebrated, common, current.",
              ],
              [
                "Predicament",
                "quandary, dilemma, pickle, problem, plight, spot, scrape, jam.",
              ],
              [
                "Put",
                "place, set, attach, establish, assign, keep, save, set aside, effect, achieve, do, build.",
              ],
              [
                "Quiet",
                "silent, still, soundless, mute, tranquil, peaceful, calm, restful.",
              ],
              [
                "Right",
                "correct, accurate, factual, true, good, just, honest, upright, lawful, moral, proper, suitable, apt, legal, fair.",
              ],
              [
                "Run",
                "race, speed, hurry, hasten, sprint, dash, rush, escape, elope, flee.",
              ],
              [
                "Say/Tell",
                "inform, notify, advise, relate, recount, narrate, explain, reveal, disclose, divulge, declare, command, order, bid, enlighten, instruct, insist, teach, train, direct, issue, remark, converse, speak, affirm, suppose, utter, negate, express, verbalize, voice, articulate, pronounce, deliver, convey, impart, assert, state, allege, mutter, mumble, whisper, sigh, exclaim, yell, sing, yelp, snarl, hiss, grunt, snort, roar, bellow, thunder, boom, scream, shriek, screech, squawk, whine, philosophize, stammer, stutter, lisp, drawl, jabber, protest, announce, swear, vow, content, assure, deny, dispute.",
              ],
              [
                "Scared",
                "afraid, frightened, alarmed, terrified, panicked, fearful, unnerved, insecure, timid, shy, skittish, jumpy, disquieted, worried, vexed, troubled, disturbed, horrified, terrorized, shocked, petrified, haunted, timorous, shrinking, tremulous, stupefied, paralyzed, stunned, apprehensive.",
              ],
              [
                "Show",
                "display, exhibit, present, note, point to, indicate, explain, reveal, prove, demonstrate, expose.",
              ],
              [
                "Slow",
                "unhurried, gradual, leisurely, late, behind, tedious, slack.",
              ],
              [
                "Stop",
                "cease, halt, stay, pause, discontinue, conclude, end, finish, quit.",
              ],
              [
                "Story",
                "tale, myth, legend, fable, yarn, account, narrative, chronicle, epic, sage, anecdote, record, memoir.",
              ],
              [
                "Strange",
                "odd, peculiar, unusual, unfamiliar, uncommon, queer, weird, outlandish, curious, unique, exclusive, irregular.",
              ],
              [
                "Take",
                "hold, catch, seize, grasp, win, capture, acquire, pick, choose, select, prefer, remove, steal, lift, rob, engage, bewitch, purchase, buy, retract, recall, assume, occupy, consume.",
              ],
              [
                "Tell",
                "disclose, reveal, show, expose, uncover, relate, narrate, inform, advise, explain, divulge, declare, command, order, bid, recount, repeat.",
              ],
              [
                "Think",
                "judge, deem, assume, believe, consider, contemplate, reflect, mediate.",
              ],
              [
                "Trouble",
                "distress, anguish, anxiety, worry, wretchedness, pain, danger, peril, disaster, grief, misfortune, difficulty, concern, pains, inconvenience, exertion, effort.",
              ],
              [
                "True",
                "accurate, right, proper, precise, exact, valid, genuine, real, actual, trusty, steady, loyal, dependable, sincere, staunch.",
              ],
              [
                "Ugly",
                "hideous, frightful, frightening, shocking, horrible, unpleasant, monstrous, terrifying, gross, grisly, ghastly, horrid, unsightly, plain, homely, evil, repulsive, repugnant, gruesome.",
              ],
              [
                "Unhappy",
                "miserable, uncomfortable, wretched, heart-broken, unfortunate, poor, downhearted, sorrowful, depressed, dejected, melancholy, glum, gloomy, dismal, discouraged, sad.",
              ],
              [
                "Use",
                "employ, utilize, exhaust, spend, expend, consume, exercise.",
              ],
              [
                "Wrong",
                "incorrect, inaccurate, mistaken, erroneous, improper, unsuitable.",
              ],
            ],
          },
        },
      ],
      keyTakeaways: [
        "Synonyms are words that have the same or almost the same meaning as another term.",
        "A strong vocabulary of synonyms is essential for solving word problems and comprehension tasks.",
        "Using variety in word choice (synonyms) makes writing and speaking more effective.",
        "Thesauruses and dictionaries are the best tools for finding synonyms.",
      ],
    },
    Fill_in_the_Blanks_Single: {
      title: "Fill in the Blanks Single",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts of analyzing the most effective word fill-in type questions. Students will be empowered with methods and strategies to solve single and multiple blank questions commonly asked in aptitude tests like NIFT/NID/UCEED.",
      introduction:
        "When a word is missing from a sentence, the meaning is lost. Filling in the blank tests your understanding of words in context across various parts of speech like nouns, verbs, adverbs, and articles.",
      sections: [
        {
          id: "approach",
          heading: "Approaches to Solving Fill in the Blank",
          subsections: [
            {
              type: "card",
              color: "#407BFF",
              icon: "🔍",
              title: "1. Collocations",
              body: "Pay attention to words before and after the blank. Associated words often form natural combinations (collocations).",
              examples: ["Expenses incurred", "Reeling under drought"],
            },
            {
              type: "card",
              color: "#FF820F",
              icon: "💡",
              title: "2. Context Clues",
              body: "Identify keywords within the statement to determine the best fit. For example, 'Not because of (blank), but due to inner urge' suggests an opposing idea like 'incentive'.",
              examples: ["Committed mistakes", "In spite of (contrast clue)"],
            },
            {
              type: "card",
              color: "#15E4B9",
              icon: "🎭",
              title: "3. Connotation Check",
              body: "Determine if the sentence has a positive or negative tone. Words like 'Woah!' indicate a positive connotation, making words like 'conversationalist' a better fit than 'chatter'.",
              examples: [
                "Positive: Conversationalist",
                "Negative: Chatter/Annoying",
              ],
            },
          ],
        },
        {
          id: "prepositions",
          heading: "Fill in the Blanks with Prepositions",
          body: "Prepositions show direction, time, or location. They are always followed by nouns or pronouns (objects). Verbs cannot be direct prepositional objects.",
          subsections: [
            {
              type: "card",
              color: "#940B92",
              icon: "⏰",
              title: "At (Exact Time)",
              body: "Use 'at' for specific times or points.",
              examples: ["At lunch time", "At 2 PM"],
            },
            {
              type: "card",
              color: "#EBC33C",
              icon: "❄️",
              title: "In (Longer Periods)",
              body: "Use 'in' for seasons, months, or years.",
              examples: ["In winter", "In the morning"],
            },
            {
              type: "card",
              color: "#FF7A7A",
              icon: "📅",
              title: "On (Days/Weekends)",
              body: "Use 'on' for specific days or holidays.",
              examples: ["On the weekend", "On Monday"],
            },
          ],
        },
      ],
      keyTakeaways: [
        "Pay attention to the words before and after the blank (Collocations).",
        "Use context clues (positive/negative tone) to eliminate options.",
        "Ensure the chosen word fits both grammatically and contextually.",
        "Prepositions are followed by nouns/pronouns (objects).",
      ],
    },
    Fill_in_the_Blanks_Double: {
      title: " Fill in the Blanks Double",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the topic – Double Blank Questions. Along with this student will be empowered to solve different categories of questions involving context, vocabulary, grammar and logic commonly asked in Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
      introduction:
        "Double blank questions are sentences that contain two missing words. The objective is to choose a pair of words that together complete the sentence meaningfully, grammatically, and logically. Both blanks are interconnected and cannot be solved separately.",
      sections: [
        {
          id: "importance",
          heading: "Why Double Blanks are Important",
          subsections: [
            {
              type: "card",
              color: "#407BFF",
              icon: "🧠",
              title: "Testing Core Skills",
              body: "These questions test your understanding of context, depth of vocabulary, grammar accuracy, and ability to identify logical relationships and collocations.",
            },
          ],
        },
        {
          id: "core-concept",
          heading: "Core Concept",
          body: "In double blank questions, meaning comes first, grammar comes next. Usually, the two blanks have one of these relationships:",
          subsections: [
            {
              type: "card",
              color: "#FF820F",
              icon: "🤝",
              title: "Supportive",
              body: "Both words support the same idea or direction.",
            },
            {
              type: "card",
              color: "#15E4B9",
              icon: "↔️",
              title: "Contrasting",
              body: "Words indicate opposite meanings or a shift in thought.",
            },
            {
              type: "card",
              color: "#940B92",
              icon: "➡️",
              title: "Cause-Effect",
              body: "One blank explains the reason or result of the other.",
            },
          ],
        },
        {
          id: "approach",
          heading: "Step-by-Step Approach",
          icon: "🪜",
          color: "#FF820F",
          tips: [
            "Read the sentence fully to understand the theme and tone.",
            "Identify signal words for contrast (but, although) or cause-effect (because, hence).",
            "Predict the nature of words (adjective, verb, noun) before looking at options.",
            "Solve the easier blank first; it often provides a stronger clue.",
            "Check pair compatibility to ensure they sound natural together.",
            "Perform a final reading test to ensure logical consistency.",
          ],
        },
        {
          id: "collocations",
          heading: "Role of Collocations",
          body: "A collocation is a natural word pair commonly used together in English. In double blanks, one word often forces the other.",
          subsections: [
            {
              type: "card",
              color: "#15E4B9",
              icon: "🔗",
              title: "Common Collocations",
              body: "Examples of words often paired together in exams.",
              examples: [
                "commit a mistake",
                "heavy rain",
                "strong evidence",
                "draw a conclusion",
              ],
            },
          ],
        },
        {
          id: "examples",
          heading: "Well-Explained Examples",
          subsections: [
            {
              type: "card",
              color: "#EBC33C",
              icon: "💡",
              title: "1. Contrast Based",
              body: "Although the task appeared ___, it was actually quite ___. (A) easy, difficult (B) difficult, simple. Answer: (B). Why: 'Although' indicates contrast.",
            },
            {
              type: "card",
              color: "#FF7A7A",
              icon: "⚡",
              title: "2. Cause–Effect",
              body: "The road was slippery ___ the rain, ___ several accidents. (A) because, causing (B) due to, resulting in. Answer: (B). Why: Due to = cause, Resulting in = effect.",
            },
          ],
        },
      ],
      keyTakeaways: [
        "Meaning comes first, grammar comes next.",
        "Both blanks are interconnected and cannot be solved separately.",
        "The correct option completes the core idea, not just the sentence.",
        "Signal words are the most important clues in identifying the relationship.",
      ],
    },
    "Idioms_&_Phrases": {
      title: "Idioms & Phrases",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the topic – Idioms & Phrases. Along with this student will be empowered to solve different categories of questions involving figurative language, contextual comprehension, and common English usage as required in tests like NIFT/NID/UCEED.",
      introduction:
        "Idioms and phrases are groups of words that express complex ideas figuratively. An idiom's meaning differs from the literal definitions of its individual words, while a phrase works as a unit within a sentence.",
      sections: [
        {
          id: "definitions",
          heading: "What are Idioms and Phrases?",
          subsections: [
            {
              type: "card",
              color: "#407BFF",
              icon: "🗣️",
              title: "Idiom",
              body: "A group of words with a figurative meaning that is different from the literal meaning.",
              examples: [
                "Kick the bucket (to die)",
                "At the eleventh hour (last moment)",
              ],
            },
            {
              type: "card",
              color: "#FF820F",
              icon: "🏷️",
              title: "Phrase",
              body: "A group of words working together to express an idea, but not forming a complete sentence.",
              examples: ["In a hurry", "At all costs"],
            },
          ],
        },
        {
          id: "approach",
          heading: "How to Approach Questions",
          icon: "🪜",
          color: "#15E4B9",
          tips: [
            "Ignore literal meaning; never translate word by word.",
            "Focus on context clues from the surrounding sentence.",
            "Eliminate extreme or illogical options.",
            "Recall familiar usage from newspapers and daily English.",
          ],
        },
        {
          id: "themes",
          heading: "Common Themes in Idioms",
          subsections: [
            {
              type: "card",
              color: "#940B92",
              icon: "📂",
              title: "Standard Themes",
              body: "Knowing themes helps in faster elimination. Common categories include Success & Failure, Anger & Emotions, Time & Speed, Money & Power, and Relationships.",
            },
          ],
        },
        {
          id: "important-idioms",
          heading: "Commonly Asked Idioms",
          icon: "📚",
          color: "#EBC33C",
          table: {
            headers: ["Idiom", "Figurative Meaning"],
            rows: [
              ["Break the ice", "To start a conversation"],
              ["Once in a blue moon", "Very rarely"],
              ["In hot water", "In trouble"],
              ["A piece of cake", "Very easy"],
              ["Hit the nail on the head", "To say exactly the right thing"],
              ["Burn the midnight oil", "To work late at night"],
              ["At the eleventh hour", "At the last possible moment"],
            ],
          },
        },
        {
          id: "examples",
          heading: "Contextual Examples",
          subsections: [
            {
              type: "card",
              color: "#FF7A7A",
              icon: "📝",
              title: "Usage in Sentences",
              body: "1. Speaking in front of strangers was a PIECE OF CAKE for her. (Very easy)\n2. The manager’s comment HIT THE NAIL ON THE HEAD. (Was exactly correct)",
            },
          ],
        },
      ],
      keyTakeaways: [
        "Structure of idioms remains fixed; do not alter articles or prepositions.",
        "Literal meanings are almost always wrong in idioms.",
        "Tense can change, but the core word combination is conventional.",
        "Revise theme-wise for better retention.",
      ],
    },
    One_Word_Substitution: {
      title: "One Word Substitution",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the Topic-One Word Substitution. Along with this students will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
      introduction:
        "One Word Substitution is a process where a single word is used to replace a whole phrase or a group of words, making the sentence structure cleaner and more precise.",
      sections: [
        {
          id: "study",
          heading: "One Word Substitution related to Study",
          icon: "🎓",
          color: "#407BFF",
          table: {
            headers: ["One Word", "Phrases"],
            rows: [
              ["Aviation", "Study of flying aeroplanes"],
              ["Astronomy", "Study of celestial bodies"],
              [
                "Alchemy",
                "The ancient search for a universal panacea, and of the philosopher’s stone. The medieval version of the study of Chemistry",
              ],
              ["Acoustics", "Study of sound and sound waves"],
              ["Anthropology", "The study of the evolution of mankind"],
              ["Astronomy", "Study of Stars"],
              ["Botany", "The study of plants"],
              ["Biology", "Study of Living Things"],
              ["Calligraphy", "Art related to ornate, good handwriting"],
              ["Chronology", "The science of time order"],
              ["Demography", "The study of statistics"],
              ["Entomology", "Study of science of insects"],
              [
                "Ecology",
                "Study of the relation between the organism and their environment",
              ],
              [
                "Genetics",
                "Study of hereditary, genes and variation in living organisms",
              ],
              ["Geology", "The study of rocks and soil"],
              ["Geography", "Mapping of earth and its formation"],
              ["Graphology", "Study of handwriting"],
              ["Gerontology", "Study of various aspects of ageing"],
              [
                "Hydraulics",
                "Study of the law of the flow of water and other liquids",
              ],
              ["Lexicography", "The practice of writing dictionaries"],
              [
                "Numismatics",
                "Study of collection of coins, tokens, paper money etc.",
              ],
              ["Ornithology", "Study of birds"],
              ["Philology", "The study of languages"],
              ["Palaeography", "The study of ancient writing and scriptures"],
              ["Psychology", "The study of Human Mind"],
              ["Psephology", "Study of election trends"],
              ["Theology", "Study of religion"],
            ],
          },
        },
        {
          id: "fear",
          heading: "One Word Substitution related to Types of Fear",
          icon: "😨",
          color: "#FF7A7A",
          table: {
            headers: ["One Word", "Phrases"],
            rows: [
              ["Ecophobia", "Fear of home surroundings"],
              ["Zoophobia", "Fear of animals"],
              ["Claustrophobia", "Fear of closed space"],
              ["Hydrophobia", "Fear of Water"],
              ["Bibliophobia", "Fear of books"],
              ["Xenophobia", "Fear or dislike of foreigners"],
              ["Necrophobia", "Fear of dead body"],
              ["Aerophobia", "Fear of height"],
              ["Ophiophobia", "Fear of snakes"],
              ["Syngenesophobia", "Fear of relatives"],
              ["Nomophobia", "Fear of being without your mobile phone"],
            ],
          },
        },
        {
          id: "killing-manias",
          heading: "One Word Substitution related to Killing & Manias",
          icon: "🩸",
          color: "#940B92",
          table: {
            headers: ["One Word", "Phrase"],
            rows: [
              ["Mariticide", "The killing of one's husband"],
              ["Parricide", "The killing of parents"],
              ["Genocide", "The killing of a whole race"],
              ["Infanticide", "The killing of a newborn child"],
              ["Regicide", "The killing of a king"],
              ["Homicide", "The killing of a human being"],
              ["Suicide", "The killing of oneself"],
              ["Patricide", "The killing of a father"],
              ["Matricide", "The killing of a mother"],
              ["Fratricide", "The killing of one's brother"],
              ["Sororicide", "The killing of one's sister"],
              ["Uxoricide", "The killing of one's wife"],
              ["Dipsomania", "Morbid compulsion to keep on consuming alcohol"],
              ["Kleptomania", "Morbid compulsion to steal"],
              ["Bibliokleptomania", "Morbid compulsion to steal books"],
              ["Pyromania", "Morbid compulsion to start a fire"],
              [
                "Megalomania",
                "Morbid delusion of power, importance or godliness",
              ],
              [
                "Nymphomania",
                "Morbid, uncontrollable desire on the part of a woman",
              ],
              [
                "Satyromania",
                "Morbid, uncontrollable desire on the part of a man",
              ],
              ["Monomania", "A person with a one-track mind"],
              ["Mythomania", "The compulsion to tell lies"],
              ["Mania", "Madness with an obsession with something"],
            ],
          },
        },
        {
          id: "things-events",
          heading: "One Word Substitution related to Things, Events & Places",
          icon: "📍",
          color: "#EBC33C",
          table: {
            headers: ["One Word", "Phrase"],
            rows: [
              [
                "Solarium",
                "A place for the sun to enter where one can sunbath",
              ],
              [
                "Aquarium",
                "A glass container in which fish and other water animals can be kept",
              ],
              ["Podium", "A place for feet or a speaker's platform"],
              [
                "Auditorium",
                "The part of a theatre where people who are watching and listening sit",
              ],
              [
                "Planetarium",
                "An optical device for projecting various celestial images and effects",
              ],
              [
                "Sanatorium",
                "An establishment for the treatment of the chronically ill",
              ],
              ["Museum", "A place where objects are exhibited"],
              ["Deciduous", "Trees whose leaves fall every autumn"],
              ["Incident", "That which falls upon befalls happens"],
              ["Accident", "That which falls to someone or something"],
              ["Occidental", "That which falls on the western countries"],
              ["Alma Mater", "The school or college one attends"],
              ["Automobile", "A self-moving vehicle"],
              ["Automatic", "A machine that functions by itself"],
              ["Perambulator", "A baby carriage"],
              ["Ambulance", "A carriage for sick people"],
            ],
          },
        },
        {
          id: "terms",
          heading: "One Word Substitution related to Terms",
          icon: "📝",
          color: "#15E4B9",
          table: {
            headers: ["One Word", "Phrase"],
            rows: [
              ["Aeon", "Indefinite period of time"],
              ["Aesthetics", "Appreciation of beauty, by a set of principles."],
              ["Blizzard", "Spartan snowstorms with uncontrollable winds"],
              ["Bohemian", "An eccentric manner of living"],
              ["Bonfire", "Huge fire for celebration"],
              ["Bonsai", "Dwarfed varieties of trees and shrubs in pots"],
              [
                "Hinterland",
                "The remote regions of a country which are far away from the rivers",
              ],
              ["Imbroglio", "Complex situation or a mix-up"],
              ["Rejuvenate", "Make someone feel young"],
              ["Remiss", "Not showing enough care and attention"],
              ["Kennel", "A shelter for a Dog"],
              ["Lair", "Where wild animals live"],
              ["Mint", "A place where coins are made"],
              ["Mundane", "Ordinary and Dull"],
              ["Posthumous", "Published after someone’s death"],
              [
                "Pedagogy",
                "The profession of teaching or the approach or style of teaching",
              ],
              ["Regalia", "The symbols of royalty"],
              [
                "Thearchy",
                "A political system solely based on the government of men by God",
              ],
              ["Oath", "A person promises to tell the truth in court"],
              ["Nascent", "Beginning to exist and evolve"],
              ["Virtue", "Ethically good behaviour one has"],
              ["Satire", "Humour that describes the weaknesses"],
              ["Veer", "Changing the direction of one’s path suddenly"],
              ["Savour", "Enjoy something for an extended time"],
              [
                "Scapegoat",
                "Someone who is wrongly blamed for things that others have done",
              ],
              ["Tannery", "Space where animal hides are tanned"],
              ["Tenacious", "Determined to achieve something"],
              ["Screech", "The sound of Parrots"],
              ["Misnomer", "Using a word or name that is inappropriate"],
              ["Monarchy", "A government that has a monarch as the head"],
              ["Grunt", "The sound of Camels"],
              ["Fiesta", "Events manifested by festivities"],
              ["Whine", "The ranting of a person"],
              ["Destitute", "People who are extremely poor"],
              ["Carte blanche", "Absolute freedom to act as one wishes"],
              ["Agnostic", "A person who is unsure about God’s existence"],
              ["Agony", "Extreme physical or mental sufferings"],
              [
                "Aikido",
                "Japanese form of self-defence with the usage of locks holds",
              ],
              ["Mercenary", "A soldier who fights for the sake of money"],
              [
                "Laurel",
                "An accolade or honour bestowed in recognition for an achievement",
              ],
              ["Immoral", "A person who behaves without moral principles"],
              ["Impeach", "Charging a politician with a serious crime"],
              [
                "Embargo",
                "Government instructions that limit trade in some way",
              ],
              ["Fatalist", "Believer of fate"],
              [
                "Gregarious",
                "Loves being social and enjoys the company of others",
              ],
              ["Grove", "Trees grow without underbrush."],
              ["Sinecure", "An office with a high salary but no work"],
              [
                "Renegade",
                "A person who betrays and descents an organisation or country",
              ],
              [
                "Fastidious",
                "One who is much concerned about details and accuracy",
              ],
              ["Arsenal", "A place for storage of arms and ammunition"],
              [
                "Excommunicate",
                "One who is expelled from a religious community",
              ],
              ["Glutton", "A person who eats too much"],
              ["Imitable", "Something which can be copied"],
            ],
          },
        },
        {
          id: "people",
          heading: "One Word Substitution Related to People/Person",
          icon: "👤",
          color: "#FF820F",
          table: {
            headers: ["One Word", "Phrase"],
            rows: [
              [
                "Eisoptrophile",
                "A person who is addicted to seeing oneself in mirror",
              ],
              [
                "Gastronome",
                "A person who loves food and finds pleasure in eating and drinking",
              ],
              ["Chrematophile", "A person who loves money"],
              ["Oneirophile", "A person who loves dream"],
              ["Anuptaphile", "A person who loves staying single"],
              ["Melophile", "A person who loves music"],
            ],
          },
        },
        {
          id: "government",
          heading: "One Word Substitution Related to Government",
          icon: "🏛️",
          color: "#407BFF",
          table: {
            headers: ["One Word", "Phrase"],
            rows: [
              ["Bureaucracy", "Government by Officials"],
              ["Autocracy", "Government by one person with absolute power"],
              ["Meritocracy", "Government by the intelligentsia"],
              ["Democracy", "Government by elected representatives"],
              ["Confederacy", "The union of states, parties or persons"],
              ["Plutocracy", "Government by rich people"],
              ["Aristocracy", "Government by the nobles"],
              ["Theocracy", "Government by Divine Laws"],
              ["Monarchy", "Government by one person (royal)"],
              ["Diarchy", "Government by two agencies"],
              ["Oligarchy", "Government by a few powerful people"],
              ["Stratocracy", "Government by military class"],
              ["Anarchy", "Absence of Government"],
            ],
          },
        },
        {
          id: "likes-dislikes",
          heading: "One Word Substitution Related to Likes and Dislikes",
          icon: "❤️",
          color: "#FF7A7A",
          table: {
            headers: ["One Word", "Phrase"],
            rows: [
              ["Sycophant", "One who is a boot licker, flatterer"],
              ["Gourmet", "One who has keen interest in food and drinks"],
              ["Philanthropy", "Love for mankind"],
              ["Bibliophile", "Someone who loves collecting books"],
              ["Anglophile", "One who loves and admires the British"],
              ["Philanderer", "One who loves without seriousness"],
              ["Philogyny", "Fondness towards women"],
              ["Androphile", "One who loves men"],
              ["Philosopher", "One who loves wisdom (hence pursues it)"],
              ["Misandrist", "One who hates men"],
              ["Andromania", "Madness or obsession with males"],
              ["Misogynist", "One who hates women"],
              ["Misanthropist", "One who hates mankind"],
              ["Patriot", "One who loves his country"],
              ["Super patriotism", "Excessive love for one's country"],
              ["Misogamist", "One who hates marriage"],
              ["Loquacious", "One who loves to speak"],
              ["Antipathy", "Strong, deep dislike"],
              ["Sympathy", "Simultaneously affected by similar feelings"],
              [
                "Empathy",
                "Mentally identifying oneself with other person or a thing",
              ],
              [
                "Pathetic",
                "Something or someone makes you feel deep sadness or pity",
              ],
            ],
          },
        },
        {
          id: "religion",
          heading: "One Word Substitution Related to Religion",
          icon: "🙏",
          color: "#58B6C5",
          table: {
            headers: ["One Word", "Phrases"],
            rows: [
              ["Atheist", "One who doesn't believe in the presence of God"],
              ["Monotheist", "One who believes in the theory of only one God"],
              ["Theist", "One who believes in the presence of God"],
              ["Polytheist", "One who believes in many Gods"],
              [
                "Pantheist",
                "One who believes that God is union of all forces of the universe",
              ],
              ["Theomania", "Religious madness"],
              ["Theomachy", "Battle among the Gods"],
            ],
          },
        },
        {
          id: "marriage",
          heading: "One Word Substitution Related to Marriage",
          icon: "💍",
          color: "#940B92",
          table: {
            headers: ["One Word", "Phrases"],
            rows: [
              ["Matrimony", "Related to marriage"],
              ["Sologamy", "Marriage with self"],
              [
                "Monogamy",
                "Practising only one marriage (having only one wife)",
              ],
              ["Bigamy", "Practising two marriages (having two wives)"],
              [
                "Polygamy",
                "The practice of several marriages (having many wives)",
              ],
              ["Autogamy", "Self-fertilization especially in plants"],
              ["Endogamy", "Marriage within one's tribe"],
              ["Exogamy", "Marriage outside one's tribe"],
              ["Polyandry", "Having several husbands"],
            ],
          },
        },
        {
          id: "speech-writing",
          heading: "One Word Substitution Related to Speech & Written Work",
          icon: "✍️",
          color: "#FF820F",
          table: {
            headers: ["One Word", "Phrase"],
            rows: [
              [
                "Omnibus",
                "Book containing all the published work of an author",
              ],
              ["Telegraph", "A written message from far off place"],
              ["Colloquial", "Informal, less grammatically rigid language"],
              [
                "Circumlocution",
                "Talking around, a method of talking indirectly",
              ],
              ["Magniloquent", "Generous, forgiving talk"],
              ["Eloquent", "Expressive in the use of words"],
              ["Grandiloquent", "High sounding pompous speech or writing"],
              [
                "Autograph",
                "A signature of a celebrity (signature of oneself)",
              ],
              ["Preamble", "Walk before-hand or an introductory statement"],
              ["Biography", "Writing of one's life story"],
              ["Autoblography", "Writing of one's own life story"],
              ["Photograph", "Written by light"],
              ["Laconic", "Few words packed with meaning, concise"],
              ["Soliloquy", "A speech to oneself, alone"],
            ],
          },
        },
      ],
      keyTakeaways: [
        "One Word Substitution is a process where a single word is used to replace a whole phrase.",
        "It categorizes synonyms, antonyms, and more contextually.",
        "Essential for cracking competitive exams like NIFT/NID/UCEED.",
      ],
    },
    "Jumbled Sentences & Parajumble": {
      title: "Jumbled Sentences & Parajumble",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the Topic-Jumble Sentences and Para Jumbles. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
      introduction:
        "Jumbled sentences or Para Jumbles are techniques for placing scattered sentences into an appropriate succession so that the consolidated meaning is exhaustive and logical. They are sets of connected statements in random order which, when unjumbled, create a meaningful paragraph.",
      sections: [
        {
          id: "guidelines",
          heading: "10 Essential Rules to Solve Jumbled Sentences",
          icon: "📏",
          color: "#FF820F",
          tips: [
            "Find the theme: Repeated words often reveal the main topic.",
            "Repeated words: If a specific word appears in multiple sentences, they likely follow each other.",
            "Names first: A sentence starting with a person's name is often the 1st sentence.",
            "Pronoun check: Sentences starting with pronouns (except 'I' and 'You') are rarely the 1st sentence.",
            "Avoid openers: Sentences starting with 'That', 'These', 'Thus', or 'Those' aren't typically the first.",
            "Article clue: Sentences starting with an article (A, An, The) have a high chance of being the 1st.",
            "Article order: Arrange articles in the order: 'A' → 'An' → 'The'.",
            "Conjunction order: Arrange sentences starting with 'But', 'So', and 'Now' in that specific order.",
            "Sentence complexity: Follow the order: Simple → Compound → Complex.",
            "Finality: Sentences starting with 'Hence', 'Finally', or 'Therefore' come last.",
          ],
        },
        {
          id: "transition-words",
          heading: "Spotting Transition & Linking Words",
          icon: "🔗",
          color: "#407BFF",
          table: {
            headers: ["Type", "Example Words"],
            rows: [
              ["Contrast", "However, Nevertheless, Instead, On the other hand"],
              ["Conclusion", "Thus, Therefore, Consequently, In short"],
              ["Addition", "Furthermore, In Addition, Moreover, Also, Besides"],
              ["Comparison", "Similarly, Likewise, As well as"],
              ["Illustration", "For Instance, Generally, In other words"],
              [
                "Time/Sequence",
                "Simultaneously, Then, After, Before, Previously",
              ],
            ],
          },
        },
        {
          id: "pronouns",
          heading: "Identifying Pronoun Antecedents",
          icon: "👤",
          color: "#15E4B9",
          body: "Spotting pronouns helps set connections coherently and logically. They fall into three main types:",
          bullets: [
            "Relative Pronouns: Who, whom, whose, which.",
            "Demonstrative Pronouns: This, that, these, those.",
            "Personal Pronouns: He, she, him, her, you, they, it.",
          ],
        },
        {
          id: "pj-types",
          heading: "Types of Para Jumble Questions",
          icon: "📋",
          color: "#940B92",
          listItems: [
            {
              title: "Random Order (4-5 Sentences)",
              description:
                "You have to unjumble all sentences from scratch. This is the toughest category.",
            },
            {
              title: "Fixed Opening",
              description:
                "The opening sentence is given; you rearrange the remaining 4-5 sentences.",
            },
            {
              title: "Fixed Closing",
              description:
                "The closing sentence is given; you sequence the rest to flow into it.",
            },
            {
              title: "Fixed Opening & Closing",
              description:
                "Both start and end are known. You only figure out the sequence in between.",
            },
          ],
        },
        {
          id: "approaches",
          heading: "Key Tactical Approaches",
          icon: "🧠",
          color: "#FFC726",
          subsections: [
            {
              title: "Noun-Pronoun Relationship",
              body: "The noun always comes first, followed by the referring pronoun in subsequent sentences.",
              icon: "📍",
              color: "#FFC726",
              examples: ["Rohan (Noun) ... He (Pronoun)"],
            },
            {
              title: "Acronym Approach",
              body: "Full forms are introduced first; short forms or acronyms are used later.",
              icon: "🏢",
              color: "#FFC726",
              examples: ["World Health Organization ... WHO"],
            },
            {
              title: "Time Sequence (TSA)",
              body: "Arrange sentences chronologically using dates or sequence words (Before, After, Later).",
              icon: "⌚",
              color: "#FFC726",
              examples: ["1990 ... 2005 ... 2024"],
            },
          ],
        },
        {
          id: "ips-strategy",
          heading: "The IPS Strategy (Order of Flow)",
          icon: "⚡",
          color: "#FF7A7A",
          hintItems: [
            {
              num: "I",
              text: "Introductory: Identify the statement with an introductory tone.",
              subbullets: ["Look for 'Firstly', 'At the outset', 'Initially'"],
            },
            {
              num: "P",
              text: "Problem: Identify the problem statement or the negative sense/tension point.",
              subbullets: ["Middle part of the paragraph"],
            },
            {
              num: "S",
              text: "Solution/Suggestion: Identify the suggestion-oriented sense or conclusion.",
              subbullets: [
                "Look for 'Hence', 'In conclusion', 'Thus', 'Lastly'",
              ],
            },
          ],
        },
      ],
      keyTakeaways: [
        "Find mandatory pairs (MP) by looking for linked phrases or Noun-Pronoun sequences.",
        "Identify the cause-effect relationship; effects usually follow their causes.",
        "Always follow the Past-Present-Future (PPF) chronology order.",
        "Use transition words to bridge ideas from one sentence to the next.",
      ],
    },
    Cloze_Test: {
      title: "Cloze Test",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the Topic – Cloze Test. Along with this, students will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in Aptitude Tests like NIFT / NID / UCEED and all such entrances.",
      sections: [
        {
          id: "read-thoroughly",
          heading: "Read Thoroughly",
          icon: "📖",
          color: "#FF820F",
          bullets: [
            "Read the passage provided very thoroughly to form an idea about the topic.",
            "Read slowly and gain an understanding of the text.",
            "Once the theme of the text is somewhat clear, your job becomes easier.",
            "You can then go on to think of the appropriate words that suit the situation being described and proceed to filling in the blanks.",
          ],
        },
        {
          id: "link-sentences",
          heading: "Link the Sentences Together",
          icon: "🔗",
          color: "#407BFF",
          bullets: [
            "Remember that it is a passage with sentences that are connected to each other.",
            "Do not make the mistake of treating each sentence like an individual one and filling in the blanks accordingly.",
            "Try to come up with logical connections that link up the sentences together and your job will automatically become easier.",
          ],
        },
        {
          id: "type-of-word",
          heading: "The Type of Word to Fill in",
          icon: "🔤",
          color: "#15E4B9",
          bullets: [
            "Now look at the blanks carefully and assess the kind of words you have to fill in.",
            "Which part of speech would it be? Would it be a noun, a pronoun, a verb, a preposition, a conjunction or an article?",
          ],
        },
        {
          id: "eliminate",
          heading: "Eliminate Options",
          icon: "❌",
          color: "#940B92",
          bullets: [
            "We can easily identify most unfit/illogical words in answer options.",
            "We should quickly eliminate these words. Only after that we should try to find the most fit word.",
            "Elimination will enhance accuracy and hence score.",
          ],
        },
        {
          id: "frequent-words",
          heading: "Go with Frequently Used Words",
          icon: "💬",
          color: "#FFC726",
          body: "Sometimes, you may not be able to decide between two words. In this case, if you see a word in the options that is frequently used with the words around the blank, then pick that option.",
        },
        {
          id: "tone",
          heading: "Check Tone of Passage",
          icon: "🎭",
          color: "#FF7A7A",
          bullets: [
            "The passage is usually written in a certain tone; sometimes narrative, sometimes critical, sometimes humorous.",
            "Pick words that fit in with the tone of the passage.",
          ],
        },
        {
          id: "hints",
          heading: "Helpful Hints",
          icon: "💡",
          color: "#58B6C5",
          hintItems: [
            {
              num: "#1",
              text: "You must ensure that the word you have inserted in the blank enables you to read the sentence smoothly and correctly.",
              subbullets: [],
            },
            {
              num: "#2",
              text: "Apply grammar rules (prepositions, noun, pronoun, adjective, verb, etc.)",
              subbullets: [
                "Preposition following a noun, adjective or verb. (Example: look at images)",
                "A prepositional phrase. (Example: in spite of)",
                "An adverb. (Example: they vacated the house two years ago)",
                "A connector. (Example: it is raining, therefore ground is wet.)",
                "A conjunction. (Example: although he is seven, he can speak eight languages)",
                "An auxiliary verb, an article, a pronoun — either subject or object. (Example: it is easier to know)",
                "A comparative or superlative involved? (Example: she's taller than me)",
              ],
            },
          ],
        },
      ],
      keyTakeaways: [
        "Always read the full passage first before attempting to fill in any blanks.",
        "Treat the passage as a connected piece — do not solve each sentence in isolation.",
        "Identify the part of speech needed for each blank before looking at options.",
        "Eliminate obviously incorrect options first to improve accuracy.",
        "Match the tone of the passage when selecting the right word.",
        "When stuck between two words, choose the one that is more frequently used with the surrounding words.",
      ],
    },

    Spot_the_Error: {
      title: "Spot the Error",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the Topic – Error Spotting. Along with this, students will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in Aptitude Tests like NIFT / NID / UCEED and all such entrances.",
      introduction:
        "Sentences, words, and phrases are the roots of any language. Errors in sentences leave a poor impression on the readers. It becomes difficult to understand the exact meaning of these sentences, and they also change the meaning of the sentence. So, error spotting is a systematic way of identifying and rectifying these errors. It is commonly used while proofreading and editing written documents.",
      sections: [
        {
          id: "rule-1",
          heading: "Rule 1: Check and Identify the Part of Speech",
          icon: "🔤",
          color: "#FF820F",
          body: "A part of speech classifies the way a word functions in a sentence. There are eight parts of speech in English Grammar: interjection, adjective, pronoun, verb, noun, preposition, adverb, and conjunction. For finding the part of speech of any sentence, check the function and role of words in a sentence.",
          bullets: [
            "If the word is a naming word (name of any place, person, etc.) — it is a Noun.",
            "If the word is replacing the noun — it is a Pronoun.",
            "If the word is showing an action — it is a Verb.",
            "If the word depicts the quality of a noun — it is an Adjective.",
            "If the word is modifying the verb and adjective — it is an Adverb.",
            "If the word connects other words — it is a Conjunction.",
            "If the word describes the relationship between noun and pronoun — it is a Preposition.",
          ],
          example: {
            question: "Ram are playing.",
            answer: "Ram is playing.",
            explanation:
              "Ram is a noun (singular) so the correct helping verb is 'is', not 'are'.",
          },
        },
        {
          id: "rule-2",
          heading: "Rule 2: Identify the Type of Sentence",
          icon: "📝",
          color: "#407BFF",
          body: "The three kinds of sentences in English Grammar are interrogative, affirmative, and negative sentences.",
          bullets: [
            "Affirmative – written in general form with subject, verb, and object.",
            "Negative – consists of 'not' with the verb.",
            "Interrogative – helping verb comes first, or it begins with question words like why, how, where.",
          ],
          example: {
            question: "How are you going?",
            answer: "Where are you going?",
            explanation:
              "'How' asks about the manner of going, which doesn't fit contextually. 'Where' asks about the destination, which is the intended meaning.",
          },
        },
        {
          id: "rule-3",
          heading: "Rule 3: Check the Punctuation",
          icon: "✏️",
          color: "#15E4B9",
          body: "Punctuation plays a major role in demonstrating the exact rhythm of the paragraph. Checking commas, question marks, full stops, and other punctuations is important.",
          bullets: [
            "If two sentences are joined, they must have a comma in between to show separation.",
            "If the sentence is ending, it must have a full stop.",
            "If the sentence has a question word and a helping word at the beginning, it must end with a question mark.",
          ],
          example: {
            question: "How are you,",
            answer: "How are you?",
            explanation:
              "The sentence is a question, so it must end with a question mark, not a comma.",
          },
        },
        {
          id: "rule-4",
          heading: "Rule 4: Identify the Pronouns",
          icon: "🔗",
          color: "#940B92",
          body: "Pronouns are words used in place of nouns. Different types of pronouns take different nouns in the sentences, so one must know the correct pronoun while replacing a noun.",
          bullets: [
            "Use 'he/him' for a male noun.",
            "Use 'she/her' for a female noun.",
            "Use 'it' for a non-living or animal noun.",
            "Use 'they/them' for plural nouns.",
          ],
          example: {
            question: "Ram is a boy. She is playing cricket.",
            answer: "Ram is a boy. He is playing cricket.",
            explanation:
              "'Ram' is a male noun, so the correct pronoun is 'he', not 'she'.",
          },
        },
        {
          id: "rule-5",
          heading: "Rule 5: Identify the Singularity and Plurality",
          icon: "🔢",
          color: "#FFC726",
          body: "Sentences in the English language are either singular or plural. There are specific rules to identify which sentence is singular and plural. Typically, the s/es are checked to identify the nouns and sentences as singular or plural.",
          bullets: [
            "Singular nouns take singular verbs (is, was, has).",
            "Plural nouns take plural verbs (are, were, have).",
            "Words like 'many', 'few', 'several' are followed by plural nouns and verbs.",
            "Adding -s or -es to a noun generally makes it plural.",
          ],
          example: {
            question: "Many boy are dancing together.",
            answer: "Many boys are dancing together.",
            explanation:
              "'Many' indicates plurality, so 'boy' should be plural — 'boys'.",
          },
        },
      ],
      keyTakeaways: [
        "Error spotting is a systematic way of identifying and correcting grammatical errors in sentences.",
        "Always identify the part of speech first — noun, pronoun, verb, adjective, adverb, conjunction, preposition.",
        "Check whether the sentence is affirmative, negative, or interrogative and verify structure accordingly.",
        "Punctuation errors are common — ensure correct use of commas, full stops, and question marks.",
        "Match pronouns to their nouns correctly — gender and number must agree.",
        "Always check for singular/plural agreement between the subject and the verb.",
      ],
    },

    Sentence_Correction: {
      title: "Sentence Completion and Sentence Correction",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the Topic – Sentence Completion and Sentence Correction. Along with this, students will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in Aptitude Tests like NIFT / NID / UCEED and all such entrances.",
      sections: [
        {
          id: "blanks",
          heading: "Check the Number of Blanks",
          icon: "🔲",
          color: "#FF820F",
          body: "If you have a single blank to complete the sentence, then look for the hints and word charge. If you have more than one blank then it is best you go for backtracking the options and blanks. This helps in easy elimination and answering.",
          bullets: [
            "Single blank – look for hints in surrounding words and the overall tone of the sentence.",
            "Multiple blanks – use backtracking: test each option against all blanks to eliminate wrong choices quickly.",
          ],
        },
        {
          id: "context",
          heading: "Contextual Meaning",
          icon: "💡",
          color: "#407BFF",
          body: "First read the sentence and understand the context that is stated. Then you can attempt to visualize and answer the question — but remember, use a word that when filled in, does not change the meaning or the underlying context of the sentence.",
          bullets: [
            "Read the full sentence carefully before looking at the options.",
            "Identify the overall theme or message the sentence is trying to convey.",
            "Avoid words that shift or distort the original meaning of the sentence.",
          ],
        },
        {
          id: "order-of-words",
          heading: "Order of Words",
          icon: "🔤",
          color: "#15E4B9",
          body: "In English Grammar, the order of words is very important. There is a structure that you need to maintain. This is mostly related to the Parts of Speech. You need to know where to use a noun followed by a pronoun and the same goes with adjectives or other parts of speech.",
          bullets: [
            "Follow the standard Subject → Verb → Object (SVO) word order.",
            "Place adjectives before the noun they describe.",
            "Adverbs typically follow the verb they modify.",
            "Prepositions must come immediately before their object nouns or pronouns.",
          ],
        },
        {
          id: "grammar",
          heading: "Correct Grammar",
          icon: "✏️",
          color: "#940B92",
          body: "Even when you think the word you choose is correct, be double sure if you don't know the exact meaning of the word. In case you just guess, you might go wrong. Grammar needs to be kept in mind — especially in phrase and sentence based questions.",
          bullets: [
            "Check subject-verb agreement (singular/plural).",
            "Verify tense consistency throughout the sentence.",
            "Ensure articles (a, an, the) are used correctly.",
            "Do not rely on guessing — verify your choice fits grammatically.",
          ],
        },
        {
          id: "visualize",
          heading: "Visualize",
          icon: "🧠",
          color: "#FFC726",
          body: "Make it a habit to first visualize your answer — read the sentence and think of a possible word to fill in before looking at the options. This makes it easy to not go wrong and choose the right word that suits the context.",
          bullets: [
            "Cover the options, read the sentence, and mentally fill in a word that feels right.",
            "Compare your mental answer with the given options — the closest match is likely correct.",
            "Visualising reduces the chance of being misled by distractors.",
          ],
        },
        {
          id: "similar-options",
          heading: "Similar Options",
          icon: "🔍",
          color: "#FF7A7A",
          body: "There might be instances where you find two or more options, or sometimes all of the options are synonymous to each other — but be very careful. You will have to choose a word that fits correctly in the context given to you.",
          bullets: [
            "When options look similar, focus on subtle shades of meaning rather than surface similarity.",
            "A synonym is not always an appropriate replacement — context and connotation matter.",
            "Eliminate options that are grammatically incorrect even if the meaning seems close.",
          ],
        },
        {
          id: "vocabulary",
          heading: "Increase Vocabulary Skills",
          icon: "📚",
          color: "#58B6C5",
          body: "The most important point to improve your score in sentence completion is increasing and improving your vocabulary.",
          bullets: [
            "Read newspapers, novels, and academic texts regularly to encounter new words in context.",
            "Maintain a personal vocabulary journal — note new words, their meanings, and example sentences.",
            "Practice word roots, prefixes, and suffixes to decode unfamiliar words during the exam.",
            "Attempt previous-year questions to understand the level and type of vocabulary tested.",
          ],
        },
      ],
      keyTakeaways: [
        "For a single blank, look for context clues; for multiple blanks, use backtracking to eliminate wrong options.",
        "Always read the full sentence first to understand its context before selecting an answer.",
        "Maintain correct word order — Subject → Verb → Object — and proper placement of parts of speech.",
        "Verify your answer grammatically — check tense, subject-verb agreement, and article usage.",
        "Visualise a possible answer before looking at options to avoid being misled by distractors.",
        "When options are synonymous, choose the one that best fits the specific context and connotation.",
        "A strong vocabulary is the single most effective way to improve your sentence completion score.",
      ],
    },
    Reading_Comprehension: {
      title: "Reading Comprehension",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the topic – Reading Comprehension. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
      introduction:
        "Reading Comprehension (RC) passages can be time consuming, energy sapping and information intensive. However, the rewards they offer are fascinating: if you grasp a passage well, you can gain significant marks. The approach you adopt for RC is the most significant factor in your accuracy and speed.",
      sections: [
        {
          id: "approaches",
          heading: "Effective Approaches for RC",
          icon: "🧭",
          color: "#407BFF",
          bullets: [
            "Do not over-emphasize trivialities: Focus on the main point, not just examples or illustrations.",
            "Do not memorize: Prioritize understanding the flow and structure over every word.",
            "Do not read the passage first: Go through the questions first to focus your reading.",
            "Do not over-emphasize vocabulary: Focus on the gist rather than getting stuck on particular words.",
            "Do not spend time on difficult RCs: Focus on the ones you can manage easily first.",
            "Answers are within the passage: Correct options are never 'out of scope'.",
            "Do not rely only on 'trigger words': Words like 'but' or 'however' can shift the entire meaning.",
          ],
        },
        {
          id: "tactics",
          heading: "Strategic Tactics",
          icon: "🎯",
          color: "#FF820F",
          bullets: [
            "Make Notes: For abstract text, note the general flow and important markers.",
            "Try to pre-empt questions: Identify parts of the passage likely to be used for questions.",
            "Focus on Opening & Closing paragraphs: The main idea often lies in the first and last paragraphs.",
            "Constantly question yourself: Ask 'What is the author's main point?' as you read.",
          ],
        },
      ],
      keyTakeaways: [
        "Skim questions before reading the passage to identify focus areas.",
        "Prioritize the overall flow and structure over memorizing minor details.",
        "Always base your final selection on information provided within the text.",
        "Pay extra attention to the first and last paragraphs for main idea questions.",
        "Maintain concentration by questioning the author's intent throughout.",
      ],
    },
    Miscellaneous: {
      title: "Miscellaneous",
      subject: "General English",
      learningOutcome:
        "In this lecture, we shall learn the basic concepts from the Miscellaneous section. Students will be empowered to solve a group of mixed language concepts including sentence improvement, error detection, and commonly confused words commonly asked in exams like NIFT/NID/UCEED.",
      introduction:
        "In English examinations, Miscellaneous refers to a group of mixed language concepts that do not fall under one single heading like grammar or vocabulary but are frequently tested.",
      sections: [
        {
          id: "importance",
          heading: "Why Miscellaneous Topics are Important",
          subsections: [
            {
              type: "card",
              color: "#407BFF",
              icon: "📊",
              title: "Scoring & Direct",
              body: "Questions are usually direct and scoring. They require conceptual clarity rather than just rote memorization.",
            },
            {
              type: "card",
              color: "#15E4B9",
              icon: "🧠",
              title: "Practical Usage",
              body: "This section helps eliminate confusion between similar language rules and tests your practical language awareness.",
            },
          ],
        },
        {
          id: "common-areas",
          heading: "Common Areas Covered",
          icon: "📋",
          color: "#FF820F",
          bullets: [
            "Sentence Improvement & Error Detection",
            "One-Word Substitution & Foreign Words",
            "Spelling Errors & Punctuation",
            "Commonly Confused Words",
            "Basic Voice & Speech transformations",
          ],
        },
        {
          id: "sentence-logic",
          heading: "Sentence Improvement & Error Detection",
          subsections: [
            {
              type: "card",
              color: "#FF7A7A",
              icon: "✍️",
              title: "Sentence Improvement",
              body: "Choose the most appropriate replacement for an underlined part. If perfectly correct, choose 'No improvement'. Check for grammar, clarity, and conciseness.",
            },
            {
              type: "card",
              color: "#940B92",
              icon: "🔍",
              title: "Error Detection",
              body: "Identify grammatical or logical errors in parts of a sentence. Common areas: Subject-Verb agreement, Tense consistency, and Preposition/Article misuse.",
            },
          ],
        },
        {
          id: "vocal-miscellany",
          heading: "Vocabulary & Word Usage",
          subsections: [
            {
              type: "card",
              color: "#EBC33C",
              icon: "🗣️",
              title: "Confused Words",
              body: "Words that look or sound similar but have different meanings (e.g., Affect vs Effect). Context is the only way to decide the correct choice.",
            },
            {
              type: "card",
              color: "#407BFF",
              icon: "📖",
              title: "One-Word Substitution",
              body: "Replacing a whole phrase with a single precise word (e.g., Polyglot for someone speaking many languages). Tests vocabulary precision.",
            },
          ],
        },
        {
          id: "technical-points",
          heading: "Technical Grammar Points",
          subsections: [
            {
              type: "card",
              color: "#FF820F",
              icon: "🏛️",
              title: "Foreign Words",
              body: "English often uses words from Latin, French, or Greek. You may be asked for their meanings or correct usage in sentences.",
            },
            {
              type: "card",
              color: "#15E4B9",
              icon: "❗",
              title: "Punctuation & Spelling",
              body: "Correct usage of commas, stops, and capitalization improves clarity. Spelling tests focus on silent letters, double letters, and vowel sounds.",
            },
          ],
        },
        {
          id: "strategy",
          heading: "How to Prepare",
          icon: "🪜",
          color: "#940B92",
          tips: [
            "Revise basic grammar rules constantly.",
            "Practice 'mixed' question sets to build adaptability.",
            "Focus on high-frequency vocabulary and common confusion pairs.",
            "Read sentences carefully before jumping to options.",
            "Don't be afraid to choose 'No Error' or 'No Improvement'.",
          ],
        },
      ],
      keyTakeaways: [
        "The simplest, clearest, and most logical option is usually the correct one.",
        "Meaning must always match the context, not just the literal definition.",
        "Tone (positive/negative) matters when choosing synonyms or antonyms in context.",
        "Punctuation and capitalization are subtle but critical for clarity.",
      ],
    },

    // ── General Test Topic Notes Begin Below ──
  },

  General_Test: {
    Mathematics: {
      Number_System: {
        title: "Number System",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Number System-1. Along with this student will be empowered to solve different categories of questions involving conversion, increase / decrease, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "A number is an arithmetic value used for representing the quantity and used in making calculations.",
        sections: [
          {
            id: "types-of-numbers",
            heading: "Basic Types of Numbers",
            icon: "🔢",
            color: "#407BFF",
            subsections: [
              {
                title: "Natural Numbers",
                icon: "🌱",
                body: "All positive integers from 1 till infinity. Denoted by N.",
                examples: [
                  "N = {1, 2, 3, 4, ...}",
                  "Smallest natural number is 1",
                  "Zero is NOT a natural number",
                ],
                color: "#407BFF",
              },
              {
                title: "Whole Numbers",
                icon: "⭕",
                body: "All positive integers from 0 till infinity. Denoted by W. Known as non-negative integers.",
                examples: [
                  "W = {0, 1, 2, 3, 4, ...}",
                  "Smallest whole number is 0",
                ],
                color: "#FF9A4F",
              },
              {
                title: "Integers",
                icon: "➕",
                body: "Includes all whole numbers and their negative counterparts. Denoted by I.",
                examples: [
                  "I = {..., -2, -1, 0, 1, 2, ...}",
                  "Positive: {1, 2, ...}",
                  "Negative: {-1, -2, ...}",
                  "Zero is neither positive nor negative",
                ],
                color: "#940B92",
              },
            ],
          },
          {
            id: "real-rational-irrational",
            heading: "Real, Rational & Irrational",
            icon: "➗",
            color: "#FF820F",
            subsections: [
              {
                title: "Rational Numbers",
                icon: "📊",
                body: "Numbers expressed as p/q (p, q are integers, q ≠ 0). Includes perfect squares and repeating decimals.",
                examples: [
                  "1/2, 3/4, 7",
                  "Zero is a rational number",
                  "√4, √9, √49",
                  "0.333..., 1.25",
                ],
                color: "#FF820F",
              },
              {
                title: "Irrational Numbers",
                icon: "♾️",
                body: "Numbers that cannot be expressed as p/q. Non-periodic infinite decimal fractions.",
                examples: ["√2, π, √3, √11", "0.0435523...", "0.3425452..."],
                color: "#58B6C5",
              },
            ],
          },
          {
            id: "number-classifications",
            heading: "Further Classifications",
            icon: "💡",
            color: "#FF7A7A",
            bullets: [
              "Prime Number: Natural number > 1 divisible only by 1 and itself (2, 3, 5, 7...). 2 is the only even prime.",
              "Composite Number: Non-prime natural numbers having factors other than 1 and itself (4, 6, 8, 9...).",
              "Co-prime Numbers: Two numbers having only 1 as a common factor (e.g., 25 & 9).",
              "Twin Prime: A prime number that is 2 more or 2 less than another prime (e.g., 3 & 5, 11 & 13).",
              "Even Number: Divisible by 2 with remainder 0 (2, 4, 8...).",
              "Odd Number: Cannot be divided by 2 (3, 5, 7, 13...).",
              "Important: 1 is neither prime nor composite.",
            ],
          },
          {
            id: "hcf-lcm",
            heading: "HCF & LCM",
            icon: "🧮",
            color: "#EBC33C",
            listItems: [
              {
                title: "HCF (Highest Common Factor)",
                description:
                  "The largest positive integer that divides two or more integers without leaving a remainder. Example: HCF of 8 and 12 is 4.",
              },
              {
                title: "LCM (Least Common Multiple)",
                description:
                  "The smallest positive integer that is divisible by both given numbers. Example: LCM of 4 and 6 is 12.",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Every rational number can be written in the form p/q where q ≠ 0.",
          "All terminating and repeating decimals are rational numbers.",
          "Irrational numbers have non-terminating and non-repeating decimal expansions.",
          "The set of real numbers consists of both rational and irrational numbers.",
          "Prime numbers have exactly two factors; 2 is the only even prime.",
          "Co-prime numbers must have HCF equal to 1, even if they are not prime.",
          "For any two positive integers: HCF × LCM = Product of the numbers.",
        ],
      },
      "Simplification_&_Calculation": {
        title: "Simplification",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Simplification. Along with this student will be empowered to solve different categories of questions involving conversion, increase / decrease, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Simplification means reducing the expression in a simpler form using various operations while Approximation is simplifying the mathematical expression to its nearest value but not exactly correct. To find the approximate value, we round off the digits in the expression to the nearest value and simplify the expression using BODMAS.",
        sections: [
          {
            id: "bodmas",
            heading: "BODMAS Rule",
            icon: "🧮",
            color: "#407BFF",
            body: "The operations used to simplify follows a fixed order known as BODMAS:",
            bullets: [
              "B = Bracket",
              "O = of",
              "D = Division",
              "M = Multiplication",
              "A = Addition",
              "S = Subtraction",
            ],
          },
          {
            id: "question-styles",
            heading: "Styles of Questions",
            icon: "📝",
            color: "#FF9A4F",
            listItems: [
              {
                title: "Missing numbers",
                description:
                  "A given equation asks to fill in the blank (Left-hand or Right-hand side). Example: 240 – __ + 100 = 5 × 35 + 265.",
              },
              {
                title: "Simplifying the equation",
                description:
                  "The direct method of solving an equation to obtain the result. Example: 242 – 235 + 90 = ?.",
              },
            ],
          },
          {
            id: "important-terms",
            heading: "Important Terms",
            icon: "📖",
            color: "#940B92",
            listItems: [
              {
                title: "Vinculum",
                description:
                  "Vinculum is a horizontal line drawn over a group of terms in a mathematical expression to indicate that they are to be operated on as a single entity by the preceding or following operator.",
              },
              {
                title: "Brackets",
                description:
                  "Brackets in simplification are of three types: Round brackets ( ), Curly brackets { }, and Box brackets [ ]. The order in which these brackets are operated is ( ), { } and [ ]. 'Of' means multiplication but it is operated before division.",
              },
              {
                title: "Surds and Indices",
                description:
                  "A surd is an irrational number that can be expressed with roots, such as √12 or ∛12. An index is a power to which a number is raised, and the plural of the index is indices. For example, k¹² has an index of 12.",
              },
            ],
          },
          {
            id: "tips-tricks",
            heading: "Tips and Tricks",
            icon: "💡",
            color: "#FF820F",
            tips: [
              "Integer & Fraction separately: If all fractions have a common denominator, calculate the integer part and fraction part separately.",
              "Decimal to Integer: Convert decimals into integers for approximate values (e.g., 46.72 → 47).",
              "Practice rounded-off values: For numbers in decimal format, use rounded-off values. Example: 46.72 → 47, 21.10 → 21.",
              "Follow BODMAS: Always solve simplification problems following the BODMAS rule.",
              "Memorize Tables: Memorizing tables up to 20 helps save time.",
              "Don't overcomplicate: Skip hard calculations if needed to work the equations in the shortest time.",
            ],
          },
        ],
        keyTakeaways: [
          "Simplification reduces expressions to their simplest exact form.",
          "Approximation helps in estimating values quickly by rounding off numbers.",
          "Brackets must be solved in the order: ( ), { }, [ ].",
          "Vinculum indicates priority and must be resolved first.",
          "Surds represent irrational numbers expressed in radical form.",
          "Indices indicate powers and follow specific algebraic laws.",
          "Speed and accuracy together are essential for success in competitive tests.",
        ],
      },
      Algebra: {
        title: "Algebra",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Algebra. Students will be empowered to solve linear equations, work with algebraic identities, perform operations on polynomials, and apply factorization methods commonly asked in aptitude tests like NIFT/NID/UCEED.",
        introduction:
          "Algebra is a branch of mathematics that uses letters, symbols, and numbers to represent quantities and their relationships. It allows us to represent unknown values, form equations, and generalize mathematical patterns.",
        sections: [
          {
            id: "basic-terms",
            heading: "Basic Algebraic Terms",
            subsections: [
              {
                type: "card",
                color: "#407BFF",
                icon: "🔤",
                title: "Variable",
                body: "A symbol (usually a letter like x, y) that represents an unknown value.",
                examples: ["x + 5 = 12 → x is the variable"],
              },
              {
                type: "card",
                color: "#FF820F",
                icon: "🔢",
                title: "Constant",
                body: "A fixed numerical value that does not change.",
                examples: ["3, −7, 10 are constants"],
              },
              {
                type: "card",
                color: "#15E4B9",
                icon: "✖️",
                title: "Coefficient",
                body: "The numerical part multiplied with a variable.",
                examples: ["In 5x, 5 is the coefficient"],
              },
              {
                type: "card",
                color: "#940B92",
                icon: "🧩",
                title: "Term",
                body: "A single part of an algebraic expression separated by + or −.",
                examples: ["3x + 4y − 7 has three terms"],
              },
            ],
          },
          {
            id: "expressions-equations",
            heading: "Expressions & Equations",
            body: "An algebraic expression is a combination of variables, constants, and operations (+, −, ×, ÷) without an equals sign. An equation shows equality using (=).",
            subsections: [
              {
                type: "card",
                color: "#FF7A7A",
                icon: "📝",
                title: "Types of Expressions",
                body: "Categorized by the number of terms they contain.",
                examples: [
                  "Monomial: 4x",
                  "Binomial: x + 5",
                  "Trinomial: x² + x + 1",
                ],
              },
              {
                type: "card",
                color: "#58B6C5",
                icon: "⚖️",
                title: "Linear Equations",
                body: "Equations where the highest power of the variable is 1. General forms: ax + b = 0.",
                examples: ["x + 4 = 10", "Only one solution"],
              },
            ],
          },
          {
            id: "polynomials-intro",
            heading: "Polynomials & Degrees",
            icon: "📉",
            color: "#EBC33C",
            body: "A polynomial is an expression with non-negative integer powers. The degree is the highest power of the variable.",
            bullets: [
              "3x² + 2x + 1 → Degree 2",
              "5x³ → Degree 3",
              "Non-Example: Variable in denominator is NOT a polynomial",
            ],
          },
          {
            id: "operations",
            heading: "Operations on Expressions",
            subsections: [
              {
                type: "card",
                color: "#407BFF",
                icon: "➕",
                title: "Addition & Subtraction",
                body: "Combine only LIKE terms (same variable and power).",
              },
              {
                type: "card",
                color: "#FF820F",
                icon: "✖️",
                title: "Multiplication",
                body: "Use distributive law. Multiply coefficients and variables separately.",
              },
              {
                type: "card",
                color: "#15E4B9",
                icon: "➗",
                title: "Division",
                body: "Divide coefficients and subtract powers of the same base variable.",
              },
            ],
          },
          {
            id: "identities",
            heading: "Standard Algebraic Identities",
            icon: "💡",
            color: "#FF820F",
            table: {
              headers: ["Identity Name", "Formula"],
              rows: [
                ["Square 1", "(a + b)² = a² + 2ab + b²"],
                ["Square 2", "(a − b)² = a² − 2ab + b²"],
                ["Difference of Squares", "(a + b)(a − b) = a² − b²"],
                ["Quadratic Form", "ax² + bx + c (a ≠ 0)"],
              ],
            },
          },
          {
            id: "factorization",
            heading: "Factorization Methods",
            icon: "🔨",
            color: "#940B92",
            body: "Expressing an algebraic expression as a product of factors.",
            listItems: [
              {
                title: "Common Factor",
                description:
                  "Taking out the greatest common divisor from all terms.",
              },
              {
                title: "Using Identities",
                description: "Reversing identities like a² - b².",
              },
              {
                title: "Middle Term Splitting",
                description:
                  "Standard method for factoring quadratic expressions.",
              },
            ],
          },
          {
            id: "common-errors",
            heading: "Common Errors to Avoid",
            icon: "⚠️",
            color: "#FF7A7A",
            tips: [
              "Do NOT mix unlike terms (e.g., 3x + 4 != 7x).",
              "Be extremely careful with signs (+ / −) when distributing.",
              "Always follow the correct identity structure (don't forget 2ab).",
              "Check for transposition errors when moving terms across the equals sign.",
            ],
          },
          {
            id: "how-to-study",
            heading: "Effective Study Approach",
            icon: "🧠",
            color: "#15E4B9",
            bullets: [
              "Practice step-by-step solving to maintain equality balance.",
              "Learn and derive identities rather than blind memorization.",
              "Verify your solution by substituting the variable back into the equation.",
              "Focus on logical thinking—Algebra is about balance.",
            ],
          },
        ],
        keyTakeaways: [
          "Identities are equations true for all values of variables.",
          "Factorization is the reverse of multiplication.",
          "The highest power determines the degree and number of potential roots.",
          "Like terms are the only ones that can be combined through addition/subtraction.",
        ],
      },
      "Fraction,_Decimals_&_Square_root": {
        title: "Fraction, Decimals & Square Root",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Fractions, Decimals and Square Roots. Students will be empowered to solve problems involving part-to-whole relationships, decimal transformations, and calculation of square roots using prime factorization and long division.",
        introduction:
          "Fractions and decimals are two different ways of representing parts of a whole, while square roots explore the relationship between a number and its squared value. Understanding these forms the backbone of numerical calculations in aptitude tests.",
        sections: [
          {
            id: "fractions-basics",
            heading: "Understanding Fractions",
            subsections: [
              {
                type: "card",
                color: "#407BFF",
                icon: "🍕",
                title: "What is a Fraction?",
                body: "A fraction represents a part of a whole, written as Numerator / Denominator. The Numerator is the number of parts taken, and the Denominator is the total equal parts.",
              },
              {
                type: "card",
                color: "#FF820F",
                icon: "📊",
                title: "Types of Fractions",
                body: "Proper: Num < Den (3/5). Improper: Num ≥ Den (7/4). Mixed: Whole number + proper fraction (1 ¾). Like: Same denominator. Unlike: Different denominators.",
              },
              {
                type: "card",
                color: "#15E4B9",
                icon: "⚖️",
                title: "Equivalent & Simplest Form",
                body: "Equivalent: Represent the same value (e.g., 1/2 = 2/4). Simplest form: When the numerator and denominator have no common factor except 1.",
              },
            ],
          },
          {
            id: "fraction-operations",
            heading: "Operations on Fractions",
            icon: "➕",
            color: "#940B92",
            body: "Basic arithmetic follows specific rules for fractions:",
            bullets: [
              "Addition/Subtraction: Requires converting to Like Fractions (same denominator).",
              "Multiplication: Multiply numerators together and denominators together.",
              "Division: Multiply the first fraction by the reciprocal of the second.",
            ],
          },
          {
            id: "decimals-basics",
            heading: "Understanding Decimals",
            subsections: [
              {
                type: "card",
                color: "#FF7A7A",
                icon: "📍",
                title: "What is a Decimal?",
                body: "A decimal is another way of representing fractions using a decimal point (e.g., 0.5 = 1/2). Digits to the right represent Tenths, Hundredths, etc.",
              },
              {
                type: "card",
                color: "#58B6C5",
                icon: "🔄",
                title: "Types of Decimals",
                body: "Terminating: Ends after finite digits (0.25). Repeating: Pattern repeats (0.333...). Non-repeating: Infinite digits with no pattern (Irrational).",
              },
            ],
          },
          {
            id: "sq-root-basics",
            heading: "Square Root Concepts",
            subsections: [
              {
                type: "card",
                color: "#EBC33C",
                icon: "📐",
                title: "Square Root (√)",
                body: "A value which, when multiplied by itself, gives the original number. Example: √16 = 4 because 4 × 4 = 16.",
              },
              {
                type: "card",
                color: "#FF820F",
                icon: "🏁",
                title: "Perfect Squares",
                body: "Numbers that are squares of whole numbers (1, 4, 9, 16, 25, 36...). Their square roots are always whole numbers.",
              },
            ],
          },
          {
            id: "sq-root-methods",
            heading: "Methods to Find Square Root",
            icon: "🖊️",
            color: "#407BFF",
            listItems: [
              {
                title: "Prime Factorization",
                description:
                  "Best for perfect squares. Break the number into prime factors and pick one from each pair.",
              },
              {
                title: "Long Division",
                description:
                  "Best for large numbers or decimals to get accurate results where factorization is difficult.",
              },
            ],
          },
          {
            id: "sq-root-properties",
            heading: "Properties of Square Root",
            icon: "💡",
            color: "#15E4B9",
            bullets: [
              "√a × √b = √(ab)",
              "√(a/b) = √a / √b",
              "The square root of a negative number is not a real number.",
              "Square roots of non-perfect squares are irrational.",
            ],
          },
          {
            id: "common-errors",
            heading: "Common Errors to Avoid",
            icon: "⚠️",
            color: "#FF7A7A",
            tips: [
              "Forgetting to simplify fractions to their lowest terms.",
              "Misplacing the decimal point during addition or multiplication.",
              "Assuming all square roots result in whole numbers.",
              "Ignoring signs when converting between fractions and decimals.",
            ],
          },
          {
            id: "exam-tips",
            heading: "Exam Strategy",
            icon: "🎯",
            color: "#940B92",
            bullets: [
              "Simplify before starting long calculations.",
              "Check if a number is a perfect square to save time.",
              "Convert decimals to fractions if they simplify the arithmetic.",
              "Estimate square roots for faster elimination of options.",
            ],
          },
          {
            id: "applications",
            heading: "Practical Applications",
            icon: "🛠️",
            color: "#58B6C5",
            body: "These concepts are essentially applied in:",
            bullets: [
              "Percentage and Ratio calculations.",
              "Measurement problems (Area, Volume).",
              "Speed, Distance, and Time problems.",
              "Geometry and Coordinate problems.",
            ],
          },
        ],
        keyTakeaways: [
          "Fractions and decimals are interchangeable forms of the same idea.",
          "Square roots test your understanding of a number's fundamental structure.",
          "A rational number can be expressed as a terminates or repeating decimal.",
          "Non-terminating, non-repeating decimals represent irrational numbers.",
        ],
      },
      "Ratio_&_Proportion": {
        title: "Ratio & Proportion",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Ratio and Proportion. Students will be empowered to compare quantities, verify equality of ratios (proportions), and solve problems involving direct/inverse relationships commonly asked in aptitude tests like NIFT/NID/UCEED.",
        introduction:
          "Ratio and Proportion deal with the comparison of quantities of the same kind and help in understanding their relative sizes. They form the base for many essential topics like Percentage, Profit & Loss, and Time & Work.",
        sections: [
          {
            id: "ratio-basics",
            heading: "Understanding Ratio",
            subsections: [
              {
                type: "card",
                color: "#407BFF",
                icon: "⚖️",
                title: "What is a Ratio?",
                body: "A ratio compares two quantities of the same unit by division. Written as a : b or a/b. The first term is the Antecedent and the second is the Consequent.",
              },
              {
                type: "card",
                color: "#FF820F",
                icon: "✂️",
                title: "Simplest Form",
                body: "A ratio is in its simplest form when the antecedent and consequent have no common factors except 1 (e.g., 2:3).",
              },
            ],
          },
          {
            id: "ratio-types",
            heading: "Types of Ratios",
            icon: "🔄",
            color: "#15E4B9",
            listItems: [
              {
                title: "Compound Ratio",
                description:
                  "The product of two or more ratios (e.g., for a:b and c:d, the compound ratio is ac : bd).",
              },
              {
                title: "Inverse Ratio",
                description:
                  "The reverse order of the same terms (e.g., b:a instead of a:b).",
              },
              {
                title: "Duplicate/Triplicate",
                description:
                  "Duplicate: a² : b². Triplicate: a³ : b³. Sub-duplicate: √a : √b.",
              },
            ],
          },
          {
            id: "proportion-basics",
            heading: "Understanding Proportion",
            subsections: [
              {
                type: "card",
                color: "#940B92",
                icon: "🔗",
                title: "Equality of Ratios",
                body: "When two ratios are equal (a:b = c:d), they are in proportion, written as a:b :: c:d. The outer terms (a, d) are Extremes and inner terms (b, c) are Means.",
              },
              {
                type: "card",
                color: "#FF7A7A",
                icon: "🔑",
                title: "Fundamental Property",
                body: "Product of Extremes = Product of Means (a × d = b × c). Use this to find missing values in a proportion sequence.",
              },
            ],
          },
          {
            id: "proportion-types",
            heading: "Types of Proportion",
            subsections: [
              {
                type: "card",
                color: "#58B6C5",
                icon: "📈",
                title: "Direct Proportion",
                body: "Both quantities increase or decrease together in the same ratio. (e.g., more workers → more work done).",
              },
              {
                type: "card",
                color: "#EBC33C",
                icon: "📉",
                title: "Inverse Proportion",
                body: "One quantity increases while the other decreases proportionally. (e.g., more workers → less time needed).",
              },
            ],
          },
          {
            id: "division-logic",
            heading: "Division & Comparison",
            icon: "➗",
            color: "#407BFF",
            bullets: [
              "Division in Ratio: If dividing a total T in ratio a:b, Part 1 = T × [a / (a + b)], Part 2 = T × [b / (a + b)].",
              "Comparison: To compare ratios, convert them to fractions and bring them to a common denominator or convert to decimals.",
              "Continued Proportion: If a:b = b:c, then 'b' is the mean proportional between a and c (b² = ac).",
            ],
          },
          {
            id: "strategy-errors",
            heading: "Exam Strategy & Errors",
            icon: "⚠️",
            color: "#FF820F",
            tips: [
              "Units: Always convert all quantities to the same unit before forming a ratio.",
              "Simplify: Simplify ratios at the earliest step to make calculations easier.",
              "Cross-Multiply: For proportion problems, cross-multiplying (a*d = b*c) is your most reliable tool.",
              "Common Error: Avoid comparing quantities of different kinds (e.g., comparing weight to height).",
            ],
          },
          {
            id: "apps",
            heading: "Practical Applications",
            icon: "🛠️",
            color: "#15E4B9",
            body: "Ratio and Proportion concepts are essential for solving:",
            bullets: [
              "Mixture & Allegation problems.",
              "Map reading and scale transformations.",
              "Partnership and profit-sharing distributions.",
              "Speed, Time & Work relationships.",
            ],
          },
        ],
        keyTakeaways: [
          "A ratio compares quantities; a proportion equates ratios.",
          "Product of Extremes = Product of Means is the golden rule for proportion.",
          "Check units carefully before starting any comparison.",
          "In Inverse Proportion, the product of the two variables remains constant.",
        ],
      },
      Partnership: {
        title: "Partnership",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Partnership. Students will be empowered to calculate profit sharing ratios for both simple and compound partnerships involving different capital investments and time periods commonly asked in aptitude tests like NIFT/NID/UCEED.",
        introduction:
          "Partnership is a business arrangement in which two or more persons invest capital in a business and share profits or losses in an agreed ratio. The share of profit or loss depends on both the capital invested and the time for which it is invested.",
        sections: [
          {
            id: "partnership-terms",
            heading: "Important Terms",
            subsections: [
              {
                type: "card",
                color: "#407BFF",
                icon: "🤝",
                title: "Partner",
                body: "A person who invests money in the business and shares its risks and rewards.",
              },
              {
                type: "card",
                color: "#FF820F",
                icon: "💰",
                title: "Capital",
                body: "The amount of money or assets invested by a partner into the business venture.",
              },
              {
                type: "card",
                color: "#15E4B9",
                icon: "📊",
                title: "Profit Sharing Ratio",
                body: "The agreed ratio in which partners distribute the business gains or deficits among themselves.",
              },
            ],
          },
          {
            id: "types-of-partnership",
            heading: "Types of Partnership",
            subsections: [
              {
                type: "card",
                color: "#940B92",
                icon: "📅",
                title: "Simple Partnership",
                body: "All partners invest capital for the same time period. Profit is divided solely in the ratio of their capitals.",
              },
              {
                type: "card",
                color: "#FF7A7A",
                icon: "⏳",
                title: "Compound Partnership",
                body: "Partners invest for different time periods. Profit is divided in the ratio of (Capital × Time).",
              },
            ],
          },
          {
            id: "sharing-principle",
            heading: "The Basic Principle",
            icon: "💡",
            color: "#EBC33C",
            body: "Profit or loss is directly proportional to the product of capital and time. Formula: Profit Share ∝ (Capital × Time).",
            bullets: [
              "Simple Partnership: Profit Ratio = Capital Ratio (since time is equal).",
              "Compound Partnership: Profit Ratio = C1×T1 : C2×T2 : C3×T3, where C is Capital and T is Time.",
            ],
          },
          {
            id: "working-partner",
            heading: "Working Partners & Membership",
            icon: "👤",
            color: "#407BFF",
            bullets: [
              "Working Partner: A partner who manages the business and may receive a Salary or Commission before the remaining profit is shared.",
              "Partner Admission: When a new partner joins, the profit sharing ratio of existing partners is adjusted based on the new investment.",
              "Partner Withdrawal: When a partner leaves, the remaining partners redistribute the future profits in a new agreed ratio.",
            ],
          },
          {
            id: "strategy-errors",
            heading: "Exam Strategy & Errors",
            icon: "⚠️",
            color: "#FF820F",
            tips: [
              "Time Factor: Never ignore the time duration in compound partnership problems.",
              "Unit Consistency: Ensure all time periods are in the same unit (e.g., convert all months into years or vice versa).",
              "Salary Adjustment: Subtract any partner salary or commission from the total profit BEFORE applying the sharing ratio.",
              "Simplify Early: Simplify the capital × time products into their lowest ratio forms to avoid large calculations.",
            ],
          },
        ],
        keyTakeaways: [
          "Equal time periods imply profit ratio is same as capital ratio.",
          "Unequal time periods require the use of the (Capital × Time) formula.",
          "Salary and commissions are treated as business expenses before profit distribution.",
          "The fundamental rule is: More Capital + More Time = More Profit Share.",
        ],
      },
      Geometry: {
        title: "Geometry",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Geometry. Students will be empowered to identify various shapes, understand angle relationships, apply properties of triangles and quadrilaterals, and solve problems involving circles and polygons commonly asked in aptitude tests like NIFT/NID/UCEED.",
        introduction:
          "Geometry is the branch of mathematics that deals with shapes, sizes, positions, and properties of figures. It helps us understand space, structure, and measurement through logical reasoning and visualization.",
        sections: [
          {
            id: "geo-basics",
            heading: "Fundamental Terms",
            subsections: [
              {
                type: "card",
                color: "#407BFF",
                icon: "📍",
                title: "Points & Lines",
                body: "Point: Shows a position (no size). Line: Straight path extending infinitely. Line Segment: Part of a line with two endpoints. Ray: Starts at one point and extends infinitely.",
              },
              {
                type: "card",
                color: "#FF820F",
                icon: "📐",
                title: "Types of Angles",
                body: "Acute (<90°), Right (90°), Obtuse (90°-180°), Straight (180°), Reflex (180°-360°), and Complete (360°).",
              },
            ],
          },
          {
            id: "lines-angles",
            heading: "Lines and Angle Relationships",
            icon: "🧬",
            color: "#15E4B9",
            bullets: [
              "Parallel Lines: Lines that never meet, no matter how far extended.",
              "Transversal: A line that intersects two or more lines (often parallel).",
              "Corresponding Angles: Equal when lines are parallel.",
              "Alternate Interior Angles: Equal when lines are parallel.",
              "Co-interior Angles: Supplementary (Sum is 180°) when lines are parallel.",
            ],
          },
          {
            id: "triangles-basics",
            heading: "Triangles & Their Properties",
            subsections: [
              {
                type: "card",
                color: "#940B92",
                icon: "🔺",
                title: "Classifications",
                body: "By Sides: Equilateral (all equal), Isosceles (two equal), Scalene (none equal). By Angles: Acute, Right, and Obtuse angled triangles.",
              },
              {
                type: "card",
                color: "#FF7A7A",
                icon: "🧪",
                title: "Core Theorems",
                body: "Angle Sum: Sum of interior angles is 180°. Triangle Inequality: Sum of any two sides > third side. Exterior Angle = Sum of opposite interior angles.",
              },
            ],
          },
          {
            id: "congruence-similarity",
            heading: "Congruence vs Similarity",
            subsections: [
              {
                type: "card",
                color: "#58B6C5",
                icon: "👯",
                title: "Congruence (Identical)",
                body: "Triangles are identical in shape and size. Rules: SSS, SAS, ASA, RHS.",
              },
              {
                type: "card",
                color: "#EBC33C",
                icon: "🔎",
                title: "Similarity (Ratio)",
                body: "Triangles have same shape but different size. Corresponding angles are equal and sides are proportional. Rules: AA, SSS, SAS.",
              },
            ],
          },
          {
            id: "quadrilaterals",
            heading: "Quadrilaterals & Circles",
            icon: "⭕",
            color: "#407BFF",
            bullets: [
              "Quadrilateral types: Square, Rectangle, Parallelogram, Rhombus, and Trapezium. Interior sum is 360°.",
              "Circle Terms: Radius, Diameter (2r), Chord, Arc, Sector, and Segment.",
              "Circle Property: Angle subtended by a diameter at any point on the circumference is always 90°.",
              "Equal chords of a circle subtend equal angles at the centre.",
            ],
          },
          {
            id: "polygons-property",
            heading: "Polygons & Angle Sums",
            icon: "🛑",
            color: "#FF820F",
            body: "A polygon is a closed figure with 3 or more sides. Regular polygons have equal sides and angles.",
            bullets: [
              "Sum of interior angles = (n − 2) × 180°.",
              "Each interior angle (Regular) = [(n − 2) × 180°] / n.",
              "Sum of exterior angles = Always 360° for any polygon.",
            ],
          },
          {
            id: "strategy-errors",
            heading: "Exam Strategy & Errors",
            icon: "⚠️",
            color: "#940B92",
            tips: [
              "Draw neat, marked figures as they often reveal hidden relationships.",
              "Don't confuse Congruence (Identical) with Similarity (Proportional).",
              "Verify the Triangle Inequality before assuming a triangle can exist.",
              "Always check if the units of given measures (cm, m, degrees) are consistent.",
              "Follow the hierarchy: Lines -> Angles -> Triangles -> Higher Polygons.",
            ],
          },
        ],
        keyTakeaways: [
          "A well-drawn figure is half the solution to any geometry problem.",
          "Geometry is logic supported by visual diagrams.",
          "Angle sum properties (180° for triangles, 360° for quads) are the most used rules.",
          "Congruent figures are always similar, but similar figures are not always congruent.",
        ],
      },
      Average: {
        title: "Average",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Average. Along with this student will be empowered to solve different categories of questions involving conversion, increase / decrease, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "An average of a set of data is the expression of the set’s center value. It is defined mathematically as the ratio of the sum of all the data to the number of units in the list. The average value of a group of integers is the computed “central” value in mathematics. The middle or ‘center’ point is referred to as the ‘average’.\n\nAverage formula = Sum of Values / Number of values",
        sections: [
          {
            id: "formulas-list",
            heading: "Average Formulas List",
            icon: "📖",
            color: "#407BFF",
            table: {
              headers: ["Property / Case", "Average Formula"],
              rows: [
                ["Average of first n natural numbers", "(n + 1) / 2"],
                ["Average of first n even numbers", "(Last even no. + 2) / 2"],
                ["Average of first n odd numbers", "(Last odd no. + 1) / 2"],
                [
                  "Average of squares of first n natural numbers",
                  "(n + 1)(2n + 1) / 6",
                ],
                [
                  "Average of cubes of first n natural numbers",
                  "n(n + 1)² / 4",
                ],
                [
                  "Average of squares of first n consecutive even numbers",
                  "2(n + 1)(2n + 1) / 3",
                ],
                [
                  "Average of squares of consecutive odd numbers till n",
                  "n(n + 2) / 3",
                ],
                [
                  "Difference between smallest & largest among n consecutive numbers with average m",
                  "2(m - 1)",
                ],
              ],
            },
          },
          {
            id: "special-cases",
            heading: "Special Addition & Removal Cases",
            icon: "⚖️",
            color: "#FF9A4F",
            listItems: [
              {
                title: "Quantity Removed",
                description:
                  "The average of n quantities is x. When a quantity is removed, the average becomes y. Value of removed quantity = n(x – y) + y.",
              },
              {
                title: "Quantity Added",
                description:
                  "The average of n quantities is x. When a quantity is added, the average becomes y. Value of added quantity = n(y – x) + y.",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Average represents the central value of a data set and is calculated as total sum divided by the number of observations.",
          "The sum of observations can always be found by multiplying the average with the number of values.",
          "When a value is added or removed, the change in average helps determine the missing value quickly.",
          "Shortcut average formulas save significant time in solving aptitude examination problems.",
        ],
      },
      Percentage: {
        title: "Percentage",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Percentage. Along with this student will be empowered to solve different categories of questions involving conversion, increase / decrease, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "The percentage is the percent which means parts per hundred. It is a value that represents the proportion of one number to another number. One percent represents 1/100 fraction.",
        sections: [
          {
            id: "basics",
            heading: "Basics of Percentage",
            icon: "📊",
            color: "#407BFF",
            listItems: [
              {
                title: "Percent Sign",
                description:
                  "The percent sign is the symbol: %. It is written to the right side of the number (e.g., 50%).",
              },
              {
                title: "Conversion to Fraction",
                description:
                  "To convert a percentage into a fraction, divide it by 100.",
              },
            ],
            table: {
              headers: ["Conversion Type", "Example"],
              rows: [
                ["Percentage to Decimal", "20% = 20/100 = 0.2"],
                ["Decimal to Percentage", "0.25 = (0.25 × 100)% = 25%"],
                [
                  "Decimal to Percentage (Large)",
                  "1.50 = (1.50 × 100)% = 150%",
                ],
              ],
            },
          },
          {
            id: "increase-decrease",
            heading: "Type 1: Percentage Increase/Decrease",
            icon: "📈",
            color: "#FF9A4F",
            listItems: [
              {
                title: "Price Increase",
                description:
                  "If price increases by R%, reduction in consumption to keep expenditure constant: [R / (100 + R)] × 100%.",
              },
              {
                title: "Price Decrease",
                description:
                  "If price decreases by R%, increase in consumption to keep expenditure constant: [R / (100 - R)] × 100%.",
              },
            ],
          },
          {
            id: "population-results",
            heading: "Type 2: Population Results",
            icon: "🏘️",
            color: "#940B92",
            body: "Let population be P and it increases at R% per annum. Population after n years = P(1 + R/100)ⁿ.",
          },
          {
            id: "depreciation-results",
            heading: "Type 3: Depreciation Results",
            icon: "📉",
            color: "#FF820F",
            body: "Let present value of a machine be P and it depreciates at R% per annum. Value after n years = P(1 - R/100)ⁿ.",
          },
          {
            id: "comparison-formulas",
            heading: "Comparison Formulas",
            icon: "⚖️",
            color: "#58B6C5",
            listItems: [
              {
                title: "A more than B",
                description:
                  "If A is R% more than B, then B is less than A by: [R / (100 + R)] × 100%.",
              },
              {
                title: "A less than B",
                description:
                  "If A is R% less than B, then B is more than A by: [R / (100 - R)] × 100%.",
              },
            ],
          },
          {
            id: "successive-changes",
            heading: "Successive Changes",
            icon: "🔄",
            color: "#FF7A7A",
            body: "For two successive changes of x% and y%, the Net Change = [x + y + (xy / 100)]%.",
          },
        ],
        keyTakeaways: [
          "Percentage means parts per hundred and is represented using the % sign.",
          "Percentages can be easily converted into fractions and decimals.",
          "Increase or decrease problems are solved using base value comparison.",
          "Successive percentage changes are not simply additive and require a formula.",
        ],
      },
      "Profit_&_Loss": {
        title: "Profit and Loss",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Profit and Loss. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Profit and Loss are terms used to identify whether a transaction is profitable or not. These concepts are widely used in business and daily life for calculating gain or loss on a product's price.",
        sections: [
          {
            id: "definitions",
            heading: "Basic Definitions",
            icon: "📋",
            color: "#407BFF",
            listItems: [
              {
                title: "Cost Price (CP)",
                description:
                  "The price at which an article is purchased is called its cost price (C.P.).",
              },
              {
                title: "Selling Price (SP)",
                description:
                  "The price at which the article is sold is called its selling price (S.P.).",
              },
              {
                title: "Profit (P)",
                description:
                  "The amount gained by selling a product with more than its cost price. (SP > CP)",
              },
              {
                title: "Loss (L)",
                description:
                  "The amount the seller incurs after selling the product less than its cost price. (CP > SP)",
              },
            ],
          },
          {
            id: "basic-formulas",
            heading: "Core Formulas",
            icon: "🧮",
            color: "#FF9A4F",
            table: {
              headers: ["Situation", "Formula"],
              rows: [
                ["Profit (Gain)", "S.P. – C.P."],
                ["Loss", "C.P. – S.P."],
                ["Gain %", "(Gain × 100) / C.P."],
                ["Loss %", "(Loss × 100) / C.P."],
              ],
            },
          },
          {
            id: "advanced-formulas",
            heading: "Calculating CP & SP",
            icon: "⚙️",
            color: "#940B92",
            listItems: [
              {
                title: "Calculate CP (Gain% given)",
                description: "C.P. = [100 / (100 + Gain%)] × S.P.",
              },
              {
                title: "Calculate CP (Loss% given)",
                description: "C.P. = [100 / (100 - Loss%)] × S.P.",
              },
              {
                title: "Calculate SP (Gain% given)",
                description: "S.P. = [(100 + Gain%) / 100] × C.P.",
              },
              {
                title: "Calculate SP (Loss% given)",
                description: "S.P. = [(100 - Loss%) / 100] × C.P.",
              },
            ],
          },
          {
            id: "discounts",
            heading: "Successive Discounts & MP",
            icon: "🏷️",
            color: "#FF820F",
            body: "The relationship between Discount, Selling Price (SP), and Marked Price (MP).",
            listItems: [
              {
                title: "Single Equivalent Discount",
                description:
                  "For two successive discounts m% and n%, single discount = [(m + n) - (mn / 100)]%.",
              },
              {
                title: "Discount Amount",
                description: "Discount = Marked Price - Selling Price.",
              },
              {
                title: "Discount %",
                description: "Discount% = (Discount / Marked Price) × 100.",
              },
              {
                title: "SP and MP Relations",
                description: "S.P. = M.P. – Discount; M.P. = S.P. + Discount.",
              },
            ],
          },
          {
            id: "mp-cp-relation",
            heading: "MP, CP and Profit/Loss",
            icon: "⚖️",
            color: "#58B6C5",
            listItems: [
              {
                title: "CP from MP (If Profit)",
                description: "C.P. = M.P. - (Discount + Profit)",
              },
              {
                title: "CP from MP (If Loss)",
                description: "C.P. = M.P. - (Discount - Loss)",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Profit occurs when Selling Price is greater than Cost Price.",
          "Loss occurs when Cost Price is greater than Selling Price.",
          "Profit or Loss percentage is always calculated on Cost Price.",
          "Discount problems involve the relationship between Marked Price and Selling Price.",
        ],
      },
      "S.I_&_C.I": {
        title: "Simple Interest and Compound Interest",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic – Simple Interest and Compound Interest. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Interest is the price paid for the use of borrowed money. Simple interest is calculated only on the principal amount, while Compound interest is calculated on the principal plus the accumulated interest from previous periods.",
        sections: [
          {
            id: "simple-interest",
            heading: "Simple Interest (S.I)",
            icon: "📈",
            color: "#407BFF",
            listItems: [
              {
                title: "Basic Formula",
                description:
                  "S.I = (P × R × T) / 100 (where P = Principal, R = Rate%, T = Time years)",
              },
              {
                title: "Finding Rate (Short Trick)",
                description:
                  "If a sum becomes n times itself in T years at S.I: R = [100(n - 1)] / T %",
              },
              {
                title: "Finding Time (Short Trick)",
                description:
                  "If a sum becomes n times itself at R% per annum S.I: T = [100(n - 1)] / R years",
              },
            ],
          },
          {
            id: "compound-interest-basics",
            heading: "Compound Interest (C.I) Basics",
            icon: "🔄",
            color: "#FF9A4F",
            body: "Note: Simple interest and compound interest for 1 year at a given rate are always equal.",
            listItems: [
              {
                title: "Amount Formula",
                description: "A = P(1 + R/100)ᵗ",
              },
              {
                title: "C.I Formula",
                description: "C.I = A – P",
              },
            ],
          },
          {
            id: "ci-compounding-periods",
            heading: "C.I Compounding Periods",
            icon: "⏱️",
            color: "#940B92",
            subsections: [
              {
                title: "Compounded Half-Yearly",
                body: "Amount (A) = P[1 + R/(100 × 2)]²ᵗ",
                color: "#940B92",
              },
              {
                title: "Compounded Quarterly",
                body: "Amount (A) = P[1 + R/(100 × 4)]⁴ᵗ",
                color: "#FF820F",
              },
              {
                title: "Compounded n times a year",
                body: "Amount (A) = P[1 + R/(100 × n)]ⁿˣᵗ",
                color: "#58B6C5",
              },
            ],
          },
          {
            id: "ci-advanced",
            heading: "Advanced C.I Formulas",
            icon: "🚀",
            color: "#FF7A7A",
            listItems: [
              {
                title: "Different Rates per Year",
                description:
                  "If rates are R1, R2, R3... for successive years: Amount = P(1 + R1/100)(1 + R2/100)(1 + R3/100)...",
              },
              {
                title: "Growth Rule",
                description:
                  "If a certain sum becomes n times in t years at C.I, then the same sum becomes nᵐ times in (m × t) years.",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Simple Interest is calculated only on the principal amount.",
          "Compound Interest is calculated on principal plus accumulated interest.",
          "SI and CI are equal for the first year at the same rate.",
          "Compounding frequency significantly affects the final amount.",
        ],
      },
      "Time,_Speed_&_Distance": {
        title: "Speed, Time and Distance",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic-Speed, Time and Distance. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "The distance formula relates to distance, speed (or velocity), and time. It’s commonly used to calculate the distance traveled by an object when you know its speed and the time it has been in motion. Distance (d) = Speed (v) × Time (t). This formula assumes constant speed and no changes in direction during the motion.",
        sections: [
          {
            id: "fundamental-formulas",
            heading: "Fundamental Formulas",
            icon: "📐",
            color: "#407BFF",
            table: {
              headers: ["To Find", "Formula"],
              rows: [
                ["Distance", "Speed × Time"],
                ["Speed", "Distance / Time"],
                ["Time", "Distance / Speed"],
                ["Average Speed", "Total Distance / Total Time"],
              ],
            },
          },
          {
            id: "conversions",
            heading: "Unit Conversions",
            icon: "🔄",
            color: "#FF9A4F",
            listItems: [
              {
                title: "Distance",
                description:
                  "1 km = 1000 m = 0.6214 mile; 1 mile = 1.609 km = 1760 yards = 5280 feet; 1 yard = 3 feet.",
              },
              {
                title: "Time",
                description: "1 hour = 60 minutes = 3600 seconds.",
              },
              {
                title: "Speed",
                description:
                  "1 km/hr = 5/18 m/sec; 1 m/sec = 18/5 km/hr; 1 km/hr = 5/8 mph.",
              },
              {
                title: "Advanced Speed",
                description: "1 mph = 22/45 yards/sec = 22/15 ft/sec.",
              },
            ],
          },
          {
            id: "train-problems",
            heading: "Train & Relative Speed Problems",
            icon: "🚆",
            color: "#940B92",
            listItems: [
              {
                title: "Opposite Direction",
                description:
                  "Relative speed = S1 + S2. Time taken to cross = (L1 + L2) / (S1 + S2).",
              },
              {
                title: "Same Direction",
                description:
                  "Relative speed = S1 - S2. Time taken to cross = (L1 + L2) / (S1 - S2).",
              },
              {
                title: "Passing a Pole/Man",
                description: "Speed = Length of Train / Time.",
              },
              {
                title: "Two Trains Crossing",
                description: "S1 + S2 = (L1 + L2) / t.",
              },
            ],
          },
          {
            id: "aptitude-tricks",
            heading: "Aptitude Tricks & Tips",
            icon: "💡",
            color: "#FF820F",
            tips: [
              "Standard Units: Convert all units to a common standard (e.g., all to km/h and hours).",
              "Ratios: Use inverse variation between speed and time if distance is constant.",
              "Visualization: Draw diagrams or lines to represent distance and arrows for speed.",
              "Visualization Tool: Fractions can often simplify calculations (plug and play).",
              "Approximations: Use estimates for quick checks (e.g., 100 km/h for 2h ≈ 200 km).",
              "Elimination: Use answer choices to eliminate unreasonable options.",
            ],
          },
        ],
        keyTakeaways: [
          "Distance, Speed, and Time are related by the formula D = S × T.",
          "Average speed depends on total distance and total time, not simple mean.",
          "Relative speed is used in train and moving object problems.",
          "Unit conversion is essential before applying formulas.",
        ],
      },
      "Time_&_Work": {
        title: "Time and Work",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic- Time & Work. Along with this students will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Time and Work problems deal with the efficiency of individuals or groups to complete a task. The fundamental principle is that the amount of work done is the product of time spent and efficiency. When work is constant, time is inversely proportional to efficiency.",
        sections: [
          {
            id: "basic-efficiency",
            heading: "Basic Efficiency Principles",
            icon: "⚙️",
            color: "#407BFF",
            bullets: [
              "Inverse Relation: Time ∝ 1/Efficiency (When work is same).",
              "Per Day Work: If A can do a piece of work in n days, then A's per day working efficiency = 1/n.",
              "Efficiency Ratios: If efficiency of A & B is x : y, then the time taken by them to finish the work is y : x.",
              "Example: If A is 3 times faster than B (Ratio 3:1), the ratio of time taken by A and B is 1:3.",
            ],
          },
          {
            id: "working-together",
            heading: "Working Together / Separately",
            icon: "👥",
            color: "#FF9A4F",
            listItems: [
              {
                title: "Two Persons (A & B)",
                description:
                  "If A takes x days and B takes y days, both together take: (xy) / (x + y) days.",
              },
              {
                title: "Three Persons (A, B & C)",
                description:
                  "If A, B, C take x, y, z days respectively, all together take: (xyz) / (xy + yz + zx) days.",
              },
              {
                title: "Finding One Person (B)",
                description:
                  "If A+B take x days and A alone takes y days, B alone takes: (xy) / (y - x) days.",
              },
            ],
          },
          {
            id: "cyclic-working",
            heading: "Groups Working in Pairs",
            icon: "🔄",
            color: "#940B92",
            body: "If A + B can finish a work in x days, B + C in y days, and C + A in z days, then A + B + C working together will finish the job in: (2xyz) / (xy + yz + zx) days.",
          },
          {
            id: "efficiency-tricks",
            heading: "K-Times Efficiency Tricks",
            icon: "🚀",
            color: "#FF820F",
            listItems: [
              {
                title: "A & B working together",
                description:
                  "If A finishes work in x days and B is k times efficient than A, together they take: x / (1 + k) days.",
              },
              {
                title: "A & B (Together time given)",
                description:
                  "If A+B take x days and B is k times efficient than A, then:\n- A alone takes (k + 1)x days\n- B alone takes [(k + 1) / k] * x days.",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Work is directly proportional to efficiency and inversely proportional to time.",
          "If a person completes work in n days, their one-day work is 1/n.",
          "When people work together, their efficiencies add up.",
          "Efficiency ratios help solve complex time and work problems quickly.",
        ],
      },
      Mensuration: {
        title: "Mensuration",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Mensuration. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Mensuration is the branch of mathematics that studies the measurement of geometric figures and their parameters like length, area, and volume. It involves both 2D shapes (Perimeter, Area) and 3D shapes (Surface Area, Volume).",
        sections: [
          {
            id: "2d-shapes",
            heading: "Mensuration Formulas: 2D Shapes",
            icon: "📐",
            color: "#407BFF",
            table: {
              headers: ["Shape", "Area (Square units)", "Perimeter (units)"],
              rows: [
                ["Square", "a²", "4a"],
                ["Rectangle", "l × b", "2(l + b)"],
                ["Circle", "πr²", "2πr"],
                [
                  "Scalene Triangle",
                  "√[s(s - a)(s - b)(s - c)] where s = (a+b+c)/2",
                  "a + b + c",
                ],
                ["Isosceles Triangle", "1/2 × b × h", "2a + b"],
                ["Equilateral Triangle", "(√3/4) a²", "3a"],
                ["Right Angle Triangle", "1/2 × b × h", "b + h + hypotenuse"],
                ["Rhombus", "1/2 × d1 × d2", "4 × side"],
                ["Parallelogram", "b × h", "2 (l + b)"],
                ["Trapezium", "1/2 h(a + b)", "Sum of all sides"],
              ],
            },
          },
          {
            id: "3d-shapes",
            heading: "Mensuration Formulas: 3D Shapes",
            icon: "📦",
            color: "#FF9A4F",
            table: {
              headers: ["Shape", "Volume", "CSA / LSA", "TSA"],
              rows: [
                ["Cube", "a³", "4a²", "6a²"],
                ["Cuboid", "l × b × h", "2h(l + b)", "2(lb + bh + lh)"],
                ["Sphere", "(4/3) πr³", "4πr²", "4πr²"],
                ["Hemisphere", "(2/3) πr³", "2πr²", "3πr²"],
                ["Cylinder", "πr²h", "2πrh", "2πrh + 2πr²"],
                ["Cone", "(1/3) πr²h", "πrl", "πr(r + l)"],
              ],
            },
          },
          {
            id: "cuboid-cube-details",
            heading: "Detailed Properties: Cuboid & Cube",
            icon: "🧊",
            color: "#940B92",
            listItems: [
              {
                title: "Cuboid Properties",
                description:
                  "Diagonal = √(l² + b² + h²). Total Surface Area is also = (sum of all sides)² – (Diagonal)². Volume can also be expressed as V = √(A1 × A2 × A3) where A are areas of different faces.",
              },
              {
                title: "Cube Properties",
                description:
                  "Diagonal = √3 a; Face Diagonal = √2 a. In-radius = a/2; Circumradius = (√3/2) a.",
              },
            ],
          },
          {
            id: "special-cases-mensuration",
            heading: "Special Geometric Cases",
            icon: "💡",
            color: "#FF820F",
            listItems: [
              {
                title: "Right Angled Triangle",
                description:
                  "Side of the maximum size square inscribed in a right angle Δ: a = (P × B) / (P + B) (where P = Perpendicular, B = Base).",
              },
              {
                title: "Right Circular Cone",
                description:
                  "Slant height (l) = √(r² + h²). Curved Surface Area = πrl. Total Surface Area = πr(l + r).",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Mensuration deals with measurement of area, perimeter, and volume.",
          "2D shapes involve area and perimeter, while 3D shapes involve surface area and volume.",
          "Correct identification of shape is crucial before applying formulas.",
          "Units must be consistent while solving mensuration problems.",
        ],
      },
      "Permutation_&_Combination": {
        title: "Permutation and Combination",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Permutation and Combination. Along with this student will be empowered to solve different categories of questions involving selection and arrangement, and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Permutations and Combinations are fundamental principles of counting used to determine the number of ways objects can be arranged or selected. Permutation is for arrangements (order matters), while Combination is for selections (order doesn't matter).",
        sections: [
          {
            id: "p-c-basics",
            heading: "Definitions & Basic Formulas",
            icon: "📊",
            color: "#407BFF",
            table: {
              headers: ["Term", "Description", "Formula"],
              rows: [
                [
                  "Permutation (nPr)",
                  "Different arrangements of n things taking r at a time (Order MATTERS)",
                  "n! / (n - r)!",
                ],
                [
                  "Combination (nCr)",
                  "Different groups or selections of n things taking r at a time (Order NOT important)",
                  "n! / [r! × (n - r)!]",
                ],
              ],
            },
          },
          {
            id: "factorial-basics",
            heading: "Factorial Table (0 to 10)",
            icon: "🔢",
            color: "#FF9A4F",
            body: "n! = n × (n-1) × (n-2) ... × 1. Note: 0! = 1.",
            table: {
              headers: ["n", "n!"],
              rows: [
                ["0", "1"],
                ["1", "1"],
                ["2", "2"],
                ["3", "6"],
                ["4", "24"],
                ["5", "120"],
                ["6", "720"],
                ["7", "5,040"],
                ["8", "40,320"],
                ["9", "362,880"],
                ["10", "3,628,800"],
              ],
            },
          },
          {
            id: "p-c-truths",
            heading: "Points to Remember",
            icon: "💡",
            color: "#940B92",
            bullets: [
              "Factorial of any negative quantity is not valid.",
              "Fundamental Principle of Counting: Either event A OR B can be done in m + n ways; Both A AND B can be done in m × n ways.",
              "nPn = n!",
              "nCn = 1; nC0 = 1",
              "nCr = nC(n-r)",
              "nC0 + nC1 + nC2 + ... + nCn = 2ⁿ",
              "Permutations of n things where p1 are of one kind, p2 of another, etc. = n! / (p1! × p2! × ... × pr!)",
            ],
          },
          {
            id: "p-c-properties",
            heading: "Important Properties",
            icon: "🧪",
            color: "#FF820F",
            listItems: [
              {
                title: "Relation",
                description: "nPr = r! × nCr",
              },
              {
                title: "Pascal's Identity",
                description: "nCr + nCr-1 = (n+1)Cr",
              },
              {
                title: "Greatest nCr",
                description:
                  "If n is even: r = n/2. If n is odd: r = (n+1)/2 or (n-1)/2.",
              },
              {
                title: "Additional Relation",
                description: "r × nCr = n × (n-1)Cr-1",
              },
              {
                title: "Property of Equality",
                description: "If nCx = nCy, then x = y or x + y = n.",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Permutation deals with arrangements where order matters.",
          "Combination deals with selections where order does not matter.",
          "Factorials form the base of permutation and combination formulas.",
          "nCr and nPr are related by the formula nPr = r! × nCr.",
        ],
      },
      Probability: {
        title: "Probability",
        subject: "General Test – Mathematics",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic-Probability. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Probability defines the likelihood of occurrence of an event. It can be defined as the ratio of the number of favourable outcomes to the total number of outcomes. The value of probability always lies between 0 and 1 inclusive.",
        sections: [
          {
            id: "probability-basics",
            heading: "Basic Concepts & Terminology",
            icon: "⚖️",
            color: "#407BFF",
            listItems: [
              {
                title: "Probability Formula",
                description:
                  "P(E) = n(E) / n(S) (Favourable Outcomes / Total Outcomes).",
              },
              {
                title: "Experiment & Trial",
                description:
                  "An operation conducted to produce an outcome (Experiment) or a single performance of a random experiment (Trial).",
              },
              {
                title: "Sample Space (S)",
                description:
                  "The set of all possible outcomes. E.g., for a coin, S = {Head, Tail}.",
              },
              {
                title: "Events",
                description:
                  "A subset of the sample space. Mutually exclusive events cannot happen together; Equally likely events have same chances of occurring.",
              },
            ],
          },
          {
            id: "probability-rules",
            heading: "Key Probability Rules",
            icon: "📜",
            color: "#FF9A4F",
            bullets: [
              "Range: 0 ≤ P(E) ≤ 1. (0 = Impossible event, 1 = Certain event).",
              "Sum Rule: P(Event) + P(Not Event) = 1 [ P(E) + P(E') = 1 ].",
              "Addition Rule: P(A ∪ B) = P(A) + P(B) - P(A ∩ B).",
              "Conditional Probability: P(B|A) = P(A ∩ B) / P(A).",
              "Multiplication (Independent): P(A ∩ B) = P(A) × P(B).",
              "Total Probability: Sum of probabilities of all possible events in an experiment is always 1.",
            ],
          },
          {
            id: "coin-dice-experiments",
            heading: "Common Experiments: Coins & Dice",
            icon: "🎲",
            color: "#940B92",
            subsections: [
              {
                title: "Coin Tossing (2ⁿ outcomes)",
                body: "1 Coin: S={H,T}, n=2. | 2 Coins: S={HH, HT, TH, TT}, n=4. | 3 Coins: n=8.",
                color: "#940B92",
              },
              {
                title: "Dice Rolling (6ⁿ outcomes)",
                body: "1 Die: n=6. P(Even) = P(Odd) = P(Prime) = 1/2. | 2 Dice: n=36. P(Doublet) = 6/36 = 1/6.",
                color: "#FF820F",
              },
            ],
          },
          {
            id: "playing-cards",
            heading: "Playing Cards (52 Deck)",
            icon: "🃏",
            color: "#15E4B9",
            table: {
              headers: ["Category", "Count", "Probability"],
              rows: [
                ["Total Cards", "52", "1"],
                [
                  "Suits (Spades, Clubs, Hearts, Diamonds)",
                  "4 (13 each)",
                  "1/4 each",
                ],
                ["Colors (Red / Black)", "26 each", "1/2 each"],
                ["Face Cards (J, Q, K)", "12 total (3 per suit)", "3/13"],
                ["Aces", "4 total", "1/13"],
              ],
            },
          },
          {
            id: "probability-types",
            heading: "Types of Probability",
            icon: "🧪",
            color: "#FF7A7A",
            listItems: [
              {
                title: "Classical / Theoretical",
                description: "Based on equally likely outcomes (a priori).",
              },
              {
                title: "Empirical / Experimental",
                description: "Based on actual trials and historical data.",
              },
              {
                title: "Subjective",
                description: "Based on individual belief or opinion.",
              },
              {
                title: "Axiomatic",
                description:
                  "Follows set rules (axioms) like Kolmogorov's axioms.",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Probability measures the likelihood of an event occurring.",
          "Probability always lies between 0 and 1.",
          "Probability is calculated as favourable outcomes divided by total outcomes.",
          "The sum of probabilities of all possible outcomes is always 1.",
        ],
      },
    },
    Logical_Reasoning: {
      Alphabet_series: {
        title: "Alphabet Series",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic-Alphabet Series. Along with this students will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "In Alphabet Series section, a string of alphabets, either in a single file or in combination form a sequence. This sequence follows a definite rule. Candidates are expected to detect this rule to find missing terms, wrong terms, or terms that don't belong to the sequence.",
        sections: [
          {
            id: "alphabet-mapping",
            heading: "Alphabet-Number Mapping Table",
            icon: "🔤",
            color: "#407BFF",
            table: {
              headers: ["Letter", "Pos", "Letter", "Pos"],
              rows: [
                ["A", "1", "N", "14"],
                ["B", "2", "O", "15"],
                ["C", "3", "P", "16"],
                ["D", "4", "Q", "17"],
                ["E", "5", "R", "18"],
                ["F", "6", "S", "19"],
                ["G", "7", "T", "20"],
                ["H", "8", "U", "21"],
                ["I", "9", "V", "22"],
                ["J", "10", "W", "23"],
                ["K", "11", "X", "24"],
                ["L", "12", "Y", "25"],
                ["M", "13", "Z", "26"],
              ],
            },
          },
          {
            id: "missing-series",
            heading: "Missing Alphabet Series",
            icon: "❓",
            color: "#FF9A4F",
            hintItems: [
              {
                num: "EX",
                text: "Find the missing letter in the series: O, R, U, __",
                subbullets: [
                  "O = 15, R = 18, U = 21 (Logic: +3 difference)",
                  "Next number: 21 + 3 = 24",
                  "24 corresponds to 'X'",
                  "Correct Answer: X",
                ],
              },
            ],
          },
          {
            id: "circular-series",
            heading: "Circular Arrangement Series",
            icon: "🔄",
            color: "#940B92",
            body: "In circular logic, the numbering doesn't stop at 26. Think of it as a straight line where A = 1, then A = 27 (26+1), A = 53 (52+1), etc.",
            hintItems: [
              {
                num: "EX",
                text: "Guess the next number: V, A, H, __",
                subbullets: [
                  "V = 22, A = 27 (Difference: 5)",
                  "A = 27, H = 34 (Difference: 7)",
                  "Logic: Increasing difference (+5, +7... next should be +9)",
                  "H (8) + 9 = 17, which corresponds to 'Q'",
                  "Correct Answer: Q (or P depending on specific gap interpretation - as per logic: Q)",
                ],
              },
            ],
          },
          {
            id: "odd-series",
            heading: "Odd Series & Wrong Terms",
            icon: "🚫",
            color: "#FF820F",
            body: "Identify the sequence that doesn't follow its own internal rule.",
            hintItems: [
              {
                num: "EX",
                text: "Find the wrong sequence among options.",
                subbullets: [
                  "Seq 1: Q(17), T(20), X(24), C(29) -> +3, +4, +5 (Correct)",
                  "Seq 2: F(6), P(16), Z(26), J(36) -> +10, +10, +10 (Correct)",
                  "Seq 3: W(23), U(21), R(18), N(14) -> -2, -3, -4 (Correct, but if rule differs it might be 'odd')",
                  "Tip: Map everything to numbers quickly to spot breaks in patterns.",
                ],
              },
            ],
          },
        ],
        keyTakeaways: [
          "Mapping alphabets to numerical positions (A=1, Z=26) is the single most effective strategy.",
          "Check for arithmetic progression (constant difference) or geometric progression in gaps.",
          "Use circular logic (Z followed by A) for wrap-around sequences.",
          "Identifying 'mandatory markers' like vowels or specific letter groupings helps in complex series.",
        ],
      },
      Venn_Diagram: {
        title: "Venn Diagram",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Venn Diagram. Students will be empowered to interpret relationships between groups using circles, solve overlaps, and calculate missing data using set theory formulas commonly asked in aptitude tests like NIFT/NID/UCEED.",
        introduction:
          "A Venn Diagram is a visual representation of sets using closed figures (usually circles) to show the relationship between different groups of data. It helps in understanding overlapping groups, common elements, and exclusive elements.",
        sections: [
          {
            id: "venn-terms",
            heading: "Basic Set Terminology",
            subsections: [
              {
                type: "card",
                color: "#407BFF",
                icon: "📁",
                title: "Sets & Universal Set",
                body: "Set: A collection of well-defined objects. Universal Set (U): Contains all elements under consideration for the problem.",
              },
              {
                type: "card",
                color: "#FF820F",
                icon: "📦",
                title: "Subsets & Null Sets",
                body: "Subset (⊆): A set entirely contained within another. Null Set (∅): An empty set with no elements.",
              },
            ],
          },
          {
            id: "diagram-types",
            heading: "Types & Regions",
            icon: "🧬",
            color: "#15E4B9",
            body: "Venn diagrams typically involve two or three overlapping circles.",
            bullets: [
              "Two-Set Diagram: Relationship between two overlapping groups (A and B).",
              "Three-Set Diagram: Complex overlaps between three categories (A, B, and C).",
              "A ∩ B (Intersection): The region where groups overlap; common to both sets.",
              "Only A / Only B: The moon-shaped regions representing elements exclusive to one set.",
              "Outside Region: Elements belonging to the universal set but none of the categories.",
            ],
          },
          {
            id: "symbols-table",
            heading: "Symbols & Meanings",
            icon: "🧮",
            color: "#940B92",
            table: {
              headers: ["Symbol", "Meaning", "Context"],
              rows: [
                ["∪", "Union", "A OR B (Either or both)"],
                ["∩", "Intersection", "A AND B (Only both)"],
                ["A′", "Complement", "Not in A"],
                ["∅", "Empty set", "No elements"],
                ["⊆", "Subset", "Contained within"],
              ],
            },
          },
          {
            id: "solving-steps",
            heading: "Step-by-Step Approach",
            icon: "🪜",
            color: "#FF7A7A",
            body: "Follow this hierarchy to ensure accuracy:",
            listItems: [
              {
                title: "Step 1: Count Sets",
                description:
                  "Identify if the problem involves two or three categories.",
              },
              {
                title: "Step 2: Draw First",
                description:
                  "Always draw the overlapping circles before filling in values.",
              },
              {
                title: "Step 3: Fill Intersections",
                description:
                  "CRITICAL: Always fill the innermost common region (intersection) first.",
              },
              {
                title: "Step 4: Fill Exclusive",
                description:
                  "Subtract intersections from totals to find 'Only' regions.",
              },
              {
                title: "Step 5: Verify Total",
                description:
                  "Ensure the sum of all regions matches the universal total.",
              },
            ],
          },
          {
            id: "formulas",
            heading: "Set Theory Formulas",
            icon: "💡",
            color: "#EBC33C",
            subsections: [
              {
                type: "card",
                color: "#15E4B9",
                icon: "✌️",
                title: "Two Sets",
                body: "n(A ∪ B) = n(A) + n(B) − n(A ∩ B)",
              },
              {
                type: "card",
                color: "#407BFF",
                icon: "🖐️",
                title: "Three Sets",
                body: "n(A∪B∪C) = n(A)+n(B)+n(C) − n(A∩B)−n(B∩C)−n(C∩A) + n(A∩B∩C)",
              },
            ],
          },
          {
            id: "keywords",
            heading: "Crucial Keywords",
            icon: "🔍",
            color: "#FF820F",
            table: {
              headers: ["Keyword", "Venn Meaning"],
              rows: [
                ["Only", "Exclusive to that circle"],
                ["At least", "Minimum (includes the intersection)"],
                ["At most", "Maximum limit"],
                ["Either / or", "One or both (Union)"],
                ["Neither", "Outside region (Complement of Union)"],
              ],
            },
          },
          {
            id: "errors",
            heading: "Common Errors to Avoid",
            icon: "⚠️",
            color: "#940B92",
            tips: [
              "NEVER fill the individual set totals before filling the intersection.",
              "Don't ignore the 'neither' region (people who don't belong to any group).",
              "Be careful with double-counting overlapping elements.",
              "Misreading 'Only A' as 'A' is the most common mistake.",
            ],
          },
        ],
        keyTakeaways: [
          "Always fill the innermost intersection first to avoid miscalculation.",
          "Every number given in the problem must belong to a specific region in the diagram.",
          "Venn diagrams turn complex language into simple visual logic.",
          "Use the formula n(A ∪ B) = n(A) + n(B) − n(A ∩ B) as a verification tool.",
        ],
      },
      Blood_Relation: {
        title: "Blood Relation",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Blood Relation. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Blood Relation refers to relationships acquired by birth. Sharing a common ancestry, these relations form a complex web or 'family tree'. Solving these questions requires analyzing chains of indicators to pinpoint exact relations between two family members.",
        sections: [
          {
            id: "relationship-terminology",
            heading: "Common Relationship Terminology",
            icon: "📜",
            color: "#407BFF",
            table: {
              headers: ["Relationship", "Term used"],
              rows: [
                ["Father's or Mother's son", "Brother"],
                ["Father's or Mother's daughter", "Sister"],
                ["Mother's brother", "Maternal Uncle"],
                ["Father's brother", "Uncle (Paternal)"],
                ["Father's/Mother's sister", "Aunt"],
                ["Son's wife", "Daughter-in-law"],
                ["Daughter's husband", "Son-in-law"],
                ["Sister's husband", "Brother-in-law"],
                ["Husband's/Wife's brother", "Brother-in-law"],
                ["Brother's wife", "Sister-in-law"],
                ["Husband's/Wife's sister", "Sister-in-law"],
                ["Husband's/Wife's father", "Father-in-law"],
                ["Husband's/Wife's mother", "Mother-in-law"],
                ["Brother's/Sister's son", "Nephew"],
                ["Brother's/Sister's daughter", "Niece"],
                ["Uncle's/Aunt's daughter/son", "Cousin"],
                ["Father's/Mother's father", "Grandfather"],
                ["Father's/Mother's mother", "Grandmother"],
                [
                  "Father/Mother of Grandfather/Grandmother",
                  "Great Grandparent",
                ],
              ],
            },
          },
          {
            id: "tips-tricks",
            heading: "Tips, Tricks & Concepts",
            icon: "💡",
            color: "#FF9A4F",
            tips: [
              "Master Terminologies: Be well-versed with both direct and extended family terms.",
              "Family Tree Diagrams: Always jot down information in a diagram format to avoid confusion.",
              "Relate to Self: In 'pointing' or 'introducing' questions, relate the scenario to your own family.",
              "Coded Questions: Try to decode coded relations from the end to reaching the answer faster.",
              "Gender Assumption: Pay close attention to names in statements; use them to cautiously infer or confirm gender roles.",
              "Practice: Regular practice of diversified question types is key to speed and accuracy.",
            ],
          },
        ],
        keyTakeaways: [
          "Family tree diagrams are the most reliable way to visualize multi-generational relationships.",
          "Pay careful attention to paternal (father's side) and maternal (mother's side) distinctions.",
          "Gender should never be assumed solely based on names; look for explicit pronouns (he/she) or roles (father/aunt).",
          "For 'Pointing-to-a-photograph' questions, try working backward from the 'my/me' possessive indicator.",
        ],
      },
      Classification: {
        title: "Classification",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic- Classification. Along with this students will be empowered to solve different categories of questions involving identification of the 'odd one out' among words, letters, and numbers.",
        introduction:
          "Classification means to assort the items of a given group on the basis of a certain common quality they possess and then spot the stranger or 'odd one out'. These questions are based on words, letters, and numerals.",
        sections: [
          {
            id: "classification-types",
            heading: "Main Types of Classification",
            icon: "🔍",
            color: "#407BFF",
            subsections: [
              {
                title: "Type 1: Choosing the Odd Word",
                body: "Choosing a word that doesn't share the same real-world features/context as others in the group.",
                examples: [
                  "Pear, Apple, Guava, Orange (Classification by citrus/non-citrus or shape)",
                  "Deck, Quay, Stern, Bow (Classification: Quay is a landing place, others are ship parts)",
                ],
                color: "#407BFF",
              },
              {
                title: "Type 2: Choosing the Odd Pair",
                body: "Identifying a pair where the relationship (e.g., origin, usage, synonym/antonym) differs from the rest.",
                examples: [
                  "Gold:Ornaments, Cloth:Garments vs Earthen pots:Clay (Classification: Material:Product vs Product:Material)",
                  "Broad:Wide, Tiny:Small vs Light:Heavy (Classification: Synonyms vs Antonyms)",
                ],
                color: "#FF9A4F",
              },
              {
                title: "Type 3: Odd Letter Group",
                body: "Determining the group of letters that breaks the established alphabetical or positional pattern.",
                examples: [
                  "H(8), Q(17), T(20), Z(26) (Classification: Q is the only odd position)",
                  "GE, MK, WU, QN (Analysis of gaps/positions between letters)",
                ],
                color: "#940B92",
              },
              {
                title: "Type 4: Odd Numbers",
                body: "Spotting the number or numeral pair with different mathematical characteristics (divisibility, squares, primes).",
                examples: [
                  "144, 169, 256, 288 (Classification: 288 is not a perfect square)",
                  "35, 49, 50, 63 (Classification: 50 is not divisible by 7)",
                ],
                color: "#FF820F",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Focus on identifying a common logic that connects all but one of the items.",
          "For numeric classification, prioritize checking prime numbers, squares, cubes, and divisibility.",
          "In verbal classification, consider synonym/antonym pairs, part-to-whole relationships, and functional similarities.",
          "Always mapping letter groups to their positional values (A=1...Z=26) reveals hidden patterns quickly.",
        ],
      },
      Clock_and_Calendar: {
        title: "Clock and Calendar",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic-Calendar and Clock. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Calendars and Clocks are fundamental systems for measuring and tracking time. While calendars help recognize dates, months, and weeks over longer spans, clocks indicate and maintain precise timing of hours, minutes, and seconds within a day. Reasoning in this section involves odd-day calculations for calendars and angular positions for clocks.",
        sections: [
          {
            id: "calendar-basics",
            heading: "Calendar: Key Concepts",
            icon: "📅",
            color: "#407BFF",
            listItems: [
              {
                title: "Ordinary vs Leap Year",
                description:
                  "Ordinary: 365 days (52 weeks + 1 odd day), not divisible by 4. Leap: 366 days (52 weeks + 2 odd days), divisible by 4.",
              },
              {
                title: "Odd Days",
                description:
                  "Extra days that don't form a complete week. Found by: Total Days % 7. E.g., 100 years have 5 odd days; 400 years have 0.",
              },
              {
                title: "Century Patterns",
                description:
                  "A block of 100 years. Centurial leap years must be divisible by 400 (e.g., 1600, 2000).",
              },
            ],
          },
          {
            id: "calendar-tricks",
            heading: "Calendar Tips & Tricks",
            icon: "💡",
            color: "#FF9A4F",
            tips: [
              "Repetition: Leap years repeat in 28 years; Ordinary years repeat in 6 or 11 years.",
              "First/Last Days: The first day of a century can be Mon, Wed, Fri, or Sat. The last day cannot be Tue, Thu, or Sat.",
              "Month Commonalities: In ordinary years, Jan & Oct start on the same day. In leap years, Jan, Apr, & July align.",
              "Calculation: If today is Monday, 128 days later is Wednesday (128 % 7 = 2 odd days).",
            ],
          },
          {
            id: "clock-math",
            heading: "Clock: Angles & Movement",
            icon: "🕒",
            color: "#940B92",
            table: {
              headers: ["Hand", "Unit Time", "Angle Traced"],
              rows: [
                ["Hour Hand", "12 Hours", "360°"],
                ["Hour Hand", "1 Hour", "30°"],
                ["Hour Hand", "1 Minute", "0.5°"],
                ["Minute Hand", "1 Minute", "6°"],
                ["Second Hand", "1 Second", "6°"],
              ],
            },
          },
          {
            id: "clock-formulas",
            heading: "Clock Reasoning Formulas",
            icon: "📐",
            color: "#FF820F",
            listItems: [
              {
                title: "Angle Between Hands",
                description:
                  "θ = |30H - (11/2)M| where H = Hours, M = Minutes. Formula: (60H - 11M)/2.",
              },
              {
                title: "Mirror Image",
                description:
                  "For 12hr clock, subtract given time from 11:60. For 24hr clock, use 23:60.",
              },
              {
                title: "Water Image",
                description:
                  "If minutes < 30, subtract from 18:30. If minutes > 30, subtract from 18:90.",
              },
              {
                title: "Coincidence",
                description:
                  "Hands coincide 11 times in 12 hours (22 times in 24 hours), skipping only 11 to 1 AM/PM.",
              },
            ],
          },
        ],
        keyTakeaways: [
          "The formula θ = |30H - (11/2)M| is the universal solution for finding angles between clock hands.",
          "Clock hands coincide 22 times and are in a straight line 44 times in a 24-hour period.",
          "Odd days (Total Days % 7) are the key to tracking weekdays across years and centuries.",
          "Century years are leap years ONLY if they are divisible by 400 (e.g., 2000 is, 1900 is not).",
        ],
      },
      Critical_Reasoning: {
        title: "Critical Reasoning",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Critical Reasoning. Along with this student will be empowered to solve different categories of questions involving deduction, logical relationships, and problems commonly asked in aptitude tests like NIFT/NID/UCEED.",
        introduction:
          "Critical Thinking is the ability to think clearly and rationally while recognizing logical relationships between concepts. It necessitates being an active learner rather than a passive receiver, deducing explanations for every logical link.",
        sections: [
          {
            id: "cr-types",
            heading: "Major Types of Critical Reasoning",
            icon: "🧠",
            color: "#407BFF",
            listItems: [
              {
                title: "Statement & Argument",
                description:
                  "Analyzing points of view for or against a proposition to select the strongest argument.",
              },
              {
                title: "Statement & Assumption",
                description:
                  "Identifying unstated premises or things taken for granted that support a given statement.",
              },
              {
                title: "Statement & Conclusion",
                description:
                  "Deducing what naturally follows from the presented facts or paragraph.",
              },
              {
                title: "Statement & Course of Action",
                description:
                  "Selecting the most practical and effective step to address a situation or problem.",
              },
              {
                title: "Cause & Effect",
                description:
                  "Assessing if statements are independent causes, shared effects, or directly related as cause-effect.",
              },
            ],
          },
          {
            id: "statement-assumption",
            heading: "Focus: Statement & Assumption",
            icon: "🔍",
            color: "#FF9A4F",
            body: "An assumption is the hidden link in an argument. Key categories include:",
            listItems: [
              {
                title: "Existence/Non-existence",
                description:
                  "Simple assumptions regarding whether a subject or fact exists as stated.",
              },
              {
                title: "Cause & Effect Relationship",
                description:
                  "Recognizing logical links often marked by words like 'therefore', 'thus', or 'hence'.",
              },
              {
                title: "Course of Action",
                description:
                  "Assumptions underlying a suggested response to a fact, report, or study.",
              },
              {
                title: "Advertisements/Notices",
                description:
                  "Assumptions in public interest notices or appeals (e.g., assuming people will read and react).",
              },
            ],
          },
          {
            id: "cr-tips",
            heading: "Critical Reasoning Tips & Tricks",
            icon: "💡",
            color: "#940B92",
            tips: [
              "Missing Link: View an assumption as the 'unsaid' bridge in the logical structure.",
              "Author's POV: Always judge the validity from the Author's perspective, not your own personal opinion.",
              "Emphasis Words: Watch for 'definitely', 'certainly', 'only', 'best', 'all' — these limit the scope and affect validity.",
              "Avoid Personal Bias: Do not bring in outside knowledge; stick to the information provided in the statement.",
              "Implicit vs Explicit: Remember that implicit assumptions are rarely corrected if they jump beyond the logical scope.",
            ],
          },
        ],
        keyTakeaways: [
          "Critical reasoning evaluates the logical link between a statement and its hidden assumptions or resulting conclusions.",
          "The 'Assumption Negation Test' is key: if the opposite of an assumption breaks the argument, the assumption is valid.",
          "Avoid personal bias or external knowledge; limit your scope strictly to the information provided in the prompt.",
          "Distinguish clearly between Fact (stated information), Assumption (hidden premise), and Inference (logical deduction).",
        ],
      },
      Arrangement_Circular_and_Linear: {
        title: "Arrangement Circular and Linear",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic-Arrangements Circular And Linear. Along with this student will be empowered to solve different categories of questions involving seating arrangements in straight lines and circles, and problems commonly asked in aptitude tests like NIFT/NID/UCEED.",
        introduction:
          "Arrangement problems involve placing people or objects in specific positions based on given conditions. These typically fall into two categories: Linear (straight line) and Circular (closed loop). Successful solving requires careful interpretation of relative positions like 'second to the left' or 'opposite'.",
        sections: [
          {
            id: "linear-arrangements",
            heading: "Linear Seating Arrangements",
            icon: "📏",
            color: "#407BFF",
            body: "People or objects are arranged in a straight line. Types include:",
            listItems: [
              {
                title: "Single Row Unidirectional",
                description:
                  "Everyone faces the same direction (e.g., all North).",
              },
              {
                title: "Single Row Bidirectional",
                description:
                  "People face different directions (some North, some South).",
              },
              {
                title: "Dual Row",
                description:
                  "Arranged in two parallel rows (often facing each other).",
              },
            ],
          },
          {
            id: "linear-logic",
            heading: "Linear Logic & Examples",
            icon: "💡",
            color: "#FF9A4F",
            hintItems: [
              {
                num: "POS",
                text: "Relative Positioning Tips",
                subbullets: [
                  "'k' places away means (k-1) people are in between.",
                  "'P is second to the left of Q' means P _ Q.",
                  "'P is 2 places away from Q' means P/Q _ _ Q/P.",
                ],
              },
              {
                num: "EX",
                text: "5 people P,Q,R,S,T in a queue. P is right of Q, R is right of T, P is left to T, Q is right to S. Middle person?",
                subbullets: [
                  "Logic: S-Q (Q is right of S), Q-P (P is right of Q), P-T (P is left of T), T-R (R is right of T).",
                  "Combined: S - Q - P - T - R.",
                  "Answer: P is in the middle.",
                ],
              },
            ],
          },
          {
            id: "circular-arrangements",
            heading: "Circular Seating Arrangements",
            icon: "🔄",
            color: "#940B92",
            body: "Objects are placed in a closed circular loop. Key types:",
            listItems: [
              {
                title: "Circular Unidirectional",
                description:
                  "Everyone faces either the center (Facing Inside) or away from the center (Facing Outside).",
              },
              {
                title: "Circular Bidirectional",
                description: "Some face the center while others face away.",
              },
            ],
          },
          {
            id: "circular-strategy",
            heading: "Circular Strategy & Tips",
            icon: "🎯",
            color: "#FF820F",
            tips: [
              "Draw first: Count the people and mark specific lines/seats on a circle first.",
              "Clockwise vs Anticlockwise: If facing center, Left is Clockwise and Right is Anticlockwise.",
              "Start with Fixed Info: Start the arrangement with a person whose position is definitively mentioned (e.g., 'P sits second to the left of Q').",
              "Symmetry: In even-numbered arrangements, check for 'opposite' relations.",
            ],
          },
        ],
        keyTakeaways: [
          "Always start with fixed information where the position of a person is definitively mentioned.",
          "In circular arrangements, remember that Left is Clockwise and Right is Anticlockwise when facing the center.",
          "For linear arrangements, 'facing North' mapping matches your own Left and Right.",
          "A rough sketch is essential—never try to solve arrangement problems mentally.",
        ],
      },
      Coding_Decoding: {
        title: "Coding Decoding",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Coding Decoding. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Coding is a process used to encrypt a word or number into a particular code based on a set of rules. Decoding is the reverse process of decrypting that pattern back into its original form. This test judges the ability to decipher messages by breaking the underlying code.",
        sections: [
          {
            id: "coding-types-overview",
            heading: "Core Coding-Decoding Types",
            icon: "🔑",
            color: "#407BFF",
            bullets: [
              "Alphabet Coding",
              "Numerical Coding",
              "Symbol Based Coding",
              "Alphabet-Symbol-Numerical Coding",
              "Values Coding",
              "Substitution Coding",
            ],
          },
          {
            id: "detailed-coding-types",
            heading: "Detailed Breakdown of Methods",
            icon: "🧩",
            color: "#FF9A4F",
            listItems: [
              {
                title: "Type 1: Letter/Number Coding",
                description:
                  "Letters are replaced by other letters or numbers according to a specific pattern (e.g., +2 shift, reverse rank).",
              },
              {
                title: "Type 2: Direct Coding",
                description:
                  "Symbols or numbers occur in the same sequence as the letters. It's a direct 1-to-1 substitution without complex shifts.",
              },
              {
                title: "Type 3: Conditional Coding",
                description:
                  "Codes for words must be found based on specific conditions provided (e.g., 'if the first letter is a vowel...').",
              },
              {
                title: "Type 4: Fictitious Language Coding",
                description:
                  "Sentences like 'pit na sa' mean 'you are good'. Decoded by comparing multiple sentences to find common words.",
              },
              {
                title: "Type 5: Substitution Coding",
                description:
                  "Words are substituted (e.g., 'Blue is called Red', 'Red is called Green'). You must find the final substituted name.",
              },
            ],
          },
          {
            id: "decoding-strategy",
            heading: "Strategic Approach",
            icon: "💡",
            color: "#940B92",
            tips: [
              "Look for Commonality: In Fictitious Language, pick two messages with a common word to isolate the code.",
              "Observe Sequence: In Direct Coding, usually the letters and codes correspond in exact order.",
              "Pattern Detection: Quick checks for position shifts (+1, -1, +2, etc.) or alphabetical opposites (A-Z, B-Y).",
              "Reverse Engineering: Sometimes decoding from the last letter or the middle of the word helps spot the pattern faster.",
            ],
          },
        ],
        keyTakeaways: [
          "Always check for the simplest patterns first, such as position shifts (+1, -1) or alphabetical opposites (A-Z).",
          "For fictitious language coding, use the elimination method by comparing multiple sentences with common words.",
          "Maintaining a quick-reference list of alphabetical positions (A=1, M=13, Z=26) is vital for speed.",
          "In substitution coding, focus only on the final object being asked about to avoid unnecessary mapping steps.",
        ],
      },
      "Decision_Making,_Order_and_Ranking": {
        title: "Decision Making and Order & Ranking",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic-Decision Making and Order & Ranking. Along with this students will be empowered to solve different categories of questions involving analytical decision making and positional ranking calculations.",
        introduction:
          "Decision Making involves analyzing provided eligibility criteria or situations to choose the best course of action. Order and Ranking focuses on determining the position of persons in a row or the total strength of a group based on relative positions.",
        sections: [
          {
            id: "decision-making-types",
            heading: "Decision Making Types",
            icon: "⚖️",
            color: "#407BFF",
            listItems: [
              {
                title: "Direct Questions",
                description:
                  "Decisions made based on a single condition or straightforward scenario.",
              },
              {
                title: "Complex Eligibility Test",
                description:
                  "Evaluating a candidate's bio-data against multiple criteria (e.g., for jobs or college admissions).",
              },
              {
                title: "Passage-based Selection",
                description:
                  "Analyzing a group of statements or a paragraph to decide which alternative follows logically.",
              },
            ],
          },
          {
            id: "order-ranking-case1",
            heading: "Order & Ranking: Case 1 (Single Person)",
            icon: "📏",
            color: "#FF9A4F",
            body: "Formulas for when the rank of one person is known from both sides.",
            listItems: [
              {
                title: "Total Persons",
                description: "Total = (Rank from Left + Rank from Right) – 1",
              },
              {
                title: "Position from Opposite Side",
                description: "Position = (Total - Given Position) + 1",
              },
            ],
          },
          {
            id: "order-ranking-case2",
            heading: "Order & Ranking: Case 2 (Two Persons)",
            icon: "👥",
            color: "#940B92",
            subsections: [
              {
                title: "Non-Overlapping Case",
                body: "If Total > (Left Position + Right Position):\nNumber of persons between = Total – (Left + Right)",
                color: "#940B92",
              },
              {
                title: "Overlapping Case",
                body: "If (Left + Right) > Total:\nNumber of persons between = (Left + Right) – Total – 2",
                color: "#FF820F",
              },
            ],
          },
          {
            id: "order-ranking-interchange",
            heading: "Order & Ranking: Interchanging Positions",
            icon: "🔄",
            color: "#15E4B9",
            listItems: [
              {
                title: "New Position of 2nd Person",
                description:
                  "New Position = Old Position + (Change in first person's position)",
              },
              {
                title: "Total Strength",
                description:
                  "Total = (Current position of one person + Old position of another person) – 1",
              },
              {
                title: "Persons Between",
                description:
                  "Between = Difference in positions of same person – 1",
              },
            ],
          },
          {
            id: "ranking-examples",
            heading: "Practice Examples",
            icon: "✏️",
            color: "#FF7A7A",
            hintItems: [
              {
                num: "1",
                text: "Ajay is 30th from left and 34th from right. Total?",
                subbullets: ["(30 + 34) - 1 = 63."],
              },
              {
                num: "2",
                text: "Total 62, Riya is 36th from left, Raman 29th from right. Between?",
                subbullets: [
                  "Check: 36 + 29 = 65 (Greater than 62, so Overlapping).",
                  "Logic: (36 + 29) - 62 - 2 = 1 person.",
                ],
              },
            ],
          },
        ],
        keyTakeaways: [
          "Total Strength formula: Total = (Rank from Left + Rank from Right) - 1.",
          "Check for overlapping: If (Sum of Ranks from ends) > Total Strength, it is an Overlapping Case.",
          "In position interchanging, the difference between the old and new positions remains constant for both individuals.",
          "Decision Making requires evaluating data strictly against provided eligibility criteria while identifying relaxation/exception rules.",
        ],
      },
      Direction_Problem: {
        title: "Direction Problem",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Direction Problems. Along with this student will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such aptitude test like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Direction is the information contained in the relative position of one point with respect to another point without the distance information. Understanding direction charts and relative movements is key to solving these problems.",
        sections: [
          {
            id: "cardinal-directions",
            heading: "Cardinal Directions & Chart",
            icon: "🧭",
            color: "#407BFF",
            body: "The four main cardinal points are North (N), East (E), South (S), and West (W).",
            listItems: [
              {
                title: "Angle of Change",
                description:
                  "Each main direction change is 90° (e.g., North to East). A change to an intermediate direction (e.g., North to North-East) is 45°.",
              },
              {
                title: "Intermediate Directions",
                description:
                  "North-East (NE), North-West (NW), South-East (SE), and South-West (SW).",
              },
            ],
          },
          {
            id: "movement-logic",
            heading: "Left-Right Movement Logic",
            icon: "🔄",
            color: "#FF9A4F",
            body: "Movement is always relative to the direction being faced. When not specified, assume turns are exactly 90°.",
            table: {
              headers: [
                "Facing Direction",
                "Left Turn faces...",
                "Right Turn faces...",
              ],
              rows: [
                ["North", "West", "East"],
                ["West", "South", "North"],
                ["East", "North", "South"],
                ["South", "East", "West"],
              ],
            },
            tips: [
              "Left Movement: Always corresponds to an anti-clockwise direction.",
              "Right Movement: Always corresponds to a clockwise direction.",
            ],
          },
          {
            id: "pythagoras-concept",
            heading: "Pythagoras Theorem in Directions",
            icon: "📐",
            color: "#940B92",
            body: "To find the shortest (displacement) distance between the starting and ending points, we use the Pythagoras Theorem in a right-angled triangle.",
            listItems: [
              {
                title: "The Formula",
                description:
                  "c² = a² + b², where 'c' is the hypotenuse (shortest path) and 'a', 'b' are the other two sides.",
              },
            ],
          },
          {
            id: "direction-key-learning",
            heading: "Key Learning Summaries",
            icon: "💡",
            color: "#FF820F",
            bullets: [
              "Always draw a quick direction cross (N, S, E, W) before starting the problem.",
              "Left = Anti-clockwise turn (90°).",
              "Right = Clockwise turn (90°).",
              "Smallest distance calculation usually requires forming a right-angled triangle.",
            ],
          },
        ],
        keyTakeaways: [
          "Always draw the cardinal direction cross (N, S, E, W) first to maintain orientation.",
          "Left turns are anti-clockwise (90°) and Right turns are clockwise (90°) relative to the facing direction.",
          "Intermediate directions (NE, NW, SE, SW) result from 45° shifts between cardinal points.",
          "Use the Pythagoras Theorem (c² = a² + b²) to calculate the shortest path or displacement distance.",
        ],
      },
      Number_Series: {
        title: "Number Series",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic-Number Series. Along with this students will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in such Aptitude Tests like NIFT/NID/UCEED and all such entrances.",
        introduction:
          "Number series questions are based on numerical sequences that follow a logical rule or pattern based on elementary arithmetic concepts. The goal is to analyze the sequence and either identify a wrong number, find a missing number, or complete a parallel series.",
        sections: [
          {
            id: "series-question-types",
            heading: "Common Question Types",
            icon: "❓",
            color: "#407BFF",
            listItems: [
              {
                title: "Identify Wrong Number",
                description:
                  "A series where one number is incorrectly placed and breaks the pattern.",
              },
              {
                title: "Find Missing Number",
                description:
                  "A series with a gap that must be filled using the established logic.",
              },
              {
                title: "Complete Incomplete Series",
                description:
                  "Applying the pattern of a complete series to a second, partial series.",
              },
            ],
          },
          {
            id: "series-patterns",
            heading: "Common Series Patterns",
            icon: "🧩",
            color: "#FF9A4F",
            hintItems: [
              {
                num: "SQ",
                text: "Perfect Squares & Cubes",
                subbullets: [
                  "Squares: 324, 361, 400, 441... (18², 19², 20², 21²...)",
                  "Cubes: 512, 729, 1000... (8³, 9³, 10³...)",
                ],
              },
              {
                num: "GEO",
                text: "Arithmetic & Geometric Series",
                subbullets: [
                  "Arithmetic: Constant addition/subtraction (e.g., +5 shift).",
                  "Geometric: Constant multiplication/division (e.g., ×9 shift).",
                ],
              },
              {
                num: "MIX",
                text: "Mixed & Complex Series",
                subbullets: [
                  "Two-stage: The differences between terms form their own arithmetic series.",
                  "Arithmetico-Geometric: Combination of adding and multiplying (e.g., 1, 4, 8, 11, 22... follows +3, ×2).",
                  "Twin/Alternate: Two independent series combined into one (e.g., 3, 4, 8, 10... where odd positions +5 and even positions +6).",
                ],
              },
            ],
          },
          {
            id: "complex-examples",
            heading: "Complex Pattern Examples",
            icon: "💡",
            color: "#940B92",
            listItems: [
              {
                title: "Two-stage Arithmetic",
                description:
                  "1, 3, 6, 10, 15... (Differences are 2, 3, 4, 5... so next difference is 6, giving 21).",
              },
              {
                title: "Geometrico-Arithmetic",
                description:
                  "1, 2, 6, 36, 44, 440... (Operations: ×2, +4, ×6, +8, ×10... next is +12, giving 452).",
              },
              {
                title: "Standard Mixed",
                description: "10, 22, 46, 94... (Rule: Previous × 2 + 2).",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Always start by calculating the difference between consecutive terms to check for an arithmetic progression.",
          "Check for common number properties like perfect squares, cubes, and prime numbers.",
          "Look for 'Twin' series where two independent patterns are interleaved in odd and even positions.",
          "Complex patterns often involve a combination of operations, such as (Previous Term × n) + k.",
        ],
      },
      Puzzle: {
        title: "Puzzle",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Puzzle. Along with this student will be empowered to solve different categories of questions involving systematic arrangement and haphazard data commonly asked in aptitude tests like NIFT/NID/UCEED.",
        introduction:
          "Puzzles involve raw, haphazard information that must be systematically arranged into a correct sequence or order. Success depends on decoding jumbled data to reveal floor plans, schedules, or multi-variable relationships.",
        sections: [
          {
            id: "puzzle-types",
            heading: "Common Puzzle Categories",
            icon: "🧩",
            color: "#407BFF",
            listItems: [
              {
                title: "Floor & Box Puzzles",
                description:
                  "Arranging people on specific building floors or stacking boxes in a vertical order.",
              },
              {
                title: "Scheduling Puzzle",
                description:
                  "Organizing activities based on chronological data like months, days, or years.",
              },
              {
                title: "Double Line-up / Multi-Variable",
                description:
                  "Matching multiple sets of variables (e.g., people, their professions, and their favorite colors).",
              },
              {
                title: "Geometric Puzzles",
                description:
                  "Arranging items in linear rows, around square tables, or even triangular corners.",
              },
            ],
          },
          {
            id: "positioning-logic",
            heading: "Positioning Logic & Syntax",
            icon: "📐",
            color: "#FF9A4F",
            body: "Understanding the precise meaning of placement terms is crucial for mapping the puzzle.",
            listItems: [
              {
                title: "'k' places away",
                description:
                  "If A is 3 people away from B, there are exactly 2 (k-1) people between them.",
              },
              {
                title: "Relative Directions",
                description:
                  "'A second to the left of B' is A – B. 'A second to the right' is B – A.",
              },
              {
                title: "Specific Ranges",
                description:
                  "'A is 2 places away from B' means A/B – – B/A (Order unknown).",
              },
            ],
          },
          {
            id: "puzzle-tricks",
            heading: "Strategic Tips & Tricks",
            icon: "💡",
            color: "#940B92",
            tips: [
              "Chronology Logic: A person born in 1990 is always older than someone born in 1992. January is older than March.",
              "Triangle Seating: In a triangle, 3 corners + 3 sides. Note if they face the center or away from it.",
              "Fixed Info First: Always start by placing 'fixed' information (e.g., 'A lives on floor 4') before handling relative conditions.",
              "Negative Constraints: Pay close attention to what *cannot* happen (e.g., 'C does not like Red') to eliminate possibilities.",
            ],
          },
        ],
        keyTakeaways: [
          "Start by identifying the fixed variables (like floors, days of the week, or months).",
          "Use a tabular format to keep track of positive and negative information concurrently.",
          "Link information pieces together—if a person is linked to a day, and that day is linked to an activity, combine them immediately.",
          "Keep track of multiple possibilities if the initial data is ambiguous, then eliminate them as you go.",
        ],
      },
      Visual_Reasoning: {
        title: "Visual Reasoning",
        subject: "General Test – Logical Reasoning",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic-Visual Reasoning. Along with this student will be empowered to solve different categories of questions involving spatial patterns, 2D/3D transformations, and problems commonly asked in Aptitude Tests like NIFT/NID/UCEED.",
        introduction:
          "Visual Reasoning tests the ability to analyze and interpret information presented in the form of figures, drawings, and designs. It encompasses tasks ranging from identifying patterns and analogies to understanding spatial transformations like paper cutting or cube rotation.",
        sections: [
          {
            id: "analogist-classification",
            heading: "Analogy & Classification",
            icon: "📐",
            color: "#407BFF",
            listItems: [
              {
                title: "Analogist",
                description:
                  "Establish a relationship between a pair of figures and apply it to a new pair (e.g., if elements double in the first pair, they must double in the second).",
              },
              {
                title: "Classification",
                description:
                  "The 'odd man out' test. Identify the one figure in a set that does not share the common characteristic of the others.",
              },
            ],
          },
          {
            id: "series-patterns",
            heading: "Series & Completion",
            icon: "📈",
            color: "#FF9A4F",
            listItems: [
              {
                title: "Series",
                description:
                  "Figures change from left to right in a specific order. Predict the next step in the sequence (e.g., a rotating line).",
              },
              {
                title: "Completion of Pattern",
                description:
                  "Identifying the missing part of a complex design to make the whole figure complete and symmetrical.",
              },
              {
                title: "Matrix",
                description:
                  "Filling missing cells in a grid based on row-wise and column-wise changes in direction, number, or style.",
              },
            ],
          },
          {
            id: "spatial-transformations",
            heading: "Spatial Transformations",
            icon: "🧊",
            color: "#940B92",
            subsections: [
              {
                title: "Cube Turning & Dice",
                body: "Dealing with different positions of the same cube. For dice, use rotation logic (e.g., rotating clockwise to find opposite faces).",
                color: "#940B92",
              },
              {
                title: "Paper Cutting & Folding",
                body: "Visualizing the result of unfolding a cut sheet or folding a transparent sheet with designs on both sides.",
                color: "#FF820F",
              },
              {
                title: "Mirror Images",
                body: "Reflections in horizontal or vertical mirrors. Remember: Horizontal reflection flips top/bottom.",
                color: "#15E4B9",
              },
            ],
          },
          {
            id: "figure-analysis",
            heading: "Figure Analysis",
            icon: "🔍",
            color: "#FF7A7A",
            listItems: [
              {
                title: "Figure Perception",
                description:
                  "Counting the number of specific shapes (squares, triangles, rectangles) hidden within a complex design.",
              },
              {
                title: "Hidden Figure Test",
                description:
                  "Identifying a simple base design embedded within a more complex overlapping structure.",
              },
              {
                title: "Construction of Squares",
                description:
                  "Combining three specific parts out of five alternatives to form a perfect square.",
              },
            ],
          },
          {
            id: "advanced-logic",
            heading: "Advanced Visual Logic",
            icon: "💡",
            color: "#407BFF",
            listItems: [
              {
                title: "Similar Quality",
                description:
                  "Choosing a figure that shares the same structural properties (e.g., 'shape inside a larger similar shape').",
              },
              {
                title: "Dot Situation",
                description:
                  "Finding a figure where a dot can be placed in a region common to a specific overlapping combination (e.g., circle and triangle but not square).",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Think in terms of rotation, reflection, and scaling for Analogy and Series questions.",
          "In paper cutting/folding, use symmetry and mirror-image logic to predict the final design.",
          "For dice/cube problems, use the rotation rule to identify opposite faces quickly.",
          "Sectional counting and structural mapping are essential for Figure Perception tasks.",
        ],
      },
    },
    General_Awareness: {
      Indian_Geography: {
        title: "India Geography",
        subject: "General Test – General Awareness",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts of Indian Geography, including its location, size, physical features, and major physiographic divisions. Students will gain insights into the diverse landscapes of India, from the Himalayas to the coastal plains and islands.",
        sections: [
          {
            id: "location-size",
            heading: "Location and Size",
            icon: "📍",
            color: "#407BFF",
            listItems: [
              {
                title: "Hemisphere",
                description:
                  "Entirely in the Northern Hemisphere; longitudinally in the Eastern Hemisphere.",
              },
              {
                title: "Coordinates",
                description:
                  "Latitude: 8°4′ N to 37°6′ N; Longitude: 68°7′ E to 97°25′ E.",
              },
              {
                title: "Tropic of Cancer",
                description:
                  "Divides India into almost two equal parts. Passes through: Gujarat, Rajasthan, MP, Chhattisgarh, Jharkhand, West Bengal, Tripura, and Mizoram.",
              },
              {
                title: "Area",
                description:
                  "3.28 million sq km (2.4% of world's total area). 7th largest country globally.",
              },
              {
                title: "Extents",
                description:
                  "North-South: 3,214 km (Kashmir to Kanyakumari); East-West: 2,933 km (Arunachal to Gujarat).",
              },
            ],
          },
          {
            id: "india-world",
            heading: "India and the World",
            icon: "🌏",
            color: "#FF9A4F",
            body: "India shares its land boundaries with seven countries: Pakistan, Afghanistan, China, Nepal, Bhutan, Myanmar, and Bangladesh. Longest border is with Bangladesh (4,096.7 km).",
            listItems: [
              {
                title: "Standard Time",
                description:
                  "Standard Meridian (82°30′ E) passes through Mirzapur (UP). IST is 5h 30m ahead of GMT.",
              },
              {
                title: "Southern Neighbors",
                description:
                  "Sri Lanka (separated by Palk Strait and Gulf of Mannar) and Maldives.",
              },
            ],
          },
          {
            id: "physiographic-divisions",
            heading: "Major Physiographic Divisions",
            icon: "🏔️",
            color: "#940B92",
            bullets: [
              "The Himalayan Mountains",
              "The Northern Plains",
              "The Peninsular Plateau",
              "The Great Indian Desert",
              "The Coastal Plains",
              "The Islands",
            ],
          },
          {
            id: "himalayas",
            heading: "The Himalayan Mountains",
            icon: "🏔️",
            color: "#15E4B9",
            subsections: [
              {
                title: "Structural Ranges",
                body: "Trans Himalaya, Himadri (Greater), Himachal (Lesser), and Shiwalik (Outer).",
                color: "#15E4B9",
              },
              {
                title: "Highest Peaks",
                body: "Mt. Everest (8848m, Nepal), Kanchenjunga (8598m, India), Nanga Parbat (8126m, India), Nanda Devi (7817m, India).",
                color: "#407BFF",
              },
            ],
          },
          {
            id: "northern-plains",
            heading: "The Northern Plains",
            icon: "🌾",
            color: "#EBC33C",
            body: "Formed by alluvial deposits of Indus, Ganga, and Brahmaputra. Divided into four regions based on relief variation:",
            listItems: [
              {
                title: "Bhabar",
                description:
                  "Narrow belt of pebbles; streams often disappear here due to high porosity.",
              },
              {
                title: "Terai",
                description:
                  "Marshy and swampy region where streams re-emerge; rich vegetation/wildlife.",
              },
              {
                title: "Bhangar",
                description:
                  "Older alluvium; terrace-like features containing calcareous deposits (Kankar).",
              },
              {
                title: "Khadar",
                description:
                  "Newer, fertile silty deposits ideal for intensive agriculture.",
              },
            ],
          },
          {
            id: "peninsular-plateau",
            heading: "The Peninsular Plateau",
            icon: "⛰️",
            color: "#FF7A7A",
            body: "Oldest and most stable landmass (Gondwana origin). Divided into:",
            listItems: [
              {
                title: "Central Highlands",
                description:
                  "North of Narmada; includes Malwa plateau and Aravallis (Guru Sikhar is highest peak).",
              },
              {
                title: "Deccan Plateau",
                description:
                  "Triangular mass south of Narmada; bordered by Western and Eastern Ghats.",
              },
              {
                title: "Western vs Eastern Ghats",
                description:
                  "Western Ghats are higher (Anaimudi, 2695m) and continuous. Eastern Ghats are discontinuous and lower (Mahendragiri, 1501m).",
              },
            ],
          },
          {
            id: "other-features",
            heading: "Desert, Coasts and Islands",
            icon: "🏝️",
            color: "#FF820F",
            subsections: [
              {
                title: "Great Indian Desert",
                body: "Undulating topography with sand dunes (barchans). Luni is the only major river.",
                color: "#FF820F",
              },
              {
                title: "Coastal Plains",
                body: "Western (submerged, narrow) and Eastern (emergent, wider with deltas of Godavari/Krishna).",
                color: "#407BFF",
              },
              {
                title: "Islands",
                body: "Andaman & Nicobar (Bay of Bengal, volcanic origin) and Lakshadweep (Arabian Sea, coral origin).",
                color: "#15E4B9",
              },
            ],
          },
        ],
        keyTakeaways: [
          "India lies entirely in the Northern Hemisphere and mainly in the Eastern Hemisphere.",
          "The Tropic of Cancer divides India into almost two equal latitudinal parts.",
          "India has six major physiographic divisions including Himalayas and Coastal Plains.",
          "The Northern Plains are formed by alluvial deposits of Indus, Ganga, and Brahmaputra.",
          "Western Ghats are higher and more continuous than the Eastern Ghats.",
        ],
      },
      Indian_Economics: {
        title: "Indian Economics",
        subject: "General Test – General Awareness",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Economics. Students will be empowered to solve different categories of questions involving economic principles, market structures, and the Indian financial system as asked in competitive exams like NIFT/NID/UCEED.",
        introduction:
          "Economics is the study of how society manages its scarce resources to satisfy unlimited ends. It is broadly categorized into Microeconomics (individual behavior) and Macroeconomics (system as a whole).",
        sections: [
          {
            id: "economic-basics",
            heading: "Fundamentals of Economics",
            icon: "📊",
            color: "#407BFF",
            listItems: [
              {
                title: "Micro vs Macro",
                description:
                  "Micro studies individual units (consumers, producers); Macro studies the economy as a whole (income, employment, trade).",
              },
              {
                title: "Production Possibility Curve (PPC)",
                description:
                  "Shows all possible combinations of two goods produced with available resources and technology.",
              },
              {
                title: "Marginal Opportunity Cost (MOC)",
                description:
                  "The amount of one good sacrificed to produce an additional unit of another good along the PPC.",
              },
            ],
          },
          {
            id: "demand-supply",
            heading: "Demand, Supply and Goods",
            icon: "🛒",
            color: "#FF9A4F",
            subsections: [
              {
                title: "Core Laws",
                body: "Law of Demand: Price and quantity vary inversely. Law of Supply: Price and supply are directly proportional. Equilibrium: Demand equals Supply.",
                color: "#FF9A4F",
              },
              {
                title: "Types of Goods",
                body: "Substitute (Tea/Coffee), Complementary (Petrol/Car), Normal (Income UP -> Demand UP), Inferior (Income UP -> Demand DOWN), Veblen (Status goods where Price UP -> Demand UP).",
                color: "#FF820F",
              },
            ],
          },
          {
            id: "elasticity-market",
            heading: "Elasticity and Market Forms",
            icon: "🏪",
            color: "#940B92",
            listItems: [
              {
                title: "Price Elasticity (Ed)",
                description:
                  "Ed=0 (Perfectly Inelastic), Ed=1 (Unitary), Ed=∞ (Perfectly Elastic). Engel's Law: Food spend % decreases as income rises.",
              },
              {
                title: "Market Structures",
                description:
                  "Perfect Competition (Many sellers, uniform price), Monopoly (Single seller), Monopolistic (Differentiated products), Oligopoly (Few large sellers).",
              },
            ],
          },
          {
            id: "production-costs",
            heading: "Production, Costs and Revenue",
            icon: "🏗️",
            color: "#15E4B9",
            listItems: [
              {
                title: "Product Concepts",
                description:
                  "Total Product (TP), Average Product (AP), and Marginal Product (MP) - the addition to TP by employing one more unit.",
              },
              {
                title: "Cost Types",
                description:
                  "Explicit (Actual payments), Implicit (Owner's resources), Fixed (Constant in short run), Variable (Varies with output), Opportunity Cost (Next best alternative foregone).",
              },
              {
                title: "Revenue",
                description:
                  "TR (Total receipts), AR (Price per unit), MR (Additional revenue). Break-even: TR = TC. Shut-down: TR = TVC.",
              },
            ],
          },
          {
            id: "indian-economy-sectors",
            heading: "Structure of Indian Economy",
            icon: "🇮🇳",
            color: "#EBC33C",
            listItems: [
              {
                title: "Mixed Economy",
                description:
                  "Unique combination of public and private sectors. Primarily agrarian but shifting towards a market economy.",
              },
              {
                title: "Sectors",
                description:
                  "Primary (Agriculture/Natural resources), Secondary (Manufacturing), Tertiary (Services - highest GDP contributor at ~53%).",
              },
            ],
          },
          {
            id: "national-income-inflation",
            heading: "National Income and Inflation",
            icon: "📈",
            color: "#FF7A7A",
            listItems: [
              {
                title: "National Income Aggregates",
                description:
                  "GDP (Within territory), GNP (GDP + NFIA), NDP (GDP - Depreciation). Real GDP is measured at constant prices.",
              },
              {
                title: "Calculation Methods",
                description:
                  "Output (Value Added), Income (Factor payments), and Expenditure (C + I + G + [X-M]).",
              },
              {
                title: "Inflation Types",
                description:
                  "Demand-pull, Cost-push, Stagflation (High inflation + unemployment), Hyperinflation (extremely rapid growth).",
              },
            ],
          },
          {
            id: "taxation-reforms",
            heading: "Taxation and Economic Reforms",
            icon: "📜",
            color: "#407BFF",
            listItems: [
              {
                title: "Direct vs Indirect Tax",
                description:
                  "Direct: Income Tax, Corporate Tax. Indirect: GST (Replacing VAT/Sales tax), Custom Duty, Excise Duty.",
              },
              {
                title: "LPG Reforms (1991)",
                description:
                  "Liberalisation (relaxed controls), Privatisation (private sector entry), Globalisation (integrating with world economy).",
              },
              {
                title: "GST Structure",
                description:
                  "Central (CGST), State (SGST), Integrated (IGST). Slabs: 0%, 5%, 12%, 18%, 28%.",
              },
            ],
          },
          {
            id: "poverty-unemployment",
            heading: "Poverty and Unemployment",
            icon: "👥",
            color: "#FF820F",
            listItems: [
              {
                title: "Poverty",
                description:
                  "Absolute (minimum needs) and Relative (standard of living comparison).",
              },
              {
                title: "Unemployment Types",
                description:
                  "Cyclical (business cycles), Frictional (temporary job change), Seasonal (agriculture), Disguised (apparent work but zero marginal productivity).",
              },
            ],
          },
          {
            id: "rbi-banking",
            heading: "RBI and Monetary Policy",
            icon: "🏦",
            color: "#940B92",
            listItems: [
              {
                title: "RBI Functions",
                description:
                  "Bank of Issue, Banker to Govt & Banks, Manager of Foreign Exchange, Controller of Credit.",
              },
              {
                title: "Monetary Tools",
                description:
                  "Repo Rate (RBI lends to banks), Reverse Repo (RBI borrows), CRR (Cash with RBI), SLR (Liquid assets with banks).",
              },
              {
                title: "Money Supply",
                description:
                  "M1 (Narrow money: Currency + Demand deposits), M3 (Broad money), Fiat Money (Currency in circulation).",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Economics studies allocation of scarce resources to satisfy unlimited wants.",
          "Indian economy is a mixed economy with public and private sector coexistence.",
          "Demand and supply determine price equilibrium in a market.",
          "GDP measures income within territory while GNP includes foreign income.",
          "RBI controls money supply using monetary policy tools like Repo and CRR.",
        ],
      },
      Indian_History: {
        title: "Indian History",
        subject: "General Test – General Awareness",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Indian History. Along with this student will be empowered to solve different categories of questions involving historical events, personalities, and monuments commonly asked in aptitude tests like NIFT/NID/UCEED.",
        introduction:
          "India has a rich and diverse history spanning over 5,000 years. From the Indus Valley Civilization to the Mughal Empire, India's past is a story of great achievements, cultural exchanges, and powerful empires that have shaped the country's identity and contributed to the world's heritage.",
        sections: [
          {
            id: "origin-periods",
            heading: "Origin and Major Periods",
            icon: "📜",
            color: "#407BFF",
            listItems: [
              {
                title: "Indus Valley Civilization (2600-1900 BCE)",
                description:
                  "Emerging in the northwest, it was characterized by advanced urban planning, drainage systems, and trade networks.",
              },
              {
                title: "Vedic Period (1500-500 BCE)",
                description:
                  "Marked by the composition of the Vedas and the rise of the first Indian states. It saw the development of Sanskrit and the spread of Hinduism.",
              },
              {
                title: "Classical Empires",
                description:
                  "The Mauryan Empire (3rd century BCE) and the Gupta Empire (4th-6th centuries CE), the latter being a 'Golden Age' of art and science.",
              },
            ],
          },
          {
            id: "history-timeline",
            heading: "Historical Timeline",
            icon: "⏳",
            color: "#FF9A4F",
            table: {
              headers: ["Period", "Time Duration"],
              rows: [
                ["Indus Valley Civilization", "2600-1900 BCE"],
                ["Vedic Period", "1500-500 BCE"],
                ["Mauryan Empire", "321-185 BCE"],
                ["Gupta Empire", "320-550 CE"],
                ["Delhi Sultanate", "1206-1526 CE"],
                ["Mughal Empire", "1526-1857 CE"],
              ],
            },
          },
          {
            id: "notable-personalities",
            heading: "Important Personalities",
            icon: "👤",
            color: "#940B92",
            table: {
              headers: ["Personality", "Description"],
              rows: [
                [
                  "Gautama Buddha",
                  "Founder of Buddhism; taught the Four Noble Truths and Eightfold Path.",
                ],
                [
                  "Emperor Ashoka",
                  "Mauryan ruler who converted to Buddhism and promoted non-violence and social welfare.",
                ],
                [
                  "Emperor Akbar",
                  "Mughal emperor known for religious tolerance and administrative reforms.",
                ],
                [
                  "Kautilya (Chanakya)",
                  "Teacher and strategist; wrote 'Arthasasthra' on statecraft.",
                ],
                [
                  "Aryabhatta",
                  "Astronomer and mathematician; analysed solar/lunar eclipses.",
                ],
                [
                  "Kalidas",
                  "Classic Sanskrit author (Shakuntala, Meghadootam).",
                ],
                ["Alexander", "Macedonian ruler who invaded India in 326 BC."],
                [
                  "Bimbisara",
                  "Founded the Magadhan Empire; first influential king of ancient India.",
                ],
                [
                  "Harshavardhana",
                  "Powerful ruler of North India; patron of Xuanzang (Hiuen Tsang).",
                ],
                [
                  "Pulikeshin II",
                  "Chalukya king who defeated Harshavardhana in the north.",
                ],
                ["Varahamihira", "Astronomer who wrote 'Brihat Samhita'."],
                [
                  "Sankaracharya",
                  "Propagated Advaita Philosophy; born in Kerala.",
                ],
                [
                  "Mahatma Gandhi",
                  "Champion of Ahimsa and leader of India's independence movement.",
                ],
              ],
            },
          },
          {
            id: "historical-places",
            heading: "Historical Places and Significance",
            icon: "🏛️",
            color: "#15E4B9",
            listItems: [
              {
                title: "Taj Mahal (Agra)",
                description:
                  "Built by Shah Jahan for Mumtaz Mahal; a symbol of Mughal architecture.",
              },
              {
                title: "Ajanta & Ellora Caves",
                description:
                  "34 cave temples in Maharashtra (Hindu, Buddhist, and Jain).",
              },
              {
                title: "Hampi (Karnataka)",
                description:
                  "Ruins of the Vijayanagara Empire; UNESCO World Heritage Site.",
              },
              {
                title: "Konark Sun Temple",
                description:
                  "Located in Odisha; a 13th-century temple shaped like a giant chariot.",
              },
              {
                title: "Golden Temple",
                description:
                  "Amritsar; the holiest shrine of Sikhism (Harmandir Sahib).",
              },
              {
                title: "Charminar (Hyderabad)",
                description:
                  "Built in 1591 to commemorate the end of a plague epidemic.",
              },
              {
                title: "Khajuraho (MP)",
                description:
                  "Famous for its Nagara-style architecture and erotic sculptures.",
              },
              {
                title: "Sanchi Stupa (MP)",
                description:
                  "One of the oldest stone structures in India; commissioned by Ashoka.",
              },
              {
                title: "Golgumbus (Bijapur)",
                description:
                  "Tomb of Mohammed Adil Shah; famous for its 'Whispering Gallery'.",
              },
            ],
          },
        ],
        keyTakeaways: [
          "Indian history spans from Indus Valley Civilization to the modern era.",
          "Maurya and Gupta empires marked major political and cultural growth.",
          "Mughal rulers contributed significantly to administration and architecture.",
          "Religion, trade, and foreign invasions shaped India’s historical evolution.",
          "Historical monuments reflect India’s diverse cultural heritage.",
        ],
      },
      Indian_Polity: {
        title: "Indian Polity",
        subject: "General Test – General Awareness",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – Indian Polity. Students will be empowered to solve different categories of questions involving the Constitution, government structure, fundamental rights, and political institutions.",
        introduction:
          "The Constitution of India is the supreme law of the land, providing the framework for governance, defining rights, and establishing principles of justice, equality, and liberty.",
        sections: [
          {
            id: "constitution-basics",
            heading: "The Constitution",
            icon: "⚖️",
            color: "#407BFF",
            listItems: [
              {
                title: "Adoption",
                description:
                  "Passed on Nov 26, 1949; came into effect on Jan 26, 1950 (Republic Day).",
              },
              {
                title: "Duration",
                description:
                  "Drafted over 2 years, 11 months, and 18 days by the Constituent Assembly.",
              },
              {
                title: "Key Attributes",
                description:
                  "Longest written constitution; declares India a Sovereign, Socialist, Secular, Democratic Republic.",
              },
            ],
          },
          {
            id: "constitutional-sources",
            heading: "Sources of the Constitution",
            icon: "🌍",
            color: "#FF9A4F",
            table: {
              headers: ["Country", "Borrowed Features"],
              rows: [
                [
                  "UK",
                  "Parliamentary system, Single Citizenship, Rule of Law, Speaker.",
                ],
                [
                  "USA",
                  "Fundamental Rights, Judicial Review, Preamble, Electoral College.",
                ],
                ["Ireland", "Directive Principles of State Policy (DPSP)."],
                [
                  "Canada",
                  "Quasi-federal system with a strong Centre, Residual Powers.",
                ],
                ["USSR", "Fundamental Duties (Art 51-A), Planning Commission."],
                ["South Africa", "Procedure for Constitutional Amendment."],
              ],
            },
          },
          {
            id: "rights-and-duties",
            heading: "Rights, Duties and Writs",
            icon: "📜",
            color: "#940B92",
            subsections: [
              {
                title: "Fundamental Rights (Art 12-35)",
                body: "Six categories: Equality, Freedom, against Exploitation, Freedom of Religion, Cultural/Educational rights, and Constitutional Remedies.",
                color: "#940B92",
              },
              {
                title: "Fundamental Duties (Art 51-A)",
                body: "Added by 42nd Amendment (1976). Originally 10; 11th added in 2002 (Education for 6-14 year olds).",
                color: "#407BFF",
              },
              {
                title: "Judicial Writs",
                body: "Five types: Habeas Corpus (to produce a person), Mandamus (to command duty), Certiorari (to quash order), Quo Warranto (by what authority), Prohibition.",
                color: "#15E4B9",
              },
            ],
          },
          {
            id: "parliamentary-system",
            heading: "The Parliament",
            icon: "🏛️",
            color: "#EBC33C",
            listItems: [
              {
                title: "Rajya Sabha (Council of States)",
                description:
                  "Permanent body; 250 max members. Members serve 6 years; 1/3 retire every 2 years. Vice-President is ex-officio Chairman.",
              },
              {
                title: "Lok Sabha (House of the People)",
                description:
                  "Maximum 552 members; 5-year term. Money bills can only be introduced here.",
              },
              {
                title: "The Speaker",
                description:
                  "Presides over Lok Sabha and joint sittings. Exercises a casting vote in case of a tie.",
              },
            ],
          },
          {
            id: "union-executive",
            heading: "Union Executive",
            icon: "👤",
            color: "#FF7A7A",
            listItems: [
              {
                title: "The President",
                description:
                  "Head of State; elected by an Electoral College. Powers include Military, Diplomatic, Legislative, and Pardoning powers.",
              },
              {
                title: "The Prime Minister",
                description:
                  "Real executive head; Chairman of the Cabinet. Communicates decisions between Council of Ministers and President.",
              },
              {
                title: "Attorney-General",
                description:
                  "Chief Law Officer of the Govt. Appointed by the President.",
              },
            ],
          },
          {
            id: "state-governance",
            heading: "State Governance",
            icon: "🏘️",
            color: "#FF820F",
            listItems: [
              {
                title: "The Governor",
                description:
                  "Appointed by the President. Head of state executive; has similar legislative/pardoning powers at state level.",
              },
              {
                title: "Chief Minister",
                description:
                  "Real executive head of the state. Appointed by the Governor.",
              },
              {
                title: "State Legislature",
                description:
                  "Unicameral in most states; some have a Legislative Council (Bicameral).",
              },
            ],
          },
          {
            id: "constitutional-bodies",
            heading: "Independent Bodies",
            icon: "🗳️",
            color: "#15E4B9",
            listItems: [
              {
                title: "Election Commission (Art 324)",
                description:
                  "Ensures free and fair elections for Parliament, State Legislatures, President, and VP.",
              },
              {
                title: "UPSC & SPSC",
                description:
                  "Recruitment agencies for All India and State civil services.",
              },
              {
                title: "Finance Commission (Art 280)",
                description:
                  "Recommends distribution of financial resources between Union and States every 5 years.",
              },
              {
                title: "NITI Aayog",
                description:
                  "Replaced Planning Commission in 2015. Fosters cooperative federalism and provides a national agenda.",
              },
            ],
          },
          {
            id: "judicial-system",
            heading: "The Judiciary",
            icon: "⚖️",
            color: "#407BFF",
            listItems: [
              {
                title: "Supreme Court",
                description:
                  "Highest court; guardian of the Constitution. Power of Judicial Review to declare laws unconstitutional.",
              },
              {
                title: "High Courts",
                description:
                  "Apex of state judiciary; essentially courts of appeal with writ jurisdiction.",
              },
            ],
          },
          {
            id: "emergency-provisions",
            heading: "Emergency Provisions",
            icon: "🚨",
            color: "#FF1E1E",
            bullets: [
              "National Emergency (Art 352): Threat to security due to war or rebellion.",
              "State Emergency (Art 356): President's Rule due to failure of state machinery.",
              "Financial Emergency (Art 360): Threat to financial stability or credit of India.",
            ],
          },
        ],
        keyTakeaways: [
          "The Constitution of India came into force on 26 January 1950.",
          "India follows a parliamentary form of government with federal features.",
          "Fundamental Rights ensure liberty, equality, and justice to citizens.",
          "Parliament consists of the President, Lok Sabha, and Rajya Sabha.",
          "The judiciary safeguards the Constitution through judicial review.",
        ],
      },
      General_Science: {
        title: "General Science",
        subject: "General Test – General Awareness",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – General Science. Along with this student will be empowered to solve different categories of questions involving Physics principles, atomic theories, optics, electricity, and measurement units as commonly asked in exams like NIFT/NID/UCEED.",
        sections: [
          {
            id: "mechanics",
            heading: "Mechanics and Gravitation",
            icon: "⚙️",
            color: "#407BFF",
            subsections: [
              {
                title: "Work, Energy & Power",
                body: "Work (Joule) is force times distance. Energy is scalar (Joule). Power (Watt) is the rate of work. 1 HP = 746 W.",
                color: "#407BFF",
              },
              {
                title: "Newton's Laws",
                body: "1st: Law of Inertia. 2nd: F = ma. 3rd: Every action has an equal and opposite reaction.",
                color: "#15E4B9",
              },
              {
                title: "Gravitation",
                body: "g = 9.8 m/s². Escape velocity: Earth (11.2 km/s), Moon (2.4 km/s). g is max at poles and min at equator.",
                color: "#FF9A4F",
              },
            ],
          },
          {
            id: "satellites",
            heading: "Satellite Technology",
            icon: "🛰️",
            color: "#940B92",
            listItems: [
              {
                title: "Geostationary Satellite",
                description:
                  "Orbit at ~36,000 km. Time period is 24 hours. Used for TV and weather forecasting.",
              },
              {
                title: "Polar Satellite",
                description:
                  "Orbit at ~800 km. Time period is ~84 minutes. Revolves in polar orbit.",
              },
            ],
          },
          {
            id: "nuclear-atomic",
            heading: "Atomic and Nuclear Physics",
            icon: "⚛️",
            color: "#FF1E1E",
            subsections: [
              {
                title: "Cathode & X-Rays",
                body: "Cathode rays travel in straight lines. X-rays (Roentgen) are used in surgery (fractures) and engineering (metal cracks).",
                color: "#FF1E1E",
              },
              {
                title: "Fission vs Fusion",
                body: "Fission: Breaking a heavy nucleus (Atom Bomb, U-235). Fusion: Combining light nuclei (Hydrogen Bomb, 10⁸ K required).",
                color: "#FF820F",
              },
              {
                title: "Nuclear Reactor",
                body: "Controlled fission. Enrico Fermi supervised the first one. Moderators: Heavy water, Graphite.",
                color: "#407BFF",
              },
            ],
          },
          {
            id: "fluids-waves",
            heading: "Fluids, Waves and Sound",
            icon: "🌊",
            color: "#15E4B9",
            listItems: [
              {
                title: "Fluid Laws",
                description:
                  "Pascal's Law (Hydraulic lift). Archimedes Principle (Buoyancy/floating ships).",
              },
              {
                title: "Waves",
                description:
                  "Longitudinal (Sound) vs Transverse (Light/Water surface). Electromagnetic waves don't need a medium.",
              },
              {
                title: "Sound Properties",
                description:
                  "Audible: 20-20,000 Hz. Infrasonic (<20 Hz). Ultrasonic (>20,000 Hz). Speed is max in solids.",
              },
            ],
          },
          {
            id: "optics",
            heading: "Optics and Human Eye",
            icon: "👁️",
            color: "#FF9A4F",
            subsections: [
              {
                title: "Reflection & Refraction",
                body: "Plane mirror image is virtual/erect. Convex mirror (rear-view) is always diminished. Twinkling stars are due to refraction.",
              },
              {
                title: "Vision Defects",
                body: "Myopia (Short-sightedness), Hypermetropia (Long-sightedness), Presbyopia (Aging eyes). Distinct vision: 25 cm.",
                color: "#940B92",
              },
              {
                title: "Total Internal Reflection",
                body: "Sparkling diamond, Mirages, and Optical fibers.",
                color: "#15E4B9",
              },
            ],
          },
          {
            id: "electricity-magnetism",
            heading: "Electricity and Magnetism",
            icon: "⚡",
            color: "#EBC33C",
            listItems: [
              {
                title: "Charge & Current",
                description:
                  "Charge (Coulomb). Silver is the best conductor. Galvanometer -> Ammeter (Shunt parallel).",
              },
              {
                title: "Magnetism Types",
                description:
                  "Diamagnetic (Repel - Gold/Water), Paramagnetic (Na/Al), Ferromagnetic (Strong - Iron/Nickel).",
              },
              {
                title: "Curie Point",
                description:
                  "Temperature at which materials lose permanent magnetic properties.",
              },
            ],
          },
          {
            id: "heat-surface",
            heading: "Heat and Surface Tension",
            icon: "🔥",
            color: "#FF820F",
            listItems: [
              {
                title: "Anomalous Expansion",
                description:
                  "Water contracts from 0°C to 4°C; density is maximum at 4°C.",
              },
              {
                title: "Surface Tension",
                description:
                  "Lubricating oil spreads due to low tension. Capillary action: Ink absorption/water in tree leaves.",
              },
              {
                title: "Absolute Zero",
                description:
                  "The lowest possible temperature: -273.15°C or 0 Kelvin.",
              },
            ],
          },
          {
            id: "scientific-instruments",
            heading: "Scientific Instruments",
            icon: "🧪",
            color: "#808080",
            table: {
              headers: ["Instrument", "Purpose"],
              rows: [
                ["Altimeter", "Measuring altitudes (aircraft)."],
                ["Barometer", "Measuring atmospheric pressure."],
                ["Fathometer", "Measuring ocean depth."],
                ["Hygrometer", "Measuring humidity level."],
                ["Pyrometer", "Measuring very high temperature."],
                ["Seismograph", "Recording earthquake shocks."],
                ["Sphygmomanometer", "Measuring blood pressure."],
                ["Stethoscope", "Analysing heart and lung sounds."],
              ],
            },
          },
          {
            id: "measurements",
            heading: "Units and Conversions",
            icon: "📏",
            color: "#407BFF",
            bullets: [
              "1 Light Year = 9.46 x 10¹⁵ m",
              "1 HP = 746 Watts",
              "1 Knot = Speed of ships",
              "Fathom = Depth of water (~6 ft)",
              "Angstrom (Å) = 10⁻¹⁰ m (Light wavelength)",
            ],
          },
        ],
        keyTakeaways: [
          "Physics explains natural phenomena through laws of motion and energy.",
          "Gravitation governs planetary motion and object behavior on Earth.",
          "Electricity and magnetism form the basis of modern technology.",
          "Optics explains vision, reflection, refraction, and optical instruments.",
          "Standard units and scientific instruments ensure accurate measurements.",
        ],
      },
      International_Cooperation_Organization: {
        title: "International Organizations and Headquarters",
        subject: "General Test – General Awareness",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the topic – List of International Organizations and their Headquarters. Students will be empowered to solve different categories of questions involving global governance, financial institutions, and international partnerships.",
        sections: [
          {
            id: "un-organizations",
            heading: "United Nations & Global Agencies",
            icon: "🇺🇳",
            color: "#407BFF",
            table: {
              headers: ["Organization", "Headquarters", "Founded", "Head"],
              rows: [
                [
                  "United Nations (UN)",
                  "New York, US",
                  "1945",
                  "António Guterres",
                ],
                ["UNICEF", "New York, US", "1946", "Catherine M. Russell"],
                ["WHO", "Geneva, Switzerland", "1948", "Tedros Adhanom"],
                ["UNESCO", "Paris, France", "1945", "Audrey Azoulay"],
                ["ILO", "Geneva, Switzerland", "1919", "Gilbert F. Houngbo"],
                ["WTO", "Geneva, Switzerland", "1995", "Ngozi Okonjo-Iweala"],
                ["FAO", "Rome, Italy", "1945", "Qu Dongyu"],
                ["UN-Women", "New York, US", "2010", "Sima Sami Bahous"],
              ],
            },
          },
          {
            id: "financial-institutions",
            heading: "International Financial Institutions",
            icon: "💰",
            color: "#EBC33C",
            table: {
              headers: ["Organization", "Headquarters", "Founded", "Head"],
              rows: [
                [
                  "World Bank (WB)",
                  "Washington DC, US",
                  "1944",
                  "David Malpass (Ajay Banga nominated)",
                ],
                ["IMF", "Washington DC, US", "1945", "Kristalina Georgieva"],
                ["ADB", "Manila, Philippines", "1966", "Masatsugu Asakawa"],
                ["AIIB", "Beijing, China", "2016", "Jin Liqun"],
                [
                  "New Development Bank (BRICS)",
                  "Shanghai, China",
                  "2014",
                  "Marcos Prado Troyjo",
                ],
                ["OECD", "Paris, France", "1961", "Mathias Cormann"],
              ],
            },
          },
          {
            id: "regional-groups",
            heading: "Regional & Strategic Alliances",
            icon: "🤝",
            color: "#15E4B9",
            table: {
              headers: ["Organization", "Headquarters", "Founded", "Key Info"],
              rows: [
                ["SAARC", "Kathmandu, Nepal", "1985", "South Asian focus"],
                [
                  "ASEAN",
                  "Jakarta, Indonesia",
                  "1967",
                  "South East Asian focus",
                ],
                ["NATO", "Brussels, Belgium", "1949", "Military Alliance"],
                ["APEC", "Singapore", "1989", "Asia-Pacific focus"],
                [
                  "Commonwealth",
                  "London, UK",
                  "1931",
                  "Former British territories",
                ],
                ["OPEC", "Vienna, Austria", "1960", "Petroleum exporters"],
                ["SCO", "Beijing, China", "2001", "Shanghai Cooperation Org"],
              ],
            },
          },
          {
            id: "sports-legal",
            heading: "Sports, Legal & Specialized Orgs",
            icon: "🏆",
            color: "#FF9A4F",
            table: {
              headers: ["Organization", "Headquarters", "Founded", "Head"],
              rows: [
                ["ICC (Cricket)", "Dubai, UAE", "1909", "Greg Barclay"],
                [
                  "IOC (Olympics)",
                  "Lausanne, Switzerland",
                  "1894",
                  "Thomas Bach",
                ],
                [
                  "FIFA (Football)",
                  "Zurich, Switzerland",
                  "1904",
                  "Gianni Infantino",
                ],
                [
                  "ICJ (Court)",
                  "The Hague, Netherlands",
                  "1945",
                  "Joan Donoghue",
                ],
                ["INTERPOL", "Lyon, France", "1923", "Ahmed Naser Al-Raisi"],
                ["WWF", "Gland, Switzerland", "1961", "Pavan Sukhdev"],
                [
                  "Amnesty International",
                  "London, UK",
                  "1961",
                  "Agnes Callamard",
                ],
              ],
            },
          },
        ],
        keyTakeaways: [
          "International organizations promote global peace, cooperation, and development.",
          "The United Nations is the largest global intergovernmental organization.",
          "Financial institutions like IMF and World Bank support economic stability.",
          "Regional groupings strengthen political and economic collaboration.",
          "Headquarters and leadership details are important exam focus areas.",
        ],
      },
      Art_Culture_and_Literature: {
        title: "Art, Culture and Literature",
        subject: "General Test – General Awareness",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic – Art, Culture and Literature. Students will be empowered to solve different categories of questions involving word problems and problems commonly asked in Aptitude Tests like NIFT / NID / UCEED and all such entrances.",
        sections: [
          {
            id: "academies",
            heading: "Academies",
            icon: "🏛️",
            color: "#407BFF",
            intro: "There are three academies for promotion of creative art.",
            listItems: [
              {
                title: "Lalit Kala Academi, New Delhi",
                description:
                  "Established in 1954, strives for the popularisation of Indian art within the country and in various countries through exhibitions, publications, workshops and camps. Regional centres (Rashtriya Lalit Kala Kendras) at Lucknow, Kolkata, Chennai and Bhubaneshwar; a small office at Mumbai.",
              },
              {
                title: "Sangeet Natak Academi, New Delhi",
                description:
                  "Set up in 1953, aims at the promotion and development of dance, drama and music.",
              },
              {
                title: "Sahitya Academi, New Delhi",
                description:
                  "Established in 1954, functions include development of Indian letters, setting high literary standards, and translation of literary works between Indian languages. Regional offices at Mumbai, Kolkata, Bengaluru and Chennai.",
              },
              {
                title: "National School of Drama",
                description:
                  "One of the top theatre training institutes in the world and the only one of its kind in India. Set up in 1959 under Sangeet Natak Academi; made an autonomous organisation in 1975.",
              },
            ],
          },
          {
            id: "sculpture",
            heading: "Sculpture",
            icon: "🗿",
            color: "#FF9A4F",
            body: "Archaeological Survey of India, set up in 1861, is responsible for preservation and maintenance of sculptures and historical monuments. It manages a number of archaeological museums across India.",
          },
          {
            id: "musical-instruments",
            heading: "Musical Instruments and Artists",
            icon: "🎵",
            color: "#940B92",
            table: {
              headers: ["Musical Instrument", "Artists"],
              rows: [
                ["Flute", "Hari Prasad Chaurasia"],
                ["Tabla", "Allah Rakha, Zakir Hussain"],
                ["Violin", "L. Subramaniam, S. Subrahmaniam"],
                ["Shehnai", "Bismilla Khan"],
                ["Sarod", "Amjad Ali Khan"],
                ["Sitar", "Pandit Ravishankar, Vilayat Khan"],
                ["Santur", "Shiv Kumar Sharma"],
                ["Rudraveena", "Zia Mohiuddin Dagar"],
                ["Pakhawaj", "Govind Rao, Kanthi Maharaj"],
                ["Mridanga", "Palghat R. Raghu"],
                ["Harmonium", "Purushottam Walawakar, M. Dhaulpuri"],
                ["Guitar", "Pt. Vishnu Mohan Bhatt, Brij Bhushan Kalra"],
              ],
            },
          },
          {
            id: "museums",
            heading: "Museums",
            icon: "🏺",
            color: "#EBC33C",
            categories: [
              {
                name: "Indian Art and Archaeological Museums",
                items: [
                  "Indian Museum, Kolkata",
                  "National Museum, New Delhi",
                  "Salar Jung Museum, Hyderabad",
                ],
              },
              {
                name: "Contemporary Historical and Art Museums",
                items: [
                  "National Gallery of Modern Art, New Delhi",
                  "Nehru Memorial Museum and Library, New Delhi",
                  "Victoria Memorial Hall, Kolkata",
                ],
              },
            ],
          },
          {
            id: "classical-dance",
            heading: "Classical Dance Forms",
            icon: "💃",
            color: "#FF7A7A",
            table: {
              headers: ["Dance", "State"],
              rows: [
                ["Bharat Natyam", "Tamil Nadu"],
                ["Kathakali", "Kerala"],
                ["Kuchipudi", "Andhra Pradesh"],
                ["Kathak", "North India"],
                ["Odissi", "Odisha"],
                ["Manipuri", "Manipur"],
              ],
            },
          },
          {
            id: "books-foreign",
            heading: "Famous Books and Authors – Foreign",
            icon: "📚",
            color: "#15E4B9",
            table: {
              headers: ["Book", "Author"],
              rows: [
                ["Adventures of Sherlock Holmes, The", "Arthur Conan Doyle"],
                ["Adventures of Tom Sawyer, The", "Mark Twain"],
                ["Alice in Wonderland", "Lewis Carroll"],
                ["Around the World in Eighty Days", "Jules Verne"],
                ["As You Like It", "William Shakespeare"],
                ["A Tale of Two Cities", "Charles Dickens"],
                ["Ben Hur", "Lewis Wallace"],
                ["Crime and Punishment", "Feodor Dostoyevsky"],
                ["Das Kapital", "Karl Marx"],
                ["David Copperfield", "Charles Dickens"],
                ["Divine Comedy, The", "A. Dante"],
                ["Doctor's Dilemma, The", "G.B. Shaw"],
                ["Dr. Zhivago", "Boris Pasternak"],
                ["Freedom at Midnight", "Larry Collins and Dominique Lapierre"],
                ["Good Earth", "Pearl S. Buck"],
                ["Gulliver's Travels", "Jonathan Swift"],
                ["History of Western Philosophy", "Bertrand Russell"],
                ["Hamlet", "William Shakespeare"],
                ["Iliad", "Homer"],
                ["Julius Caesar", "William Shakespeare"],
                ["Lady Chatterley's Lover", "D.H. Lawrence"],
                ["Lajja", "Taslima Nasreen"],
                ["Les Miserables", "Victor Hugo"],
                ["Lolita", "V. Nabokov"],
                ["Long Walk to Freedom (Autobiography)", "Nelson Mandela"],
                ["Mein Kampf", "Adolf Hitler"],
                ["Merchant of Venice, The", "William Shakespeare"],
                ["Moor's Last Sigh, The", "Salman Rushdie"],
                ["Mother", "Maxim Gorky"],
                ["Nineteen Eighty-Four", "George Orwell"],
                ["Nirbashita Narir Kabita", "Taslima Nasreen"],
                ["The Old Man and the Sea", "Ernest Hemingway"],
                ["Origin of Species, The", "Charles Darwin"],
                ["Othello", "William Shakespeare"],
                ["Paradise Lost", "John Milton"],
                ["Paradise Regained", "John Milton"],
                ["Price of Power, The", "Seymour Hersh"],
                ["Pride and Prejudice", "Jane Austen"],
                ["Romeo and Juliet", "William Shakespeare"],
                ["Robinson Crusoe", "Daniel Defoe"],
                ["Satanic Verses, The", "Salman Rushdie"],
                ["Shape of Things to Come", "H. G. Wells"],
                ["Social Contract, The", "Rousseau"],
                ["Tempest, The", "William Shakespeare"],
                ["Twelfth Night", "William Shakespeare"],
                ["War and Peace", "Leo Tolstoy"],
                ["Wealth of Nations", "Adam Smith"],
                ["Wuthering Heights", "Emily Bronte"],
              ],
            },
          },
          {
            id: "books-indian",
            heading: "Famous Books and Authors – Indian",
            icon: "📖",
            color: "#FF820F",
            table: {
              headers: ["Book", "Author"],
              rows: [
                ["Ain-i-Akbari", "Abul Fazal"],
                ["Anandmath", "Bankim Chandra Chatterjee"],
                ["Arthashastra", "Kautilya"],
                ["Abhigyan Shakuntalam", "Kalidas"],
                ["Bhagwad Gita", "Ved Vyas"],
                ["Devdas", "Sarat Chandra Chatterjee"],
                ["Diwan-i-Ghalib", "Mirza Ghalib"],
                ["Discovery of India", "Jawaharlal Nehru"],
                ["Fasting Feasting", "Anita Desai"],
                ["Gunahon ka Devta", "Dharmveer Bharti"],
                ["Ganadevta", "Tara Shankar Bandyopadhyaya"],
                ["God of Small Things", "Arundhati Roy"],
                ["Geet Govinda", "Jaya Dev"],
                ["Gitanjali", "R.N. Tagore"],
                ["Glimpses of World History", "Jawaharlal Nehru"],
                ["Godan", "Prem Chand"],
                ["Gora", "R.N. Tagore"],
                ["The Guide", "R.K. Narayan"],
                ["Harsh Charita", "Bana Bhatt"],
                ["Hindu View of Life", "S. Radhakrishnan"],
                ["Idols", "Sunil Gavaskar"],
                ["India Wins Freedom", "Abul Kalam Azad"],
                ["Kadambari", "Bana Bhatta"],
                ["Kagaz te Kanwas", "Amrita Pritam"],
                ["Kamasutra", "Vatsyayana"],
                ["Kamayani", "Kubernath Ray"],
                ["Kumar Sambhav", "Kalidas"],
                ["Mahabharata", "Ved Vyas"],
                ["Mansorovar (10 Vols.)", "Prem Chand"],
                ["Meghdoot", "Kalidas"],
                ["Midnight's Children", "Salman Rushdie"],
                ["Mrichhankatikam", "Sudraka"],
                ["My Experiments with Truth", "M.K. Gandhi"],
                ["One Day Wonders", "Sunil Gavaskar"],
                ["Panchtantra", "Vishnu Sharma"],
                ["Prison Diary", "Jayaprakash Narayan"],
                ["Post Office, The (Dak Ghar)", "R.N. Tagore"],
                ["Raguvansha", "Kalidas"],
                ["Rajtarangini", "Kalhana"],
                ["Ramayana", "Valmiki"],
                ["Ramcharit Manas", "Tulsidas"],
                ["Saket", "Maithili Sharan Gupta"],
                ["Satyarth Prakash", "Swami Dayanand"],
                ["Shah Nama", "Firdausi"],
                ["The God of Small Things", "Arundhati Roy"],
                ["The White Tiger", "Arvind Adiga"],
              ],
            },
          },
        ],
        keyTakeaways: [
          "Three Academies: Lalit Kala (1954 – visual arts), Sangeet Natak (1953 – performing arts), Sahitya (1954 – literature).",
          "National School of Drama was set up in 1959, made autonomous in 1975.",
          "Archaeological Survey of India (1861) preserves sculptures and historical monuments.",
          "Key classical dances: Bharat Natyam (TN), Kathakali (Kerala), Kuchipudi (AP), Kathak (North India), Odissi (Odisha), Manipuri (Manipur).",
          "William Shakespeare authored the most books in the foreign list.",
          "Kalidas is the most prolific Indian author in the classical list.",
          "R.N. Tagore wrote Gitanjali, Gora, and Post Office (Dak Ghar).",
        ],
      },
      Award_and_Honor: {
        title: "Sports and Games, Awards and Honours",
        subject: "General Test – General Awareness",
        learningOutcome:
          "In this lecture, we shall learn the basic concepts from the Topic – Sports and Games, Awards and Honours. Students will be empowered to solve different categories of questions involving conversion, word problems and problems commonly asked in Aptitude Tests like NIFT / NID / UCEED and all such entrances.",
        sections: [
          /* ── National Awards ── */
          {
            id: "bharat-ratna",
            heading: "Bharat Ratna",
            icon: "🏅",
            color: "#FFD700",
            body: "Bharat Ratna is India's highest Civilian Award, first awarded in 1954. The actual award is designed in the shape of a peepal leaf with 'Bharat Ratna' inscribed in Devanagri script in the Sun Figure.",
            table: {
              headers: ["Recipient", "Year"],
              rows: [
                ["Madan Mohan Malaviya", "2014"],
                ["Atal Behari Vajpayee", "2014"],
                ["Nanaji Deshmukh", "2019"],
                ["Bhupen Hazarika", "2019"],
                ["Pranab Mukherjee", "2019"],
              ],
            },
          },
          {
            id: "padma-awards",
            heading: "Padma Awards",
            icon: "🎖️",
            color: "#FF820F",
            body: "Padma Awards fall in line after the Bharat Ratna. They were discontinued in 1977 along with the Bharat Ratna and restarted in 1980.",
            listItems: [
              {
                title: "Padma Vibhushan",
                description:
                  "Given for exceptional and distinguished service in any field, including service rendered by Government servants.",
              },
              {
                title: "Padma Bhushan",
                description:
                  "Given for distinguished service of a high order in any field, including service rendered by Government servants.",
              },
              {
                title: "Padma Shri",
                description:
                  "Given for distinguished service in any field, including service rendered by Government servants.",
              },
            ],
          },
          {
            id: "gallantry-awards",
            heading: "Gallantry Awards",
            icon: "⚔️",
            color: "#940B92",
            listItems: [
              {
                title: "Param Vir Chakra",
                description:
                  "The highest award for bravery or some daring and pre-eminent act of valour or self-sacrifice in the presence of the enemy, whether on land, at sea or in the air.",
              },
              {
                title: "Mahavir Chakra",
                description:
                  "The second highest decoration, awarded for acts of conspicuous gallantry in the presence of the enemy, whether on land, at sea or in the air.",
              },
              {
                title: "Vir Chakra",
                description:
                  "Third in order of awards given for acts of gallantry in the presence of enemy, whether on land, at sea or in the air.",
              },
              {
                title: "Ashok Chakra",
                description:
                  "Awarded for the most conspicuous bravery or some daring or pre-eminent act of valour or self-sacrifice, but not in the presence of enemy.",
              },
              {
                title: "Vishishta Sewa Medal",
                description:
                  "Awarded to personnel of all three Services in Class I, II and III for distinguished service of 'most exceptional', 'exceptional' and 'high' order respectively.",
              },
              {
                title: "Jeewan Raksha Padak",
                description:
                  "Awarded for meritorious acts of saving life from drowning, fire and rescue operations in mines, etc.",
              },
            ],
          },
          {
            id: "other-awards",
            heading: "Other Notable Awards",
            icon: "🏆",
            color: "#15E4B9",
            bullets: [
              "Sahitya Akademi Awards – Awarded annually to authors of outstanding books in each of 22 recognised languages, plus Sanskrit and English. Prize: inscribed copper plate + ₹1 lakh cheque.",
              "Dada Saheb Phalke Award – Cash prize of ₹10 lakh, a Shawl, and Swarna Kamal. First recipient: Mrs. Devika Rani Roerich (1969). 2017: Vinod Khanna; 2018: Amitav Ghosh.",
              "Bharatiya Jnanpith Award (2017) – Krishna Sobti.",
              "Indira Gandhi Award for National Integration (2017) – T. M. Krishna.",
              "Important Football Cups & Trophies – Merdeka (Asian), Colombo Cup (India, Pakistan, Sri Lanka, Burma), Rovers Cup, Durand Cup.",
            ],
          },
          /* ── Sports Terms ── */
          {
            id: "sports-terms",
            heading: "Sports Terms",
            icon: "🎯",
            color: "#407BFF",
            sportTerms: [
              {
                sport: "Badminton",
                terms:
                  "Drop · Smash · Let · Game point · Service over · Deuce · Love all",
              },
              {
                sport: "Baseball",
                terms:
                  "Home · Diamond · Pitcher · Strike · Bunting · Battery · Catcher · Hitter",
              },
              {
                sport: "Basketball",
                terms:
                  "Blocking · Dribbling · Free Throw · Held Ball · Jump Ball · Pivot",
              },
              {
                sport: "Billiards",
                terms:
                  "Cue · Jigger · Pot · Break · Baulk · In Off · Cannons · Hazard · Scratch",
              },
              {
                sport: "Boxing",
                terms:
                  "Punch · Uppercut · Round · Jab · Hook · Bout · Knockdown · Knockout · Ring",
              },
              {
                sport: "Bridge",
                terms:
                  "Grand Slam · Little Slam · No Trump · Trump · Dummy · Finesse · Rubber",
              },
              {
                sport: "Chess",
                terms:
                  "Bishop · Gambit · Checkmate · Stalemate · Pawn · King · Knight · Rook · Queen",
              },
              {
                sport: "Cricket",
                terms:
                  "LBW · Stumped · Bye · Googly · Hat-trick · Maiden over · Duck · Follow on · No Ball · Googly · Cover point · Hitwicket · Slip · Boundary",
              },
              {
                sport: "Football",
                terms:
                  "Foul · Off-side · Corner kick · Free kick · Dribble · Throw-in · Hat-trick · Penalty kick",
              },
              {
                sport: "Golf",
                terms:
                  "Bogey · Foursome · Tee · Putt · Niblick · Caddie · Links · Bunker · Par · Fairway",
              },
              {
                sport: "Hockey",
                terms:
                  "Penalty Corner · Tie-breaker · Short-corner · Bully · Sticks · Striking Circle · Dribble · Scoop",
              },
              {
                sport: "Horse Racing",
                terms: "Punter · Jockey · Break · Steeple chase",
              },
              { sport: "Polo", terms: "Mallet · Bunker · Chukker" },
              {
                sport: "Rugby Football",
                terms: "Scrum · Tackle · Lines · Touch · Try",
              },
              { sport: "Swimming", terms: "Crawl · Breast stroke · Dive" },
              {
                sport: "Tennis",
                terms:
                  "Backhand drive · Volley · Smash · Half volley · Deuce · Service · Let · Grand Slam · Fault",
              },
              {
                sport: "Volleyball",
                terms:
                  "Deuce · Love all · Smash · Sideout · Block · Double fault · Spikers · Holding · Volley",
              },
              {
                sport: "Wrestling",
                terms:
                  "Half Nelson · Heave · Drop Kick · Head Lock · Hold · Scissors",
              },
            ],
          },
          /* ── National Games ── */
          {
            id: "national-games",
            heading: "National Games of Some Countries",
            icon: "🌍",
            color: "#EBC33C",
            table: {
              headers: ["Country", "Game", "Country", "Game"],
              rows: [
                ["America", "Baseball", "Spain", "Bull Fighting"],
                ["Australia", "Cricket", "Scotland", "Rugby Football"],
                ["Canada", "Ice Hockey", "UK", "Cricket"],
                ["Japan", "Jujitsu", "India", "Hockey"],
              ],
            },
          },
          /* ── Players ── */
          {
            id: "players",
            heading: "Games and Number of Players on Each Side",
            icon: "👥",
            color: "#FF9A4F",
            table: {
              headers: ["Game", "Players", "Game", "Players"],
              rows: [
                ["Baseball", "9", "Kabaddi", "8"],
                ["Basketball", "5", "Netball", "7"],
                ["Badminton", "1 to 2", "Polo", "4"],
                ["Cricket", "11", "Table Tennis", "1 to 2"],
                ["Football (Association)", "11", "Tennis", "1 to 2"],
                ["Football (Rugby)", "15", "Volleyball", "6"],
                ["Hockey", "11", "Water Polo", "7"],
              ],
            },
          },
          /* ── Field Measurements ── */
          {
            id: "fields",
            heading: "Measurement of Some Sports Fields",
            icon: "📐",
            color: "#FF7A7A",
            table: {
              headers: ["Sport / Field", "Dimensions"],
              rows: [
                ["Badminton Court", "20×44 ft (Doubles), 17×44 ft (Singles)"],
                [
                  "Baseball",
                  "Distance between bases: 90 ft (diamond shaped). Diagonal: 127 ft",
                ],
                ["Basketball", "85×46 ft (max dimension)"],
                ["Cricket", "Oval/round ground; Pitch: 22 yds"],
                ["Croquet", "35×28 yds"],
                ["Derby Course", "1½ miles (2.4 km)"],
                [
                  "Football (Association)",
                  "Length 100–130 yds; Width 50–100 yds",
                ],
                ["Football (Rugby)", "Length 110 yds; Width 75 yds"],
                ["Golf Hole", "4½ inches in diameter"],
                ["Hockey Ground", "100×55–60 yds"],
                ["Marathon Race", "26 miles, 385 yards"],
                ["Ping Pong", "9×5 ft (table), Net 2½ ft above floor"],
                ["Polo", "300×160 yds (boarded); 300×200 yds (unboarded)"],
                [
                  "Tennis Court (Doubles)",
                  "78×36 ft; Net height at centre: 3 ft",
                ],
                ["Tennis Court (Singles)", "78×28 ft"],
                ["Volleyball", "60×30 ft (rectangular)"],
                ["Water Polo", "30×20 yds"],
              ],
            },
          },
          /* ── Olympics ── */
          {
            id: "olympics",
            heading: "The Olympic Games",
            icon: "🏟️",
            color: "#58B6C5",
            body: "The Olympic games were first held at Olympia, Greece in 776 BC. They ceased in 394 AD by a Roman Emperor's decree and were revived at Athens in 1896.",
            table: {
              headers: ["Year", "Location"],
              rows: [
                ["2012", "London, England"],
                ["2016", "Rio de Janeiro, Brazil"],
                ["2020", "Tokyo, Japan"],
              ],
            },
          },
          /* ── Asian Games ── */
          {
            id: "asian-games",
            heading: "Asian Games (ASIAD)",
            icon: "🌏",
            color: "#940B92",
            body: "The Asian Relations Conference (New Delhi, 1947) decided to organise a sports festival every four years. First held at New Delhi in 1951.",
            table: {
              headers: ["Year", "Venue"],
              rows: [
                ["1951", "New Delhi, India"],
                ["1954", "Manila, Philippines"],
                ["1958", "Tokyo, Japan"],
                ["1962", "Jakarta, Indonesia"],
                ["1966", "Bangkok, Thailand"],
                ["1970", "Bangkok, Thailand"],
                ["1974", "Tehran, Iran"],
                ["1978", "Bangkok, Thailand"],
                ["1982", "New Delhi, India"],
                ["1986", "Seoul, South Korea"],
                ["1990", "Beijing, China"],
                ["1994", "Hiroshima, Japan"],
                ["1998", "Bangkok, Thailand"],
                ["2002", "Busan, South Korea"],
                ["2006", "Doha, Qatar"],
                ["2010", "Guangzhou, China"],
                ["2014", "Incheon, South Korea"],
                ["2018", "Jakarta, Indonesia"],
              ],
            },
          },
          /* ── Commonwealth Games ── */
          {
            id: "commonwealth",
            heading: "Commonwealth Games",
            icon: "🌐",
            color: "#FF820F",
            body: "The Commonwealth Games are held every four years, in the same year as the Asian Games. All Commonwealth Countries (former British colonies) can participate. The first Commonwealth Games were held in 1930 at Hamilton, Canada.",
          },
        ],
        keyTakeaways: [
          "Bharat Ratna is India's highest civilian award, first given in 1954.",
          "Gallantry awards in order: Param Vir Chakra > Mahavir Chakra > Vir Chakra > Ashok Chakra.",
          "Dada Saheb Phalke Award: ₹10 lakh + Shawl + Swarna Kamal. First recipient: Devika Rani Roerich (1969).",
          "India's national game is Hockey; USA – Baseball; UK & Australia – Cricket; Japan – Jujitsu.",
          "Olympic Games originated in Olympia, Greece (776 BC), revived in Athens (1896).",
          "First Asian Games: New Delhi, 1951. First Commonwealth Games: Hamilton, Canada, 1930.",
          "Cricket, Football (Association), and Hockey all have 11 players per side.",
        ],
      },
    },
  },
};

export default notesData;
